import { getStateCallbacks, Predict, type Reconciler } from "@colyseus/sdk";
import { Frustum, Matrix4, Quaternion, Sphere, Vector3, type Mesh, type Object3D, type WebGLRenderer } from "three";
import { FALL, GRAYS, MECHANIC_HINTS, PALETTE, SHAPE_GLYPHS, classColor, STATUS_IDS, TONE_COLORS, ULT_CHARGE, classSkills, classOf, attackName, creatureTone, ammoOf, arrowSpeed, MELEE, BOSS_IDS, BOW, BUILD, bowCharge, bowOf, CREATURE_BY_ID, MASCATE_ITEM, MASCATE_RULES, naturalChestOf, pieceOf, PROCESS_STATION_ITEMS, SIMPATIA_CHEST_REACH, simpatiaChestOf, simpatiaEffects, simpatiaPrice, STATION_RADIUS, swingCycle, carrancaPrice, SIMPATIA_BY_ID, DEATH, ENCANTO, SACI, GEM_NAMES, GUARDIAN, RAFT, RAFT_DECK_COLLIDER, anchoredDeckBox, guardianOf, shrineColor, storageSlotsOf, structureName, worldStructure, type Difficulty, edibleFood, harvestBlocker, itemDef, type WeaponStats, meleeAllowed, meleeCycle, meleeWeapon, RECIPE_BY_ID, RESOURCE_TYPES, toolOf } from "@eldrun/content";
import { type EloEvent, type OathOffer, type SkillAck, type SkillEvent,
  knowledgeOf,
  MoveInput,
  MSG,
  type AvatarType,
  type CollectedEvent,
  type CreatureAttackEvent,
  type CreatureType as CreatureEntity,
  type DamageEvent,
  type HazardEvent,
  type ArrowEndEvent,
  type ArrowEvent,
  type ShotRefusedEvent,
  type StationClosedEvent,
  type LootFoundEvent,
  type SimpatiaGainedEvent,
  type RevivedEvent,
  type BossWarningEvent,
  type BossEndEvent,
  type EncantoEvent,
  type ChallengeEndEvent,
  type MuiraquitaEvent,
  type TravessuraEvent,
  type SaciCalmEvent,
  type MeteorEvent,
  type RaftFoundEvent,
  type HazardClearEvent,
  type LobBlockedEvent,
  type OrbEndEvent,
  type OrbEvent,
  type TradeDoneEvent,
  type InventoryType,
  type StructureType,
  type ItemDropType,
  type SwingEvent,
  type UnlockedEvent,
  type ChatEvent,
  type MarkEvent,
  type SystemEvent,
  type HarvestedEvent,
  type BowDrawEvent,
  type HarvestSwingEvent,
  type DamageKind,
  type DamageProcs,
} from "@eldrun/protocol";
import {
  BELT_SLOTS,
  equippedItem,
  cascade,
  HeightfieldWorld,
  horizontalSpeed,
  groundAt,
  VITALS,
  lookDirection,
  MOVE,
  pieceBoxes,
  LEAP_AIM_LIMIT,
  LEAP_RANGE,
  LOB_RELEASE_HEIGHT,
  pieceDistance,
  touchesGround,
  type PieceKind,
  placementBase,
  placementBlocker,
  resourceCylinder,
  stepTick,
  structureColliderId,
  structureCylinder,
  type InventoryLike,
  type MoveCommand,
  type StackLike,
} from "@eldrun/sim";
import { clampDelta, FrameTimer, PerfWindow, type PerfSummary } from "../engine/frame-loop.ts";
import { GpuTimer } from "../engine/gpu-timer.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import type { AvatarSignals } from "../game/avatar-state.ts";
import { createAvatarView, type AvatarView } from "../game/avatar-view.ts";
import { Spectator } from "../game/spectator.ts";
import { stopReviving } from "../game/revive-hold.ts";
import { eyeFor, ThirdPersonCamera, type CameraInput } from "../game/camera-rig.ts";
import { CrosshairAim } from "../game/crosshair-aim.ts";
import { DissolvePool } from "../game/dissolves.ts";
import { DropLayer } from "../game/drop-layer.ts";
import { heldMesh } from "../game/held-item.ts";
import { CreatureBars } from "../game/creature-bars.ts";
import { creatureFirst, CreatureLayer, creatureState, pickCreature, rayPickCreature, type CreatureSnapshot } from "../game/creature-layer.ts";
import { CreatureSkins } from "../game/creature-skins.ts";
import { Telegraphs } from "../game/telegraphs.ts";
import { HazardDecals } from "../game/hazards.ts";
import { Lobs } from "../game/lobs.ts";
import { OrbLayer } from "../game/orbs.ts";
import { MARK_LIFE, Smoke } from "../game/smoke.ts";
import { fenixSource, FlameEmitters, hazardSource } from "../game/flame-emitters.ts";
import { ClockTracker, type ClockReading } from "../game/world-clock.ts";
import { ClockHud } from "../ui/hud/clock-hud.ts";
import { arrowKey, ArrowLayer } from "../game/arrows.ts";
import { type Ribbon, RibbonPool, ribbonLook, SwingRibbons, swingEnds } from "../game/ribbons.ts";
import { flareTracer, passiveFx } from "../game/passive-fx.ts";
import { animationEvery } from "../game/avatar-lod.ts";
import { BowString } from "../game/bow.ts";
import { BowHud } from "../ui/hud/bow-hud.ts";
import { aimPiece, buildPreview, hitLevel, PendingDismantles, piecesFrom } from "../game/building-client.ts";
import { HarvestTargeting } from "../game/harvest-target.ts";
import { aimAtGround, isPlaceableStation, PlacementGhost, placementTarget, ROTATE_STEP, wrapYaw } from "../game/placement.ts";
import { StructureLayer } from "../game/structure-layer.ts";
import { createGhostMaterial } from "../render/shaders/ghost.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { craftingTabs, stationsNear } from "../ui/panels/crafting-model.ts";
import { CraftingPanel } from "../ui/panels/crafting-panel.ts";
import { VitalsHud } from "../ui/hud/vitals-hud.ts";
import { DamageNumbers } from "../ui/hud/damage-numbers.ts";
import { CameraShake, SHAKE } from "../game/camera-shake.ts";
import { SwingTracker } from "../game/swing.ts";
import { ActionGate } from "../game/action-gate.ts";
import { vitalsView } from "../ui/hud/vitals-model.ts";
import { PlayerInput, typingIn } from "../game/player-input.ts";
import { relockOnKey, showResumeHint, type OpenPanels, type RelockContext, type RelockKey } from "../game/pointer-lock.ts";
import { GameHud } from "../ui/hud/hud.ts";
import { toast } from "../ui/hud/toast.ts";
import { SimpatiasHud } from "../ui/hud/simpatias-hud.ts";
import { mascateName, simpatiaText, tradeRows } from "../ui/panels/trade-model.ts";
import { TradePanel } from "../ui/panels/trade-panel.ts";
import { CarrancaPanel } from "../ui/panels/carranca-panel.ts";
import { BossBarsHud, bossBars, bossName, capitalize, challengeText } from "../ui/hud/boss-bars.ts";
import { ArenaRings } from "../game/arena-rings.ts";
import { RelicHud, relicSlots } from "../ui/hud/relic-hud.ts";
import { WhirlwindLayer } from "../game/whirlwind-layer.ts";
import { peneiraStep } from "../game/peneira.ts";
import { RaftLayer } from "../game/raft-layer.ts";
import { CercoLayer, cercoText, cercoVignette } from "../game/cerco-layer.ts";
import { RaftPanel } from "../ui/panels/raft-panel.ts";
import { ChatBox } from "../ui/hud/chat.ts";
import { helpLines, mandingaText, parseChatInput, systemText, voteText } from "../ui/hud/chat-model.ts";
import { inferPing, PingMarkers } from "../game/pings.ts";
import { IslandLayers, IslandMap } from "../ui/panels/island-map.ts";
import { FogOfWar, mapMarkers } from "../ui/panels/map-model.ts";
import { TutorialCard } from "../ui/hud/tutorial.ts";
import { mergeTutorial, NO_SIGNALS, parseTutorial, Tutorial, TUTORIAL_PREF, tutorialKeys, type TutorialSignals, type TutorialTarget } from "../ui/hud/tutorial-model.ts";
import { readPref } from "../ui/kit/storage.ts";
import { loadTutorial, saveTutorial } from "../ui/hud/tutorial-store.ts";
import { keyName, refreshKeyHints, settings } from "../ui/panels/settings.ts";
import { sharedSettingsPanel, TUTORIAL_EVENT, type SettingsPanel } from "../ui/panels/settings-panel.ts";
import { AutoMeter, FlashLimiter, type Settings } from "../ui/panels/settings-model.ts";
import { PauseMenu } from "../ui/panels/pause-menu.ts";
import { escapeAction, pauseNote } from "../ui/panels/pause-model.ts";
import { DEFAULT_SENSITIVITY } from "../game/player-input.ts";
import { screenPoint } from "../game/pings.ts";
import { audio } from "../audio/engine.ts";
import { creatureVoice, GameAudio } from "../audio/game-audio.ts";
import { PRIORITY } from "../audio/audio-model.ts";
import { nightBlend, type AmbienceInput } from "../audio/ambience-model.ts";
import { CaptionsHud, CAPTIONS_PREF } from "../ui/hud/captions.ts";
import { BIOME, BIOME_NAMES, PLAN_SIDE, PLAN_SPACING, poiColliders, samplePlanGrid, WORLD_HALF } from "@eldrun/worldgen";
import { h } from "../ui/kit/dom.ts";
import { applyClassTheme, rememberedClass } from "../ui/theme/class-theme.ts";
import { raftRows, raftStatus } from "../ui/panels/raft-model.ts";
import { InventoryActions } from "../net/inventory-actions.ts";
import type { MatchSession } from "../net/match-client.ts";
import { ItemIcons } from "../render/item-icons.ts";
import { models } from "../render/model-library.ts";
import { AVATAR_BOUNDS, avatars, CLASS_AVATARS } from "../render/avatar-library.ts";
import { BlobShadows, PLAYER_BLOB_RADIUS } from "../render/blob-shadows.ts";
import type { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { ProgramPrewarm } from "../render/prewarm.ts";
import { QUALITY } from "../render/quality.ts";
import { BeltHud, InventoryPanel } from "../ui/panels/inventory-panel.ts";
import { WorldView } from "../render/world-view.ts";
import type { LoadedWorld } from "../world/load-world.ts";
import { gameColor, playerBySlot, playerColor, setWorldAccent } from "../render/style/roles.ts";
import { setRingLight } from "../render/atmosphere.ts";
import { GroupHud } from "../game/scene/group-hud.ts";
import { NavHud } from "../game/scene/nav-hud.ts";
import { MINIMAP_REDRAW_MS } from "../ui/hud/minimap-model.ts";
import { createSlotRing } from "../render/shaders/slot-ring.ts";
import { SkillVfx, skillShock } from "../game/skill-vfx.ts";
import { EnergyEvents } from "../render/energy-events.ts";
import { AbilitiesPanel } from "../ui/panels/abilities-panel.ts";
import { abilitiesView } from "../ui/panels/abilities-model.ts";
import { SheetPanel } from "../ui/panels/sheet-panel.ts";
import { sheetView } from "../ui/panels/sheet-model.ts";
import { debrisPieces } from "../game/debris.ts";
import { CRYSTALS, Shards, type RubbleBurst } from "../game/shards.ts";
import { FootDust, type DustEvent } from "../game/foot-dust.ts";
import { FootSteps, stepSurface, type StepEvent, type StepSurface } from "../game/foot-steps.ts";
import { bossSlam, CreatureFxPlayer, hasCreatureFx, planCreatureFx } from "../game/creature-fx.ts";
import { HIT_STOP, HitStop } from "../game/hit-stop.ts";
import { SkillBar } from "../ui/hud/skill-bar.ts";
import { skillBarView } from "../ui/hud/skill-bar-model.ts";
import { guardianMark, StatusGlyphs } from "../ui/hud/status-glyphs.ts";
import { chevronAngle, DamageChevrons } from "../ui/hud/damage-chevrons.ts";

/** O que E alcança: estação ou baú, Baú de Simpatia, mascate, Cruz de aliado ou Carranca. */
type Interactable = "estacao" | "simpatia" | "mascate" | "cruz" | "carranca" | "pedra" | "info" | "jangada";

interface AvatarEntry {
  /** O avatar animado (F49) ou, sem o pacote, o boneco procedural. */
  view: AvatarView;
  held: string;
  heldMesh: Mesh | undefined;
  /** Instante da morte (tombo e dissolve) e da volta (reaparece), em ms; −1 sem. */
  diedAt: number;
  revivedAt: number;
  /** Caído (F74): o tombo vale desde `diedAt`, mas o dissolve só desde a morte de vez (`deadAt`); `rose`: a volta veio da morte (reaparece em dissolve), não do chão. */
  deadAt: number;
  rose: boolean;
  /** Instante do último golpe levado (ms; −∞ sem): o `dano` toca pelo tempo desde ele. */
  hurtAt: number;
}

/** Puxada de outro jogador que ninguém encerrou (mensagem perdida): some depois disto (ms). */
const STALE_DRAW_MS = 20_000;

/** Aviso de receitas novas: até três nomes, depois "e mais N". */
export function unlockedText(recipes: readonly string[]): string {
  const names = recipes.map((id) => {
    const r = RECIPE_BY_ID.get(id);
    return r ? (itemDef(r.output)?.name ?? r.output) : id;
  });
  const shown = names.slice(0, 3).join(", ");
  const more = names.length > 3 ? ` e mais ${names.length - 3}` : "";
  return names.length === 1 ? `Nova receita: ${shown}` : `Novas receitas: ${shown}${more}`;
}

export interface AvatarProbe {
  sessionId: string;
  x: number;
  y: number;
  z: number;
  local: boolean;
}

/** Entulho dos blocos que quebram (F90): pedaços de uma quebra e de um golpe, e como as lascas saem. */
const BREAK_PIECES = 24;
const CHIP_PIECES = 5;
const CHIP: RubbleBurst = { speed: 1.4, up: 1.6, life: 1.4 };

/** Janela em que uma trava pedida pela cena ainda pode chegar depois de ela sair (F99). */
const LATE_LOCK_MS = 1000;

/**
 * Cena da partida (F07): o próprio corpo é predito localmente com a mesma simulação do
 * servidor (reconciler do Colyseus); os demais são interpolados 100 ms no passado.
 */


/** Um grão da ponta da fita do golpe a cada tantos ms por corpo (Fase 14; o mesmo a 60 e a 144 Hz). */
const GRAIN_MS = 25;
/**
 * Efeito de cada chão nos passos (respingo, torrão, folha de capim, poeira): estilhaços em cinza neutro
 * (`gray`, sem bloom) ou poeira do pool da fumaça (`dust`, largura do sopro, e `gray` não conta); `n` por
 * passo andando.
 */
const STEP_FX: Readonly<Record<StepSurface, { gray: number; n: number; speed: number; size: number; life: number; up: number; dust?: number }>> = {
  agua: { gray: GRAYS.branco, n: 6, speed: 2.2, size: 0.06, life: 0.6, up: 0.95 },
  raso: { gray: GRAYS.branco, n: 9, speed: 2.6, size: 0.06, life: 0.6, up: 0.9 },
  // Lama e areia molhada em cinza médio: o chão do mangue é escuro, e um torrão escuro sumia nele.
  lama: { gray: GRAYS.medio, n: 6, speed: 1.9, size: 0.08, life: 0.65, up: 0.7 },
  molhada: { gray: GRAYS.medio, n: 4, speed: 1.4, size: 0.06, life: 0.5, up: 0.6 },
  grama: { gray: GRAYS.claro, n: 4, speed: 1.4, size: 0.07, life: 0.75, up: 0.85 },
  areia: { gray: 0, n: 3, speed: 1.1, size: 0, life: 0, up: 0, dust: 0.6 },
  terra: { gray: 0, n: 2, speed: 1, size: 0, life: 0, up: 0, dust: 0.5 },
  pedra: { gray: 0, n: 1, speed: 0.9, size: 0, life: 0, up: 0, dust: 0.4 },
  madeira: { gray: 0, n: 0, speed: 0, size: 0, life: 0, up: 0 },
};
export class GameScene {
  private readonly renderer: WebGLRenderer;
  private readonly world: LoadedWorld;
  private readonly session: MatchSession;
  private readonly view: WorldView;
  private readonly sim: HeightfieldWorld;
  private readonly input = new PlayerInput();
  private readonly predict;
  private readonly moveInput;
  private reconciler: Reconciler<AvatarType, MoveCommand> | undefined;
  private readonly avatars = new Map<string, AvatarEntry>();
  /** Quem os outros clientes avisaram que está puxando o arco (F49), desde quando (ms). */
  private readonly drawing = new Map<string, number>();
  /** Avisou o servidor de que começou a puxar e ainda não soltou nem desistiu. */
  private drawSent = false;
  /** Quadros da cena (o LOD da animação desencontra os avatares por ele). */
  private frameIndex = 0;
  /** Custo dos avatares por quadro (ms, média móvel): animação, mixers e item na mão. */
  private avatarMs = 0;
  private readonly frustum = new Frustum();
  private readonly projection = new Matrix4();
  private readonly sphere = new Sphere();
  /** Painel de FPS e desempenho: segue a opção; sem escolha, ligado só no desenvolvimento (fechado, nada é medido). */
  private readonly perfHud = new StatsHud(document.body, "desempenho");
  private perfOn = import.meta.env.DEV;
  private readonly timer = new FrameTimer();
  /** CPU do quadro no JavaScript (F36: orçamento de 6 ms do doc 05), do começo do quadro ao render. */
  private readonly cpu = new FrameTimer();
  /** Janela de medição dos orçamentos (todos os quadros desde o último `perf(true)`). */
  private readonly perfWindow = new PerfWindow();
  /** Tempo de GPU do quadro (Fase 12, Gate A), só com o painel ligado e onde a extensão existe. */
  private readonly gpu: GpuTimer;
  private readonly gpuMs = new FrameTimer();
  /** Fim do quadro anterior medido: o intervalo entre quadros entra na janela (percentis, engasgos). */
  private lastFrameAt: number | undefined;
  /** A aba voltou (Gate B7): os jorros que a rede pediu com o quadro parado não estouram juntos. */
  private readonly onVisibility = (): void => {
    if (!document.hidden) this.shards.dropBursts();
  };
  /** Pós-processamento (F52): do fluxo, que o aquece no carregamento; a cena só o usa e solta. */
  private readonly post: PostFx;
  /** Draw calls do quadro (F52): a cena, o pós e o quadro inteiro, que `stats()` e o HUD leem. */
  private readonly frameStats: FrameStats;
  private readonly unwatchFrame: () => void;
  /** Pedido de diagnóstico: conta os draw calls do próximo render por camada. */
  private breakdown: { counts: Record<string, number>; done: () => void } | undefined;
  /** Grupos de efeito escondidos pela medida do Gate D2 (`testHideFx`, só desenvolvimento). */
  private hiddenFx: ReadonlySet<string> = new Set();
  private fxHiding = false;
  /** Quadros pedidos pela captura de combate (`testGrab`, só desenvolvimento): o instante e quem espera a imagem. */
  private grabs: { at: number; done: (url: string) => void }[] = [];
  private readonly crosshair: HTMLElement;
  /** "Clique para voltar ao jogo" (F99): o ponteiro solto sem painel aberto, depois de ter travado. */
  private readonly resumeHint: HTMLElement;
  /** Câmera em terceira pessoa (F55): braço de mola, enquadramento por estado e colisão. */
  private readonly rig = new ThirdPersonCamera();
  private readonly cameraInput: CameraInput = { dt: 0, x: 0, y: 0, z: 0, yaw: 0, pitch: 0, speed: 0, grounded: true, crouching: false, swimming: false, submerged: false, carried: false, aiming: false, seaLevel: 0 };
  /** Ponto sob a cruz e a mira do olho até ele (F55), refeitos a cada quadro. */
  private readonly aim = new CrosshairAim();
  /** Origem (no plano do olho) e direção do raio da cruz, para construção, posicionamento e ping. */
  private readonly aimOrigin = new Vector3();
  /** Troca entre a câmera do jogador e a de morte/espectador: de onde a mistura sai e quando começou. */
  private readonly blendPos = new Vector3();
  private readonly blendQuat = new Quaternion();
  private readonly targetQuat = new Quaternion();
  private blendStart = -Infinity;
  private wasSpectating = false;
  /** Pouso (F55): no ar, a maior velocidade de queda vista; no chão, o mergulho da câmera. */
  private wasGrounded = true;
  private fallSpeed = 0;
  private detachInput: () => void = () => {};
  private last = performance.now();
  /** Rótulo do alvo e o cartão "interagir" (F81) com a tecla remapeada. */
  private readonly gameHud = new GameHud(document.body, () => keyName("interact"));
  private readonly actions: InventoryActions;
  private readonly icons: ItemIcons;
  private readonly inventoryPanel: InventoryPanel;
  private readonly belt: BeltHud;
  private readonly targeting: HarvestTargeting;
  private readonly drops: DropLayer;
  /**
   * Dissolve em mais famílias (F95): a peça que este jogador desmonta, os drops que saem do chão, a Cruz,
   * a Trouxa e o cardume que some se desfazem fora do lote, no programa do dissolve dos avatares.
   */
  private readonly dissolves: DissolvePool;
  /** Ids que o cliente já sabe estarem removidos (para refazer colisores e instâncias). */
  private readonly removedResources = new Set<number>();
  private readonly resourceIndex = new Map<number, number>();
  private attackQueued = false;
  /** Cadência da ação principal (golpe ou coleta), espelhando a porta do servidor. */
  private readonly actionGate = new ActionGate();
  private nextSyncAt = 0;
  private readonly offCollected: () => void;
  private readonly offUnlocked: () => void;
  private readonly crafting: CraftingPanel;
  private readonly trade: TradePanel;
  private readonly carranca: CarrancaPanel;
  private readonly simpatiasHud: SimpatiasHud;
  /** Bênçãos e Habilidades (F70, tecla B) e a oferta aberta do Juramento da Alvorada. */
  private readonly abilities: AbilitiesPanel;
  /** Ficha da classe (F82, tecla C). */
  private readonly sheet: SheetPanel;
  private oathOffer: string[] = [];
  /** Carga da ultimate no quadro anterior (o aviso de pronta, F72). */
  private lastCharge = 0;
  /** Caído no quadro anterior (o aviso, F74). */
  private wasFallen = false;
  private readonly structures: StructureLayer;
  private readonly ghost: PlacementGhost;
  /** Giro extra do fantasma (R), somado ao olhar. */
  private placeTurn = 0;
  private nextPlaceAt = 0;
  private readonly aimDir = new Vector3();
  private readonly vitalsHud = new VitalsHud(document.body);
  private readonly numbers = new DamageNumbers(document.body);
  private readonly shake = new CameraShake();
  /** Opções (F30): a tela, o que vale agora, o limite de lampejos e o Automático. */
  private readonly settingsPanel: SettingsPanel = sharedSettingsPanel();
  /** Pausa (F80): Esc com o ponteiro livre; o mundo não para. */
  private readonly pause = new PauseMenu(document.body, {
    resume: () => this.closePause(true),
    settings: () => this.openSettingsFromPause(),
    leave: () => {
      this.session.leave().catch((err: unknown) => console.warn("sair da partida falhou", err));
    },
  });
  /** Próxima atualização da ficha aberta (4 Hz). */
  private nextSheetAt = 0;
  /** Quando o ponteiro soltou pela última vez (o Esc que chega logo depois não mexe na pausa). */
  private unlockedAt = -Infinity;
  /** A cena pediu a soltura (painel, chat, pausa): ela não abre a pausa (F99). */
  private plannedUnlock = false;
  /** Quando a cena pediu a trava pela última vez (a que chega depois do `stop` solta). */
  private lockRequestedAt = -Infinity;
  /** O ponteiro já travou nesta partida (o aviso de clicar só vale depois disso). */
  private everLocked = false;
  private readonly onPointerLock = () => {
    if (document.pointerLockElement) {
      this.everLocked = true;
      return;
    }
    this.unlockedAt = performance.now();
    const planned = this.plannedUnlock;
    this.plannedUnlock = false;
    // Soltou sem a cena pedir (o Esc do navegador, trocar de janela): abre a pausa, como nos jogos (F99).
    if (!planned && !this.panelOpen) this.openPause();
  };
  /**
   * A tecla que fecha o último painel aberto trava o ponteiro no próprio keydown (F99): é o gesto que o
   * navegador aceita. O painel fecha no quadro seguinte, pelo caminho de sempre.
   */
  private readonly onRelockKey = (e: KeyboardEvent) => {
    if (e.repeat || document.pointerLockElement) return;
    const key = this.relockKey(e);
    if (key && relockOnKey(key, this.openPanels(), this.relockContext())) this.lockPointer();
  };
  /** Esc na fase de captura: antes dos painéis (que fecham no próprio Esc), para saber se havia um aberto. */
  private readonly onEscape = (e: KeyboardEvent) => {
    if (e.key !== "Escape" || e.repeat) return;
    // Painéis com Esc próprio só o ouvem com o foco dentro (revisão da F82): com o foco fora, a cena fecha.
    const unfocused = this.unfocusedSelfPanels(e.target as Node);
    const action = escapeAction({
      pauseOpen: this.pause.open,
      otherOpen: this.selfClosingOpen,
      settingsUnfocused: unfocused.length > 0,
      plainOpen: this.plainPanelOpen,
      locked: !!document.pointerLockElement,
      sinceUnlockMs: performance.now() - this.unlockedAt,
    });
    if (action === "nada") return;
    e.preventDefault();
    if (action === "abrir") this.openPause();
    else if (action === "fechar") this.closePause(false);
    else if (action === "fechar-opcoes") for (const close of unfocused) close();
    else this.closePlainPanels();
  };
  private prefs: Settings = settings.get();
  private readonly offSettings: () => void;
  private readonly flashes = new FlashLimiter();
  private readonly autoMeter = new AutoMeter();
  private autoMeasured = false;
  private readonly onTutorialPref = () => this.tutorial.adopt(mergeTutorial(this.tutorial.toJSON(), loadTutorial()));
  private readonly swings = new SwingTracker();
  private readonly offCombat: (() => void)[] = [];
  /** Contadores para os testes de ponta a ponta. */
  private readonly combatStats = { dealt: 0, taken: 0, swingsSeen: 0, creatureHits: 0, telegraphs: 0 };
  private hitMarkerUntil = 0;
  /** Quem estava caído no quadro anterior (para avisar quando alguém desmaia). */
  private readonly fainted = new Set<string>();
  /** Barras de chefe e linha do Desafio (F22), anéis das arenas e o aviso de noite de chefe já dado. */
  private readonly skillBar: SkillBar;
  private readonly skillVfx: SkillVfx;
  /** Eventos de energia (pesquisa 11): habilidades e impactos acendem o chão e a geometria vizinha. */
  private readonly energy: EnergyEvents;
  /** Efeitos de combate (F68): estilhaços, rastros, fios e a pausa de impacto. */
  private readonly shards: Shards;
  /** Assinatura dos inimigos maiores (F91): os passos agendados de cada ataque deles. */
  private readonly creatureFx: CreatureFxPlayer;
  private readonly hitStop = new HitStop();
  private readonly trailTip = new Vector3();
  private readonly trailBase = new Vector3();
  /** Fitas dos golpes (F92): uma por corpo golpeando; a que some cede a vaga para um golpe novo. */
  private readonly swingRibbons: SwingRibbons;
  /** Fitas da Investida (Gate C): uma por quem avança, pelo caminho real do corpo. */
  private readonly dashRibbons: RibbonPool;
  private readonly dashing = new Map<string, Ribbon>();
  private readonly statusGlyphs: StatusGlyphs;
  /** Chevron do dano recebido apontando a origem (F76). */
  private readonly chevrons: DamageChevrons;
  /** Quando a ativa fica pronta (relógio local, pela resposta do servidor; F65). */
  private skillReadyAt = 0;
  private readonly groupHud: GroupHud;
  /** HUD do mockup ui-b (F81): a coluna da esquerda (objetivos e grupo), a bússola, os botões e o minimapa. */
  private readonly hudLeft: HTMLElement;
  private readonly navHud: NavHud;
  /** A ilha e a névoa em canvas, divididas pelo mapa e pelo minimapa. */
  private readonly islandLayers: IslandLayers;
  private readonly bossBars: BossBarsHud;
  private readonly arenas: ArenaRings;
  private readonly relic: RelicHud;
  /** Redemoinhos do Saci (F24) e o redemoinho em que este jogador está segurando a Peneira. */
  private readonly whirlwinds: WhirlwindLayer;
  private peneiraOn: string | undefined;
  private peneiraRetryAt = 0;
  /** Jangada (F25): modelo, painel de reparo, linha de estado, anel de fogo e o convés ancorado. */
  private raftLayer: RaftLayer | undefined;
  private readonly raftPanel: RaftPanel;
  private readonly raftHud: HTMLElement;
  private readonly fireRing: ArenaRings;
  private raftGround: number | undefined;
  private deckAdded = false;
  private raftStart = { x: 0, z: 0 };
  private bossPhases = new Map<string, number>();
  /** Cerco do Boitatá (F26, Treta): parede de fogo, vinheta e a linha do HUD. */
  private readonly cerco: CercoLayer;
  private readonly cercoVignetteEl: HTMLElement;
  private readonly cercoHud: HTMLElement;
  private meteors = 0;
  private bossNightSeen = false;
  /** Chat, pings, mapa com névoa de exploração e a linha da votação de expulsão (F27). */
  private readonly chat: ChatBox;
  private readonly pings: PingMarkers;
  private readonly islandMap: IslandMap;
  private readonly fog = new FogOfWar();
  private readonly voteHud: HTMLElement;
  private nextFogAt = 0;
  private nextMapAt = 0;
  /** Tutorial por jogador (F28): progresso guardado no navegador, sinais desta partida e a seta. */
  private readonly tutorial: Tutorial;
  private readonly tutorialCard: TutorialCard;
  private readonly tutorialSignals: TutorialSignals = { ...NO_SIGNALS };
  private readonly onStorage = (e: StorageEvent) => {
    // Outra aba mexeu no tutorial: junta (preferência nova vence; progresso soma).
    if (e.key === `eldrun:${TUTORIAL_PREF}`) this.tutorial.adopt(mergeTutorial(this.tutorial.toJSON(), parseTutorial(e.newValue)));
  };
  private eatBase: number | undefined;
  private nextTutorialAt = 0;
  private tutorialTarget: { x: number; y: number; z: number } | undefined;
  /** O primeiro patch de estruturas já chegou (o que entra depois foi construído agora). */
  private structuresSynced = false;
  /** O estado inicial dos recursos já chegou (F90): entrada nova depois disso é golpe ao vivo. */
  private resourcesSynced = false;
  /** Peças que este jogador mandou desmontar (F90): saem sem entulho para ele (F95: em dissolve), até 1,5 s depois do pedido. */
  private readonly dismantling = new PendingDismantles();
  /** Áudio da partida (F29): eventos e estado de cada quadro viram som; legendas opcionais. */
  private readonly sound = new GameAudio(audio);
  /** Só nos e2e (`testAmbience`): parte da entrada dos leitos de ambiente forçada. */
  private ambienceOverride: Partial<AmbienceInput> | undefined;
  private readonly captionsHud: CaptionsHud;
  private readonly hazardFires = new Map<string, { x: number; y: number; z: number; until: number }>();
  private patuaWasBroken = false;
  private eatSeen: number | undefined;
  /** Vida e Patuá do quadro anterior: qualquer queda (golpe, criatura, Cerco, teste) vira som. */
  private hpSeen: { hp: number; patua: number; dead: boolean } | undefined;
  private hurtAt = 0;
  /** Câmera de quem morreu (F21) e cliques acumulados para trocar o aliado assistido. */
  private readonly spectator = new Spectator();
  private spectatorCycle = 0;
  /** Cruz em que este jogador está segurando E para reviver alguém. */
  private revivingCross: string | undefined;
  /** O ponteiro estava travado ao começar a reviver (soltá-lo, com Esc, interrompe). */
  private reviveLocked = false;
  private readonly offState: (() => void)[] = [];
  private readonly creatures: CreatureLayer;
  private readonly creatureSkins: CreatureSkins;
  /** Sombras-blob (F51): jogadores e criaturas mais perto, desenhadas pelo terreno e pelo capim. */
  private readonly blobs: BlobShadows;
  /** Pré-compilação dos programas (F51): a cena e as variantes que só aparecem depois. */
  private readonly prewarm: ProgramPrewarm;
  private prewarmMs: number | undefined;
  private readonly creatureBars: CreatureBars;
  private readonly telegraphs: Telegraphs;
  private readonly hazards: HazardDecals;
  private readonly lobs: Lobs;
  /** Orbes do cajado em voo (F98). */
  private readonly orbs: OrbLayer;
  private readonly smoke: Smoke;
  /** Poeira dos pés (Fase 14): os eventos de cada corpo pelo estado dele. */
  private readonly footDust = new FootDust();
  /** Passos com efeito de chão (respingo, lama, capim, poeira): os mesmos dos sons. */
  private readonly footSteps = new FootSteps();
  /** Passos com efeito por chão, desde o começo (diagnóstico, `stats().steps`). */
  private readonly stepCounts: Partial<Record<StepSurface, number>> = {};
  /** Quando cada corpo soltou o último grão da ponta da fita (Fase 14): pelo tempo, não pelo quadro. */
  private readonly grainAt = new Map<string, number>();
  /** Lambidas de chama (F93) sobre o fogo e as brasas dos perigos e na borda do Círculo da Fênix. */
  private readonly flameEmitters: FlameEmitters;
  /** O piso num ponto, para as fontes de chama (um só, sem closure nova a cada quadro). */
  private readonly floorAt = (x: number, z: number): number => this.sim.floorHeight(x, z);
  /** Chefes que acabaram (F93): as chamas garantidas das marcas deles não acendem mais. */
  private readonly endedBosses = new Set<string>();
  private readonly clockTracker = new ClockTracker();
  private readonly clockHud = new ClockHud(document.body);
  private clockReading: ClockReading | undefined;
  private readonly arrows: ArrowLayer;
  private readonly bowString = new BowString();
  private readonly bowHud = new BowHud(document.body);
  /** Contador dos próprios disparos (casa a flecha prevista com a do servidor). */
  private shots = 0;
  /** Caixas de colisão registradas por peça da grade (para refazer ao abrir a porta ou remover). */
  private readonly pieceBoxCount = new Map<string, number>();
  /** Giro da peça de construção (×90°, R gira). */
  private buildRot = 0;
  /** Peça na mira (porta abre com E; a própria desmonta com X). */
  private aimedPiece: string | undefined;
  /** Por que a peça na mão não entra onde a mira está ("" = entra); só para os testes. */
  private buildBlocker = "sem peça na mão";
  /** Estação de processo (Forno, Panela) ou baú aberto, pelo id da estrutura. */
  private openStation: string | undefined;
  /** Objetivos já anunciados (mapas). */
  private readonly objectivesSeen = { raft: false, guardians: false };
  /** Item na mão no quadro anterior: trocar cancela a puxada da corda. */
  private lastEquipped = "";
  /** Criaturas do quadro atual (posição interpolada), para a mira e as barras. */
  private creatureFrame: readonly CreatureSnapshot[] = [];

  constructor(renderer: WebGLRenderer, world: LoadedWorld, session: MatchSession, post: PostFx) {
    this.renderer = renderer;
    this.gpu = new GpuTimer(renderer.getContext() as WebGL2RenderingContext);
    this.world = world;
    this.session = session;
    this.view = new WorldView(renderer, world, 0.15);
    this.post = post;
    // Numa troca entre o canvas direto e o compositor (as Opções, o Automático), a pré-compilação
    // inteira (a cena e as variantes) roda para a saída nova antes de o caminho mudar.
    post.bind(this.view.atmosphere, (scene, camera, target) => this.prewarm.run(this.renderer, scene, camera, target));
    this.frameStats = new FrameStats(renderer.info);
    this.unwatchFrame = this.frameStats.watch(this.view.scene);
    this.sim = new HeightfieldWorld(world.heightfield);
    this.predict = Predict.get(session.room, { mode: "lerp", delay: 100 });
    this.predict.attachAll("avatars", { mode: "lerp", fields: ["x", "y", "z", "vx", "vz"] });
    this.predict.attachAll("avatars", { mode: "lerp", fields: ["yaw", "pitch"], angle: true });
    // Criaturas: a mesma interpolação de 100 ms dos outros jogadores (doc 06).
    this.predict.attachAll("creatures", { mode: "lerp", fields: ["x", "y", "z"] });
    this.predict.attachAll("creatures", { mode: "lerp", fields: ["yaw"], angle: true });
    this.moveInput = session.room.input({ type: MoveInput });
    const set = world.resources;
    for (let k = 0; k < set.count; k++) {
      this.resourceIndex.set(set.ids[k] as number, k);
      const cylinder = resourceCylinder(set.types[k] as number, set.x[k] as number, set.y[k] as number, set.z[k] as number, set.scale[k] as number);
      if (cylinder) this.sim.addCylinder(set.ids[k] as number, cylinder);
    }
    // Pontos de interesse: os mesmos colisores do servidor na predição (os adereços são do WorldView).
    const colliders = poiColliders(world.plan.pois, (x, z) => this.sim.height(x, z));
    for (const c of colliders.cylinders) this.sim.addCylinder(c.id, c);
    for (const b of colliders.boxes) this.sim.addBox(b.id, b);
    this.targeting = new HarvestTargeting(set);
    this.dissolves = new DissolvePool(this.view.scene, this.view.atmosphere);
    this.watchResources();
    this.resourcesSynced = true;
    this.drops = new DropLayer(this.view.scene, this.view.drop);
    this.watchDrops();
    this.actions = new InventoryActions(session.room);
    this.icons = new ItemIcons(renderer);
    // O ✕ dos painéis (F82) fecha como a tecla: com o som e, no inventário, a estação aberta junto.
    this.inventoryPanel = new InventoryPanel(document.body, this.actions, this.icons, () => {
      this.inventoryPanel.toggle(false);
      this.sound.play("ui-fechar", { bus: "ui" });
      this.closeStation();
    });
    this.belt = new BeltHud(document.body, this.icons);
    this.crafting = new CraftingPanel(document.body, this.actions, this.icons, () => {
      this.crafting.toggle(false);
      this.sound.play("ui-fechar", { bus: "ui" });
    });
    this.trade = new TradePanel(document.body, this.icons, (side, index) => {
      if (this.trade.id) this.session.room.send(MSG.trade, { id: this.trade.id, side, index });
    });
    this.simpatiasHud = new SimpatiasHud(document.body);
    this.abilities = new AbilitiesPanel(document.body, (id) => this.session.room.send(MSG.oath, { id }));
    this.sheet = new SheetPanel(document.body);
    this.carranca = new CarrancaPanel(
      document.body,
      (target) => {
        // Sozinho e morto (F54): a própria volta; senão, um aliado na Carranca aberta.
        if (this.carranca.solo) this.session.room.send(MSG.soloDeath, { choice: "pagar" });
        else if (this.carranca.id) this.session.room.send(MSG.carranca, { id: this.carranca.id, target });
      },
      () => this.session.room.send(MSG.soloDeath, { choice: "desistir" }),
    );
    this.bossBars = new BossBarsHud(document.body);
    // HUD do mockup ui-b (F81): objetivos e grupo à esquerda; bússola, botões do canto, minimapa, Relicário e
    // avisos à direita.
    this.hudLeft = h("div", { id: "hud-esquerda" });
    document.body.appendChild(this.hudLeft);
    // F109 (a referência "Neon Sentinel"): o cartão do jogador e as Bênçãos no alto da coluna da esquerda,
    // com os meus status, os objetivos e o grupo embaixo (a ordem é do CSS).
    this.hudLeft.append(this.vitalsHud.element, this.simpatiasHud.element);
    this.islandLayers = new IslandLayers(world.plan);
    this.navHud = new NavHud(document.body, this.islandLayers, [
      { id: "botao-opcoes", icon: "hud-icone-opcoes", label: "Opções", key: { action: "settings", name: keyName("settings") }, onClick: () => this.pressAction("settings") },
      { id: "botao-mochila", icon: "hud-icone-mochila", label: "Inventário", key: { action: "inventory", name: keyName("inventory") }, onClick: () => this.pressAction("inventory") },
      { id: "botao-mapa", icon: "hud-icone-mapa", label: "Mapa", key: { action: "map", name: keyName("map") }, onClick: () => this.pressAction("map") },
      { id: "botao-pausa", icon: "hud-icone-menu", label: "Pausa", key: { fixed: "Esc" }, onClick: () => this.togglePauseButton() },
    ]);
    this.groupHud = new GroupHud(document.body, this.hudLeft);
    // Habilidades (F65): barra, efeitos e glifos de status sobre as criaturas.
    this.skillBar = new SkillBar(document.body);
    this.energy = new EnergyEvents(this.view.atmosphere);
    this.skillVfx = new SkillVfx(this.view.scene, true, this.energy);
    // O entulho (F90) quica e assenta no chão da predição: o piso mais alto, se estiver abaixo do pedaço;
    // senão o terreno (um piso ou telhado acima de quem quebrou embaixo não puxa o entulho para cima).
    this.shards = new Shards(this.view.scene, (x, z, y) => {
      const floor = this.sim.floorHeight(x, z);
      return floor <= y + 0.05 ? floor : this.sim.height(x, z);
    });
    // Assinatura dos inimigos maiores (F91): some com o ataque cancelado (a mesma regra dos avisos) e o pouso
    // do bote barrado cai onde o corpo está no estado.
    this.creatureFx = new CreatureFxPlayer({
      shards: this.shards,
      vfx: this.skillVfx,
      ground: (x, z) => this.sim.floorHeight(x, z),
      shake: (at, x, z, amount, radius) => this.shakeAt(at, x, z, amount, radius),
      active: (id) => this.attacking(id),
      body: (id) => this.session.state.creatures.get(id),
      ended: (id) => this.endedBosses.has(id),
      // As chamas do fogo do Boitatá (F93), no pool do `fumaca`.
      flames: { flames: (x, y, z, color, n, spread, size, alpha) => this.smoke.flames(x, y, z, color, n, spread, size, alpha) },
      // A onda de choque do pós (F94): o `PostFx` só a solta no Alto e sem "Reduzir lampejos".
      shock: (x, y, z, strength, radius, at) => this.post.shockwave(x, y, z, strength, at, radius),
    });
    this.statusGlyphs = new StatusGlyphs(document.body);
    this.chevrons = new DamageChevrons(document.body);
    this.arenas = new ArenaRings(this.view.scene, (x, z) => this.sim.height(x, z));
    this.relic = new RelicHud(this.navHud.column);
    this.raftPanel = new RaftPanel(
      document.body,
      (part) => this.session.room.send(MSG.raftDeposit, { part }),
      () => this.session.room.send(MSG.raftSail, {}),
    );
    this.raftHud = h("p", { id: "jangada-estado", role: "status" });
    this.raftHud.hidden = true;
    document.body.appendChild(this.raftHud);
    this.fireRing = new ArenaRings(this.view.scene, () => this.sim.seaLevel + 0.6);
    this.cerco = new CercoLayer(this.view.scene, this.view.atmosphere, (x, z) => Math.max(this.sim.height(x, z), this.sim.seaLevel));
    this.cercoVignetteEl = h("div", { id: "cerco-vinheta", "aria-hidden": "true" });
    this.cercoHud = h("p", { id: "cerco-estado", role: "status" });
    this.cercoHud.hidden = true;
    document.body.append(this.cercoVignetteEl, this.cercoHud);
    this.chat = new ChatBox(document.body);
    this.chat.names = () => [...this.session.state.players.values()].filter((p) => p.sessionId !== this.session.sessionId).map((p) => p.name);
    this.chat.onSubmit = (text) => this.onChatSubmit(text);
    this.pings = new PingMarkers(document.body);
    this.islandMap = new IslandMap(document.body, this.islandLayers);
    this.tutorial = new Tutorial(loadTutorial());
    this.tutorialCard = new TutorialCard(
      this.hudLeft,
      () => {
        this.tutorial.skip(performance.now());
        this.saveTutorial();
      },
      () => {
        this.tutorial.disable();
        this.saveTutorial();
        toast("Tutorial desligado. Dá para religar no menu inicial.");
      },
    );
    this.captionsHud = new CaptionsHud(document.body, readPref(CAPTIONS_PREF, "0") === "1");
    this.voteHud = h("p", { id: "votacao", role: "status" });
    this.voteHud.hidden = true;
    document.body.appendChild(this.voteHud);
    this.structures = new StructureLayer(this.view.scene, this.view.foliage, toonMaterial(this.view.atmosphere, "#FF8A2A", 1, true), { treasure: this.view.treasure });
    this.ghost = new PlacementGhost(this.view.scene, createGhostMaterial(this.view.atmosphere));
    this.watchStructures();
    this.structuresSynced = true;
    // Criaturas animadas (F50): cada tipo carrega na primeira vez que uma dele chega perto.
    this.creatureSkins = new CreatureSkins(this.view.atmosphere);
    this.creatures = new CreatureLayer(this.view.scene, this.view.atmosphere, this.creatureSkins);
    this.blobs = new BlobShadows(this.view.atmosphere);
    // Sem todos os personagens das classes (F88: um arquivo pode faltar), algum jogador é boneco procedural.
    this.prewarm = new ProgramPrewarm(this.view.atmosphere, this.view.foliage, CLASS_AVATARS.every((id) => avatars.library?.has(id)));
    this.creatureBars = new CreatureBars(this.view.scene);
    this.telegraphs = new Telegraphs(this.view.scene, this.view.atmosphere);
    this.whirlwinds = new WhirlwindLayer(this.view.scene, this.view.atmosphere);
    this.hazards = new HazardDecals(this.view.scene, this.view.atmosphere);
    this.lobs = new Lobs(this.view.scene, this.view.atmosphere);
    this.orbs = new OrbLayer(this.view.scene);
    this.smoke = new Smoke(this.view.scene);
    this.flameEmitters = new FlameEmitters(this.smoke);
    // Fitas atrás das flechas (F92), na cor da classe de quem atirou, como os outros efeitos dele.
    this.arrows = new ArrowLayer(this.view.scene, this.view.atmosphere, (owner) => ribbonLook("flecha", playerColor(this.session.state.players.get(owner)), this.fxAlpha(owner), this.prefs.reduceFlashes));
    this.swingRibbons = new SwingRibbons(this.view.scene, (id) => {
      const alpha = this.fxAlpha(id);
      return alpha > 0 ? ribbonLook("golpe", playerColor(this.session.state.players.get(id)), alpha, this.prefs.reduceFlashes) : undefined;
    });
    this.dashRibbons = new RibbonPool(this.view.scene, 4);
    // Assombração se desfazendo ao amanhecer: nuvem de fumaça onde ela está.
    this.creatures.onStateChange = (c) => {
      if (c.state === "dissolvendo") this.smoke.burst(c.x, c.y + 0.8, c.z, 10, 0.8);
    };
    this.offCollected = session.room.onMessage(MSG.collected, (e: CollectedEvent) => {
      toast(`+${e.qty} ${itemDef(e.itemId)?.name ?? e.itemId}`);
      this.sound.play(e.itemId === "tostao" ? "tostoes" : "coleta", { bus: "ui", gain: 0.7 });
    });
    window.addEventListener("storage", this.onStorage);
    this.offUnlocked = session.onUnlocked((e: UnlockedEvent) => toast(unlockedText(e.recipes)));
    this.offCombat.push(
      session.room.onMessage(MSG.swing, (e: SwingEvent) => {
        this.combatStats.swingsSeen++;
        const bow = bowOf(e.itemId) !== undefined;
        // O disparo também chega como golpe: no boneco procedural o braço se mexe; no avatar animado, a puxada acaba.
        this.swings.start(e.sessionId, performance.now(), e.cycle ?? meleeCycle(meleeWeapon(e.itemId)), bow ? "arco" : "golpe");
        if (bow) this.drawing.delete(e.sessionId);
        // O arco também anima o braço (`golpe`), mas o som dele é o da flecha.
        const a = this.session.state.avatars.get(e.sessionId);
        if (a && !bow) this.sound.play("golpe-ar", { x: a.x, y: a.y + 1.2, z: a.z, gain: 0.6 });
      }),
      // Gestos dos outros (F49): a corda puxada e o golpe de coleta.
      session.room.onMessage(MSG.bowDraw, (e: BowDrawEvent) => {
        if (e.pulling) this.drawing.set(e.sessionId, performance.now());
        else this.drawing.delete(e.sessionId);
      }),
      session.room.onMessage(MSG.harvestSwing, (e: HarvestSwingEvent) => this.swings.start(e.sessionId, performance.now(), e.cycle, "coleta")),
      session.room.onMessage(MSG.damage, (e: DamageEvent) => this.onDamage(e)),
      session.room.onMessage(MSG.creatureAttack, (e: CreatureAttackEvent) => this.onCreatureAttack(e)),
      // Avisos e áreas no piso: o convés da Jangada fica acima do fundo do mar.
      session.room.onMessage(MSG.hazard, (e: HazardEvent) => {
        // No piso do ponto, ou no teto em que a poça caiu (a laje da Caverna): `e.y`.
        this.hazards.spawn(e, performance.now(), (x, z) => e.y ?? this.sim.floorHeight(x, z));
        // Fogo e brasa no chão crepitam enquanto durarem.
        if (e.kind === "fogo" || e.kind === "brasa") {
          const now = performance.now();
          this.hazardFires.set(e.id, { x: e.x, y: e.y ?? this.sim.floorHeight(e.x, e.z), z: e.z, until: now + e.ms });
          // Lambidas de chama (F93) no laranja do desenho do perigo, pela faixa ou pelo disco.
          this.flameEmitters.add(hazardSource(e, now, gameColor(TONE_COLORS.inimigo)));
        }
      }),
      session.room.onMessage(MSG.arrow, (e: ArrowEvent) => {
        this.arrows.fromEvent(e, performance.now(), this.session.room.clock?.smoothedRtt?.() ?? 0);
        if (e.owner !== this.session.sessionId && e.index === 0) this.sound.play("flecha-solta", { x: e.x, y: e.y, z: e.z });
      }),
      session.room.onMessage(MSG.shotRefused, (e: ShotRefusedEvent) => this.arrows.cancelShot(this.session.sessionId, e.shot)),
      session.room.onMessage(MSG.stationClosed, (e: StationClosedEvent) => {
        if (e.id !== this.openStation) return;
        // Fechada pelo servidor (longe demais): a tampa do baú bate também.
        this.chestLid(e.id, false);
        this.openStation = undefined;
      }),
      // Alguém voltou (Cruz ou Carranca).
      session.room.onMessage(MSG.revived, (e: RevivedEvent) => {
        const name = (sid: string) => (sid === this.session.sessionId ? "você" : (this.session.state.players.get(sid)?.name ?? "alguém"));
        const who = e.sessionId === this.session.sessionId ? "Você voltou" : `${name(e.sessionId)} voltou`;
        // Pagou a própria volta (F54): sem "graças a".
        toast(e.by === e.sessionId ? `${who} na ${structureName("carranca")}.` : `${who} na ${structureName(e.at === "cruz" ? "cruz" : "carranca")}, graças a ${name(e.by)}.`);
        const a = this.session.state.avatars.get(e.sessionId);
        this.sound.play("reviver", a && e.sessionId !== this.session.sessionId ? { x: a.x, y: a.y + 1, z: a.z, priority: PRIORITY.importante } : { bus: "ui" });
      }),
      // Chefes (F22): aviso, fim, Pedra de Encanto tocada e fim do Desafio.
      session.room.onMessage(MSG.bossWarning, (e: BossWarningEvent) => {
        toast(`${capitalize(bossName(e.type, "um chefe"))} vem aí!`);
        this.sound.play(e.type === "boitata" ? "rugido-fogo" : "rugido", { x: e.x, y: this.sim.height(e.x, e.z) + 2, z: e.z, priority: PRIORITY.critico });
      }),
      session.room.onMessage(MSG.bossEnd, (e: BossEndEvent) => {
        this.endedBosses.add(e.id);
        const name = bossName(e.type, "o chefe");
        const who = e.by === this.session.sessionId ? "Você" : (this.session.state.players.get(e.by)?.name ?? "O grupo");
        toast(e.result === "derrotado" ? `${who} derrubou ${name}!` : `${capitalize(name)} recuou.`);
        this.sound.play(e.result === "derrotado" ? "ferroada-vitoria" : "ferroada-falha", { bus: "ui", priority: PRIORITY.importante });
      }),
      session.room.onMessage(MSG.encantoStarted, (e: EncantoEvent) => {
        const by = e.by === this.session.sessionId ? "Você" : (this.session.state.players.get(e.by)?.name ?? "Alguém");
        const s = Math.round(e.inMs / 1000);
        const stone = this.session.state.structures.get(e.id);
        this.sound.play("encanto", stone ? { x: stone.x, y: stone.y + 1, z: stone.z, priority: PRIORITY.importante } : { bus: "ui" });
        if (e.kind === "guardiao") {
          const color = shrineColor(this.session.state.structures.get(e.id)?.itemId ?? "") ?? "";
          toast(`${by} despertou o ${guardianOf(color)?.name ?? "Guardião"}: ele surge em ${s} s.`);
        } else toast(`${by} tocou o ${structureName(e.kind === "chefe" ? "pedra-chefe" : "pedra-encanto")}: ${e.kind === "chefe" ? "um chefe surge" : "o Desafio começa"} em ${s} s.`);
      }),
      // Saci (F24): travessura, captura com a Peneira e a Pedra Fulminada que vai cair.
      session.room.onMessage(MSG.travessura, (e: TravessuraEvent) => this.onTravessura(e)),
      // Peneira recusada pelo servidor: solta para tentar de novo (enquanto segura).
      session.onNotice((n) => {
        if (n.code === "saci") this.peneiraOn = undefined;
      }),
      session.room.onMessage(MSG.saciCalm, (e: SaciCalmEvent) => {
        this.whirlwinds.calm = true;
        this.sound.play("saci-preso", { bus: "ui", priority: PRIORITY.importante });
        const who = e.by === this.session.sessionId ? "Você" : (this.session.state.players.get(e.by)?.name ?? "Alguém");
        toast(`${who} prendeu o Diabrete na Garrafa! Os redemoinhos sumiram até amanhã.`);
      }),
      session.room.onMessage(MSG.meteor, (e: MeteorEvent) => {
        const now = performance.now();
        const ground = this.sim.height(e.x, e.z);
        this.telegraphs.spawn({ id: `meteoro-${this.meteors++}`, attack: "pedra-fulminada", shape: "circulo", x: e.x, z: e.z, yaw: 0, range: 2.5, angle: 360, width: 0, inMs: e.inMs }, now, 0, (x, z) => this.sim.floorHeight(x, z), 0, "neutro");
        this.lobs.spawn({ x: e.x + 20, y: ground + 80, z: e.z - 10 }, { x: e.x, y: ground, z: e.z }, now, now + e.inMs);
        this.sound.play("meteoro", { x: e.x, y: ground + 10, z: e.z, priority: PRIORITY.critico });
        this.sound.play("explosao", { x: e.x, y: ground, z: e.z, priority: PRIORITY.critico, delay: e.inMs / 1000 });
        this.shakeAt(now + e.inMs, e.x, e.z, 0.8, 40);
      }),
      // Jangada (F25): achada; o Boitatá limpa o fogo do convés na troca de fase.
      session.room.onMessage(MSG.raftFound, (e: RaftFoundEvent) => {
        toast(e.by ? `${e.by === this.session.sessionId ? "Você achou" : `${this.session.state.players.get(e.by)?.name ?? "Alguém"} achou`} o Naufrágio!` : "O mapa revelou onde está o Naufrágio.");
        this.sound.play("ferroada-vitoria", { bus: "ui", priority: PRIORITY.importante });
      }),
      // Cúpula do Bastião (F96): o caldeirão para nela e ela trinca no ponto.
      session.room.onMessage(MSG.lobBlocked, (e: LobBlockedEvent) => {
        const now = performance.now();
        this.lobs.block(e.id, now);
        this.skillVfx.crack(e.x, e.y, e.z, playerColor(this.session.state.players.get(e.by)), now, this.fxAlpha(e.by));
        this.sound.play("patua-quebra", { x: e.x, y: e.y, z: e.z, gain: 0.6, priority: PRIORITY.importante });
      }),
      session.room.onMessage(MSG.hazardClear, (e: HazardClearEvent) => {
        this.hazards.clear(e.ids);
        for (const id of e.ids) {
          this.hazardFires.delete(id);
          this.flameEmitters.remove(id);
        }
      }),
      // Muiraquitã direto ao Relicário do grupo (doc 07, I8).
      session.room.onMessage(MSG.muiraquita, (e: MuiraquitaEvent) => {
        const name = this.session.state.players.get(e.by)?.name;
        const by = e.by === this.session.sessionId ? "sua conquista" : name ? `conquista de ${name}` : "conquista do grupo";
        toast(`${GEM_NAMES[e.color as keyof typeof GEM_NAMES] ?? e.color} no Relicário! (${by})`);
        this.sound.play("simpatia-lendaria", { bus: "ui", priority: PRIORITY.importante });
      }),
      session.room.onMessage(MSG.challengeEnd, (e: ChallengeEndEvent) => {
        const who = e.by === this.session.sessionId ? "você" : (this.session.state.players.get(e.by)?.name ?? "quem tocou o Menir");
        toast(e.result === "vencido" ? `Desafio vencido! A Bênção vai para ${who}.` : "Desafio perdido: o grupo saiu do círculo e os monstros sumiram.");
        this.sound.play(e.result === "vencido" ? "ferroada-vitoria" : "ferroada-falha", { bus: "ui", priority: PRIORITY.importante });
      }),
      // Simpatia comprada: quem ganhou e qual (doc 07, I7: vai para quem pagou).
      session.room.onMessage(MSG.simpatiaGained, (e: SimpatiaGainedEvent) => {
        toast(simpatiaText(e.simpatia, e.sessionId === this.session.sessionId ? undefined : (this.session.state.players.get(e.sessionId)?.name ?? "Alguém")));
        if (e.sessionId === this.session.sessionId) {
          const rarity = SIMPATIA_BY_ID.get(e.simpatia)?.rarity;
          this.sound.play(rarity === "lendaria" ? "simpatia-lendaria" : rarity === "rara" ? "simpatia-rara" : "simpatia-comum", { bus: "ui", priority: PRIORITY.importante });
        }
      }),
      session.room.onMessage(MSG.tradeDone, (e: TradeDoneEvent) => {
        const name = itemDef(e.item)?.name ?? e.item;
        toast(e.side === "comprar" ? `Comprou ${e.qty}× ${name} por ${e.price} moedas.` : `Vendeu ${e.qty}× ${name} por ${e.price} moedas.`);
        this.sound.play("tostoes", { bus: "ui" });
      }),
      // Achado lendário num baú: anunciado a todos (doc 03).
      session.room.onMessage(MSG.lootFound, (e: LootFoundEvent) => {
        const who = e.sessionId === this.session.sessionId ? "Você" : (this.session.state.players.get(e.sessionId)?.name ?? "Alguém");
        toast(`${who} achou ${itemDef(e.itemId)?.name ?? e.itemId} no ${structureName(e.chest)}!`);
        if (e.sessionId === this.session.sessionId) this.sound.play("simpatia-lendaria", { bus: "ui", priority: PRIORITY.importante });
      }),
      // Orbe do cajado (F98): voa na cor de quem atirou; no fim, a faísca mágica (F93) onde parou.
      session.room.onMessage(MSG.orb, (e: OrbEvent) => {
        this.orbs.spawn(e, playerColor(this.session.state.players.get(e.owner)), performance.now(), this.session.room.clock?.smoothedRtt?.() ?? 0, this.fxAlpha(e.owner));
        if (e.owner !== this.session.sessionId) this.sound.play("golpe-ar", { x: e.x, y: e.y, z: e.z, gain: 0.5, rate: 1.4 });
      }),
      session.room.onMessage(MSG.orbEnd, (e: OrbEndEvent) => {
        this.orbs.end(e.id);
        const alpha = this.fxAlpha(e.owner);
        const owner = this.session.state.players.get(e.owner);
        const color = playerColor(owner);
        this.smoke.magic(e.x, e.y, e.z, color, Math.max(2, Math.round((e.hit ? 7 : 4) * Math.min(1, alpha))), e.hit ? 0.4 : 0.28, alpha);
        if (alpha > 0) this.skillVfx.pulse(e.x, e.z, this.sim.floorHeight(e.x, e.z), e.hit ? 1 : 0.6, color, classSkills(classOf(owner?.classId ?? "")).ativa.shape, performance.now(), alpha * 0.6);
      }),
      session.room.onMessage(MSG.arrowEnd, (e: ArrowEndEvent) => {
        this.arrows.end(e, performance.now());
        this.sound.play("flecha-crava", { x: e.x, y: e.y, z: e.z, gain: 0.7 });
      }),
      // Golpe de coleta aceito (só para quem golpeou): o tutorial conta galhadas, moitas e árvores derrubadas.
      session.room.onMessage(MSG.harvested, (e: HarvestedEvent) => {
        const k = this.resourceIndex.get(e.id);
        const type = k === undefined ? undefined : RESOURCE_TYPES[this.world.resources.types[k] as number];
        if (!type) return;
        // Som do golpe aceito pelo material; a árvore que cai range e bate no chão.
        const set = this.world.resources;
        const at = k === undefined ? undefined : { x: set.x[k] as number, y: (set.y[k] as number) + 1, z: set.z[k] as number };
        this.sound.play(type.tool === "machado" ? "machado-madeira" : type.tool === "picareta" ? "picareta" : "colher-mao", at ? { ...at, gain: 0.8 } : {});
        if (!e.removed) return;
        if (type.tool === "machado" && at) this.sound.play("arvore-cai", { ...at, priority: PRIORITY.importante });
        if (type.id === "galhada" || type.id === "moita") this.tutorialSignals.collected++;
        if (type.tool === "machado") this.tutorialSignals.treesFelled++;
      }),
      // Chat (F27): falas já filtradas pelo servidor, mensagens de sistema por chave e pings.
      session.room.onMessage(MSG.chat, (e: ChatEvent) => {
        this.chat.push({ kind: "fala", text: e.text, from: e.from, name: e.name, dead: e.dead, at: performance.now() });
        if (e.from !== this.session.sessionId && !this.chat.log.isMuted(e.from)) this.sound.play("chat", { bus: "ui", gain: 0.6 });
      }),
      session.room.onMessage(MSG.system, (e: SystemEvent) => this.chat.push({ kind: "sistema", text: systemText(e), at: performance.now() })),
      session.room.onMessage(MSG.skillAck, (ack: SkillAck) => {
        if (ack.slot === "ativa") this.skillReadyAt = performance.now() + ack.cooldownMs;
        if (!ack.ok) {
          this.skillBar.refuse(ack.slot);
          this.sound.play("ui-erro", { bus: "ui", gain: 0.5 });
        }
      }),
      session.room.onMessage(MSG.skillUsed, (e: SkillEvent) => this.skillUsed(e)),
      session.room.onMessage(MSG.oathOffer, (e: OathOffer) => {
        this.oathOffer = e.options;
        if (e.options.length > 0) {
          toast(`Juramento da Alvorada: escolha uma melhoria (${keyName("abilities")}).`);
          this.abilities.select("habilidades");
        }
      }),
      session.room.onMessage(MSG.elo, (e: EloEvent) => {
        const who = e.by.map((id) => {
          const p = this.session.state.players.get(id);
          return { color: playerColor(p), shape: classSkills(classOf(p?.classId ?? "")).ativa.shape };
        });
        this.skillVfx.elo(e.x, e.z, this.sim.floorHeight(e.x, e.z), who, performance.now());
        // O acorde do Elo (F72): uma nota por participante (sozinho, o de dois).
        this.sound.play(`elo-${Math.min(4, Math.max(2, e.by.length)) as 2 | 3 | 4}`, { x: e.x, y: this.sim.floorHeight(e.x, e.z) + 1, z: e.z, gain: e.by.includes(this.session.sessionId) ? 0.8 : 0.5 });
      }),
      session.room.onMessage(MSG.marked, (e: MarkEvent) => {
        // Na malha, a cor é a da classe de quem pingou (F63: cor = quem; F85: da classe; o símbolo diz o tipo).
        const pinger = this.session.state.players.get(e.by);
        const owner = pinger ? classColor(pinger.classId) : undefined;
        this.pings.add(e, e.by === this.session.sessionId ? "Você" : (pinger?.name ?? "Alguém"), performance.now(), owner);
        this.sound.play("ping", { x: e.x, y: e.y + 1, z: e.z, priority: PRIORITY.importante });
      }),
    );
    this.crosshair = document.createElement("div");
    this.crosshair.id = "mira";
    this.crosshair.setAttribute("aria-hidden", "true");
    document.body.appendChild(this.crosshair);
    this.resumeHint = document.createElement("div");
    this.resumeHint.id = "retomar";
    this.resumeHint.textContent = "Clique para voltar ao jogo";
    this.resumeHint.hidden = true;
    document.body.appendChild(this.resumeHint);
    // Opções (F30): aplicadas agora e a cada mudança.
    this.applySettings(this.prefs);
    this.offSettings = settings.subscribe((next) => this.applySettings(next));
    // Fechadas as Opções abertas pela pausa, o foco volta para ela; senão, para o jogo.
    this.settingsPanel.onClose = () => {
      if (!this.pause.open) return this.renderer.domElement.focus?.();
      this.pause.setSettingsOpen(false);
      this.pause.focus("opcoes");
    };
    window.addEventListener(TUTORIAL_EVENT, this.onTutorialPref);
    settings.setAuto(undefined);
  }

  start(): void {
    this.detachInput = this.input.attach(this.renderer.domElement);
    window.addEventListener("keydown", this.onEscape, { capture: true });
    window.addEventListener("keydown", this.onRelockKey, { capture: true });
    document.addEventListener("pointerlockchange", this.onPointerLock);
    document.addEventListener("visibilitychange", this.onVisibility);
    // F52: o compositor do preset (já aquecido no carregamento; senão, agora) e, na pré-compilação, o
    // alvo dele: a cena compila os programas com a saída de cor em que vai desenhar.
    try {
      this.post.warm();
    } catch (error) {
      console.warn("aquecimento do pós-processamento", error);
    }
    // Programas do dissolve, do fantasma, do skinning e dos lotes já no começo, não no primeiro uso.
    const started = performance.now();
    this.prewarm
      .run(this.renderer, this.view.scene, this.view.camera, this.post.sceneTarget())
      .then(() => (this.prewarmMs = performance.now() - started))
      .catch((error: unknown) => console.warn("pré-compilação dos programas", error));
    this.renderer.setAnimationLoop((now: number) => this.frame(now));
  }

  /** Encerra a cena e libera tudo o que ela criou (idempotente). */
  stop(): void {
    setWorldAccent(undefined);
    this.renderer.setAnimationLoop(null);
    // Fora da partida, o acento volta ao da classe lembrada (F85: vale em toda parte, menu inclusive).
    applyClassTheme(rememberedClass());
    // O compositor sai com a cena (o próximo carregamento aquece outro): ele guardava esta cena e a câmera.
    this.post.bind(undefined);
    this.post.dispose();
    this.gpu.dispose();
    this.unwatchFrame();
    this.detachInput();
    this.vitalsHud.remove();
    this.offCollected();
    this.offUnlocked();
    for (const off of this.offCombat) off();
    this.numbers.remove();
    for (const off of this.offState) off();
    this.inventoryPanel.remove();
    this.crafting.remove();
    this.trade.remove();
    this.carranca.remove();
    this.bossBars.element.remove();
    this.groupHud.remove();
    this.navHud.remove();
    this.hudLeft.remove();
    this.creatureFx.clear();
    this.skillVfx.dispose();
    this.energy.clear();
    this.chevrons.remove();
    this.statusGlyphs.element.remove();
    this.skillBar.element.remove();
    this.arenas.dispose();
    this.relic.element.remove();
    this.whirlwinds.dispose();
    this.raftLayer?.dispose();
    this.raftPanel.remove();
    this.raftHud.remove();
    this.fireRing.dispose();
    this.cerco.dispose();
    this.cercoVignetteEl.remove();
    this.cercoHud.remove();
    this.chat.remove();
    this.offSettings();
    window.removeEventListener("keydown", this.onEscape, { capture: true });
    window.removeEventListener("keydown", this.onRelockKey, { capture: true });
    document.removeEventListener("pointerlockchange", this.onPointerLock);
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.pause.remove();
    this.settingsPanel.hide();
    window.removeEventListener(TUTORIAL_EVENT, this.onTutorialPref);
    settings.setAuto(undefined);
    this.sound.dispose();
    this.captionsHud.remove();
    this.tutorialCard.remove();
    window.removeEventListener("storage", this.onStorage);
    this.pings.remove();
    this.islandMap.remove();
    this.voteHud.remove();
    this.simpatiasHud.remove();
    this.abilities.remove();
    this.sheet.remove();
    this.structures.dispose();
    this.ghost.dispose();
    this.belt.remove();
    this.icons.dispose();
    this.drops.dispose();
    this.dissolves.dispose();
    this.creatures.dispose();
    this.creatureSkins.dispose();
    this.creatureBars.dispose();
    this.telegraphs.dispose();
    this.hazards.dispose();
    this.lobs.dispose();
    this.orbs.dispose();
    this.flameEmitters.clear();
    this.smoke.dispose();
    // Estilhaços, entulho e a camada ambiente (Gate B1): a geometria instanciada e o material deles.
    this.shards.dispose();
    this.clockHud.remove();
    this.arrows.dispose();
    this.swingRibbons.dispose();
    this.dashRibbons.dispose();
    this.dashing.clear();
    this.bowHud.remove();
    this.gameHud.remove();
    this.predict.dispose();
    for (const entry of this.avatars.values()) entry.view.dispose();
    this.avatars.clear();
    this.prewarm.dispose();
    this.view.dispose();
    this.perfHud.remove();
    this.crosshair.remove();
    this.resumeHint.remove();
    this.releasePointer();
    this.dropLateLock();
  }

  /**
   * Uma trava pedida há pouco pode chegar depois de a cena sair, com o menu na tela e ninguém para soltá-la
   * (revisão da F99): por um segundo, a que chegar solta. Pelo evento, que todo navegador manda (nem todo
   * devolve promessa); passado o segundo, a trava é da próxima cena.
   */
  private dropLateLock(): void {
    if (performance.now() - this.lockRequestedAt > LATE_LOCK_MS) return;
    const canvas = this.renderer.domElement;
    const onLate = () => {
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    };
    document.addEventListener("pointerlockchange", onLate);
    setTimeout(() => document.removeEventListener("pointerlockchange", onLate), LATE_LOCK_MS);
  }

  /**
   * Ação principal sem mouse travado. Só para e2e (o headless não trava o ponteiro): sem
   * argumento, um clique; com `true`/`false`, aperta ou solta (para comer).
   */
  primaryAction(held?: boolean): void {
    if (held === undefined) {
      this.input.primary(true);
      this.input.primary(false);
    } else this.input.primary(held);
  }

  /** Mira como o mouse faria (só e2e, sem pointer lock no headless). */
  lookAt(yaw: number, pitch: number): void {
    this.input.yaw = yaw;
    this.input.pitch = pitch;
  }

  /** Põe a cruz sobre o ponto (x, y, z): o olhar que leva o raio da câmera até ele (só e2e). */
  aimAt(x: number, y: number, z: number): { yaw: number; pitch: number } {
    const look = this.rig.lookThrough(x, y, z, { yaw: 0, pitch: 0 });
    this.lookAt(look.yaw, look.pitch);
    return look;
  }

  /**
   * Custo da câmera (F55, e2e `budgets`): roda `n` vezes o quadro do rig, da mira e do tremor com o
   * estado de agora (sem desenhar nem mexer na câmera de verdade). O e2e mede o heap em volta.
   */
  cameraBench(n: number): number {
    const started = performance.now();
    const input = this.cameraInput;
    for (let i = 0; i < n; i++) {
      const pose = this.rig.update(input, this.sim);
      this.aim.begin(pose.x, pose.y, pose.z, pose.yaw, pose.pitch, input.x, input.y + MOVE.eyeHeight, input.z, this.sim);
      this.aim.finish(pose.yaw, pose.pitch);
      this.shake.step(1 / 60, 0);
    }
    return performance.now() - started;
  }

  /** Posições como este cliente as vê (próprio predito, remotos interpolados), para testes. */
  probeAvatars(): AvatarProbe[] {
    const result: AvatarProbe[] = [];
    this.session.state.avatars.forEach((avatar, sessionId) => {
      const p = this.position(sessionId, avatar);
      result.push({ sessionId, ...p, local: sessionId === this.session.sessionId });
    });
    return result;
  }

  /** Criaturas como este cliente as vê (interpoladas), para testes. */
  probeCreatures(): CreatureSnapshot[] {
    return this.creatureFrame.map((c) => ({ ...c }));
  }

  stats(): Record<string, unknown> {
    return {
      ...this.view.terrain.stats,
      fps: this.timer.fps,
      // Draw calls da cena principal no último quadro (doc 05; F52: sem os do pós e dos ícones, que
      // vão em `frameCalls`, o quadro inteiro, e em `post.calls`).
      drawCalls: this.frameStats.last.drawCalls,
      shadowCalls: this.view.shadowCalls,
      shadowTriangles: this.view.shadowTriangles,
      frameCalls: this.frameStats.last.frameCalls,
      // Programas de shader compilados da cena (F51: teto no e2e `budgets`; F52: os do pós em
      // `post.programs`) e o tempo da pré-compilação.
      programs: countPrograms(this.renderer.info.programs).scene,
      // Nomes dos programas vivos (diagnóstico dos tetos de programa: quem entrou a mais).
      programNames: (this.renderer.info.programs ?? []).map((p) => p.name),
      prewarmMs: this.prewarmMs,
      // Pós-processamento (F52): tier, efeitos, multisampling, draw calls e programas.
      post: this.post.stats(this.frameStats.last),
      // Efeitos de cenário (F90): entulho vivo e assentado, e a camada ambiente do último quadro.
      scenery: { rubble: this.shards.rubbleStats.live, resting: this.shards.rubbleStats.resting, ambient: this.shards.ambientCount },
      // Orçamentos do doc 05 (F36): triângulos do último quadro e o JavaScript por quadro.
      triangles: this.frameStats.last.triangles,
      jsMs: this.cpu.averageMs,
      jsWorstMs: this.cpu.worstMs,
      // Recursos de GPU vivos (Gate A): a reentrada na cena e a troca de tier não podem fazê-los crescer.
      memory: { ...this.renderer.info.memory },
      // Efeitos de habilidade vivos (anéis, zonas e peças) e os estilhaços, sob os tetos dos pools.
      vfx: { skills: this.skillVfx.count, batches: this.skillVfx.batchCount, shards: this.shards.live, flames: this.flameEmitters.count },
      gpuTimer: this.gpu.available,
      lastProcessed: this.moveInput.lastProcessed,
      command: this.input.command(),
      resources: this.view.resources.drawn,
      // Enfeites do chão (F44) desenhados agora e a densidade da opção "Capim e detalhes".
      details: { ...this.view.details.stats(), density: QUALITY.details },
      // Capim sob construções (F53): peças e estações na máscara e os envios dela.
      grass: { ...this.view.grassMask.stats(), density: QUALITY.grass },
      held: Object.fromEntries([...this.session.state.players.entries()].map(([id, p]) => [id, p.held])),
      // Avatares (F49): personagem (ou `procedural`), clipe, gesto e cadência da animação de cada um, o
      // custo deles por quadro (ms, média) e o carregamento do pacote.
      avatars: Object.fromEntries([...this.avatars].map(([id, entry]) => [id, { ...entry.view.stats(), slot: this.session.state.players.get(id)?.slot, classId: this.session.state.players.get(id)?.classId }])),
      avatarsMs: this.avatarMs,
      avatarLoad: avatars.stats(),
      // Itens no chão (F47): no estado, desenhados, tipos com malha própria e cubos de sobra; draw calls.
      drops: { total: this.session.state.drops.size, ...this.drops.stats() },
      // Dissolve das famílias (F95): peças se desfazendo, os começados e os sem vaga.
      dissolves: this.dissolves.stats(),
      // F50: animadas agora, trocas de corpo, teto do preset e os modelos carregados.
      creatures: { ...this.creatures.stats, cap: QUALITY.animated, skins: this.creatureSkins.stats(), bars: this.creatureBars.visible, telegraphs: this.telegraphs.count, hazards: this.hazards.count, lobs: this.lobs.count },
      // Chamas e magia (F93): vivas no pool, o draw ligado, as fontes (perigos e Fênix) e as lambidas soltas.
      flames: { ...this.smoke.stats, sources: this.flameEmitters.count, emitted: this.flameEmitters.emitted },
      // Passos com efeito de chão, por chão (respingo, lama, capim, poeira).
      steps: { ...this.stepCounts, ...this.smoke.marksLive },
      // Sombras-blob (F51): projetores oferecidos, escolhidos (ids) e quantas cópias do Guardião entre eles (0).
      blobs: (() => {
        const b = this.blobs.stats();
        return { ...b, limit: QUALITY.blobs, decoys: b.ids.filter((id) => this.creatures.decoy(id)).length };
      })(),
      arrows: { ...this.arrows.stats, shots: this.shots, charging: this.bowString.charging },
      // Fitas dos golpes (F92): seguindo, sumindo e desenhadas agora.
      swingRibbons: this.swingRibbons.stats,
      clock: this.clockReading && { day: this.clockReading.day, night: this.clockReading.night, phase: this.clockReading.phase, dusk: this.clockReading.dusk },
      // Estações posicionadas (sem as peças da grade e os baús naturais).
      structures: [...this.session.state.structures.values()].filter((s) => !s.grid && !worldStructure(s.itemId)).length,
      // Camada das estruturas (F48): com modelo e procedurais, tampas (e abertas aqui), LOD de longe, draw calls.
      structureLayer: this.structures.stats(),
      chests: {
        natural: [...this.session.state.structures.values()].filter((s) => naturalChestOf(s.itemId)).length,
        placed: [...this.session.state.structures.values()].filter((s) => storageSlotsOf(s.itemId) > 0 && !naturalChestOf(s.itemId)).map((s) => ({ x: s.x, z: s.z })),
        coins: this.session.state.inventories.get(this.session.sessionId)?.coins ?? 0,
        objectives: this.session.state.objectives?.toJSON(),
      },
      death: {
        dead: this.session.state.vitals.get(this.session.sessionId)?.dead === true,
        wait: this.session.state.deaths?.get(this.session.sessionId)?.wait,
        following: this.spectator.following,
        crosses: [...this.session.state.structures.entries()].filter(([, st]) => st.itemId === "cruz").map(([id, st]) => ({ id, owner: st.owner, x: st.x, z: st.z })),
        trouxas: [...this.session.state.structures.values()].filter((st) => st.itemId === "trouxa").length,
      },
      bosses: {
        list: [...(this.session.state.bosses?.entries() ?? [])].map(([id, b]) => ({ id, type: b.type, source: b.source, phase: b.phase, hp: this.session.state.creatures.get(id)?.hp ?? 0 })),
        bar: this.bossBars.text,
        night: this.session.state.bossNight,
        arenas: this.arenas.count,
        relic: this.relic.count,
        mandinga: this.session.state.mandinga,
        cerco: { active: this.session.state.cerco?.active, radius: this.session.state.cerco?.radius, x: this.session.state.cerco?.x, z: this.session.state.cerco?.z, visible: this.cerco.visible, vignette: Number(this.cercoVignetteEl.style.opacity || 0) },
        raft: { stage: this.session.state.raft?.stage, countdown: this.session.state.raft?.countdown, found: this.session.state.raft?.found, x: this.session.state.raft?.x, z: this.session.state.raft?.z, deck: this.deckAdded },
        whirlwinds: this.whirlwinds.count,
        inverted: this.session.state.avatars.get(this.session.sessionId)?.inverted === true,
        // Pedras-de-raio no próprio inventário (o loot do chefe).
        raio: [...(this.session.state.inventories.get(this.session.sessionId)?.slots ?? [])].reduce((n, st) => n + (st.itemId === "fragmento-raio" ? st.qty : 0), 0),
      },
      // Peças dos adereços dos pontos de interesse no mapa (doc 04; montagens do F43).
      pois: this.view.pois.instances,
      economy: {
        simpatias: Object.fromEntries(this.session.state.inventories.get(this.session.sessionId)?.simpatias.entries() ?? []),
        simpatiaChests: [...this.session.state.structures.entries()].filter(([, s]) => simpatiaChestOf(s.itemId)).map(([id, s]) => ({ id, x: s.x, z: s.z, open: s.open, kind: s.itemId })),
        mascates: [...this.session.state.structures.entries()].filter(([, s]) => s.itemId === MASCATE_ITEM).map(([id, s]) => ({ id, x: s.x, z: s.z })),
        trade: this.trade.id,
      },
      combat: { ...this.combatStats },
      vitals: this.session.state.vitals.get(this.session.sessionId)?.toJSON(),
      stamina: (this.reconciler?.state as AvatarType | undefined)?.stamina,
      body: (() => {
        const a = this.session.state.avatars.get(this.session.sessionId);
        const p = this.reconciler?.state as AvatarType | undefined;
        return a && { canEat: a.canEat, useTime: a.useTime, eatCount: a.eatCount, predictedUse: p?.useTime, predictedEat: p?.eatCount, command: this.command().use };
      })(),
      known: knowledgeOf(this.session.state, this.session.sessionId)?.size ?? 0,
      crafting: this.crafting.open,
      ghost: this.ghost.visible,
      station: { open: this.openStation, lit: this.structures.lit },
      building: (() => {
        let pieces = 0;
        let open = 0;
        const doors: { x: number; y: number; z: number }[] = [];
        this.session.state.structures.forEach((s) => {
          if (!s.grid) return;
          pieces++;
          if (s.open) open++;
          if (s.itemId === "parede-porta") doors.push({ x: s.x, y: s.y, z: s.z });
        });
        return { pieces, open, doors, aimed: this.aimedPiece, blocker: this.buildBlocker };
      })(),
      rtt: this.session.room.clock?.smoothedRtt?.(),
      // Câmera em terceira pessoa (F55): braço depois da colisão, enquadramento, pesos, sumiço do boneco e mira.
      camera: {
        arm: this.rig.arm,
        framing: { ...this.rig.framing },
        weights: { ...this.rig.weights },
        fade: this.rig.fade,
        trauma: this.shake.level,
        aim: { yaw: this.aim.aimYaw, pitch: this.aim.aimPitch, hit: this.aim.hit, distance: this.aim.t, body: this.aim.body, x: this.aim.x, y: this.aim.y, z: this.aim.z },
      },
      audio: { ...audio.stats(), music: this.sound.musicState, captions: this.sound.captions.map((c) => c.text) },
      settings: {
        fov: this.view.camera.fov,
        sensitivity: this.input.sensitivity,
        invertY: this.input.invertY,
        jump: [...this.input.bindings.jump],
        pixelRatio: this.renderer.getPixelRatio(),
        quality: settings.graphics(),
        shake: this.shake.scale,
        reduceFlashes: document.body.classList.contains("sem-lampejos"),
        colorblind: document.body.classList.contains("daltonico"),
        open: this.settingsPanel.open,
        paused: this.pause.open,
        captions: this.captionsHud.enabled,
      },
      tutorial: {
        enabled: this.tutorial.enabled,
        step: this.tutorial.current?.id,
        done: [...this.tutorial.done],
        visible: this.tutorialCard.visible,
        text: this.tutorialCard.text,
        arrow: !this.tutorialCard.arrow.hidden,
      },
      social: {
        chat: this.chat.log.lines.map((l) => (l.kind === "fala" ? `${l.name}: ${l.text}` : l.text)),
        chatShown: this.chat.shown,
        chatOpen: this.chat.open,
        blocked: this.panelOpen,
        pings: this.pings.list(performance.now()).map((p) => ({ by: p.event.by, kind: p.event.kind })),
        map: this.islandMap.open,
        fog: this.fog.fraction,
        vote: this.voteHud.hidden ? undefined : this.voteHud.textContent,
        serverRtt: this.session.serverRtt,
      },
    };
  }

  private ensureReconciler(): void {
    if (this.reconciler) return;
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!mine) return;
    this.input.yaw = mine.yaw;
    this.reconciler = this.predict.reconciler<AvatarType, MoveCommand>(mine, {
      input: this.moveInput,
      step: (ctx, state, command) => stepTick(state, command, this.sim, ctx.dt, ctx.subSteps),
      smoothMs: 65,
    });
  }

  private frame(now: number): void {
    const started = performance.now();
    this.frameStats.begin();
    if (this.perfOn) {
      this.gpu.poll((ms, current) => {
        this.gpuMs.push(ms);
        // Só os quadros da janela aberta pelo último `perf(true)`: uma consulta de antes que chega agora fica fora.
        if (current) this.perfWindow.addGpu(ms);
      });
      this.gpu.begin();
    }
    try {
      this.frameBody(now);
    } finally {
      const f = this.frameStats.end();
      if (this.perfOn) {
        this.gpu.end();
        const ms = performance.now() - started;
        this.cpu.push(ms);
        this.perfWindow.add(ms, f.drawCalls, f.triangles, f.frameCalls, this.lastFrameAt === undefined ? undefined : now - this.lastFrameAt);
        this.lastFrameAt = now;
      } else this.lastFrameAt = undefined;
    }
  }

  /** Orçamentos medidos desde o último `perf(true)` (F36; `reset` recomeça a janela). */
  perf(reset = false): PerfSummary {
    const now = performance.now();
    const summary = this.perfWindow.summary(now);
    if (reset) {
      this.perfWindow.reset(now);
      this.gpu.mark();
    }
    return summary;
  }

  private frameBody(now: number): void {
    const dt = clampDelta((now - this.last) / 1000);
    if (this.perfOn) this.timer.push(now - this.last);
    this.last = now;
    this.ensureReconciler();
    this.handleSocialInput();
    this.handleInventoryInput();
    // Travado com um painel ou a pausa aberta (Continuar com a pausa de volta, a tecla que não fechou o
    // painel como previsto): solta, o mouse é deles. Depois das teclas, que fecham o painel da trava (F99).
    if (document.pointerLockElement && this.panelOpen) this.releasePointer();

    if (this.input.consumePrimaryClick()) this.attackQueued = true;
    this.updatePeneira();
    // Morto, o clique troca o aliado assistido (doc 02 §18).
    if (this.attackQueued && this.session.state.vitals.get(this.session.sessionId)?.dead) {
      this.attackQueued = false;
      this.spectatorCycle++;
    }
    // Soltou o E, abriu um painel, soltou o ponteiro ou morreu: para de reviver (o servidor
    // confere a distância a cada tick).
    const hold = {
      held: this.input.isDown("interact"),
      panelOpen: this.panelOpen,
      unlocked: this.reviveLocked && !document.pointerLockElement,
      dead: this.session.state.vitals.get(this.session.sessionId)?.dead === true,
    };
    if (this.revivingCross && stopReviving(hold)) {
      this.session.room.send(MSG.revive, { id: this.revivingCross, holding: false });
      this.revivingCross = undefined;
    }
    // Envia exatamente os passos fixos devidos neste quadro; o reconciler prevê cada envio.
    const steps = this.predict.tick(now);
    for (let i = 0; i < steps; i++) {
      Object.assign(this.moveInput.data, this.command());
      this.moveInput.send();
    }

    this.blobs.begin();
    // Pausa de impacto (F68): só as animações param; a simulação e a câmera seguem.
    const animDt = dt * this.hitStop.scale(now);
    this.syncAvatars(animDt, now);
    this.updateCamera(dt, now);
    this.syncCreatures(animDt, now);
    this.creatures.castBlobs(this.blobs, (x, z) => this.sim.height(x, z));
    this.view.resources.castBlobs(this.blobs, this.view.camera.position.x, this.view.camera.position.z);
    this.blobs.end(this.view.camera.position.x, this.view.camera.position.z, QUALITY.blobs);
    // Quem anda no capim o deita (os mesmos atores das sombras-blob, sem os recursos).
    this.view.trampleGrass(this.blobs.casters, (x, z) => this.sim.height(x, z), now / 1000);
    this.syncWorld(now);
    this.updateVitals();
    if (!this.updatePlacement(now)) this.updateAction(now);
    // Desistiu da puxada sem atirar (painel, morte, sem flecha, trocou de item): os outros param de vê-la.
    if (this.drawSent && !this.bowString.charging) {
      this.session.room.send(MSG.draw, { stop: true });
      this.drawSent = false;
    }
    this.drops.update(this.session.state, now / 1000, this.view.camera.position.x, this.view.camera.position.z);
    this.structures.update(now / 1000, this.view.camera.position.x, this.view.camera.position.z);
    if (this.inventoryPanel.open && this.openStation !== undefined) {
      const known = knowledgeOf(this.session.state, this.session.sessionId);
      // O mapa filtrado pela StateView só existe no cliente depois que a primeira estação entra na view.
      const stations = this.session.state.stations as typeof this.session.state.stations | undefined;
      const title = structureName(this.session.state.structures.get(this.openStation)?.itemId ?? "");
      this.inventoryPanel.updateStation(stations?.get(this.openStation), (id) => known?.get(id) === true, now, this.openStation, title);
    } else if (this.openStation === undefined) this.inventoryPanel.updateStation(undefined, () => false, now);
    this.announceObjectives();
    this.numbers.update(dt, this.view.camera, window.innerWidth, window.innerHeight);
    // Habilidades (F65): a barra, os efeitos e os glifos de status das criaturas.
    {
      const nowMs = performance.now();
      const me = this.session.state.players.get(this.session.sessionId);
      // Investida: a recarga é a do corpo previsto.
      if (this.myActive() === "investida") this.skillReadyAt = nowMs + ((this.reconciler?.state as AvatarType | undefined)?.dashCooldown ?? 0) * 1000;
      const charge = this.session.state.vitals.get(this.session.sessionId)?.ultCharge ?? 0;
      // Caído agora (F74): o aviso para quem caiu.
      const fallen = this.session.state.vitals.get(this.session.sessionId)?.fallen === true;
      if (fallen && !this.wasFallen) toast(`Você caiu: um aliado tem ${FALL.seconds} s para te levantar.`);
      this.wasFallen = fallen;
      // Ultimate carregada agora (F72): o aviso sonoro, uma vez.
      if (charge >= ULT_CHARGE.max && this.lastCharge < ULT_CHARGE.max) this.sound.play("ultimate-pronta", { bus: "ui", gain: 0.6 });
      this.lastCharge = charge;
      this.skillBar.update(skillBarView(me?.classId ?? "", this.skillReadyAt, nowMs, { ativa: keyName("skill"), ultimate: keyName("ultimate") }, charge));
      this.skillVfx.update(nowMs, this.view.camera.quaternion);
      // Vagas de energia disputadas perto de quem joga (Gate B4): a câmera segue o jogador local.
      this.energy.focus(this.view.camera.position.x, this.view.camera.position.z);
      // "Reduzir lampejos": o acender local de energia fica pela metade (área e tempo continuam).
      this.energy.update(nowMs, QUALITY.post.reduceFlashes ? 0.5 : 1);
      const creatures = this.session.state.creatures;
      const players = this.session.state.players;
      this.statusGlyphs.update(
        (function* () {
          for (const c of creatures.values()) {
            // O Guardião mostra a forma da gema dele (F76, doc 09 §1.3 e §2.4), na cor do chefe.
            const mark = guardianMark(c.type);
            if (c.status === 0 && !mark) continue;
            // `statusBy` é o número de quem aplicou; a cor é a da classe dessa pessoa (F85).
            yield { x: c.x, y: c.y, z: c.z, status: c.status, ending: c.statusEnding, color: playerColor(playerBySlot(players.values(), c.statusBy - 1)), height: CREATURE_BY_ID.get(c.type)?.height ?? 1.8, ...(mark && { mark }) };
          }
        })(),
        this.view.camera,
        window.innerWidth,
        window.innerHeight,
      );
    }
    // HUD do grupo (F63): quadros dos aliados e setas para quem está fora da tela.
    this.groupHud.update(this.session.state, this.session.sessionId, this.view.camera, window.innerWidth, window.innerHeight, performance.now());
    // Bússola e seta do minimapa (F81): giram com a câmera a cada quadro.
    this.navHud.frame(this.view.camera);
    this.crosshair.classList.toggle("acerto", now < this.hitMarkerUntil);
    // A atmosfera inteira (céu, luz, neblina, água) segue o relógio do servidor.
    this.clockReading = this.clockTracker.read(this.session.state.clock, now);
    this.clockHud.update(this.clockReading, now, this.session.state.mandinga);
    // Na travessia, a ilha some na neblina (até 9× mais densa no fim dos 60 m).
    const raft = this.session.state.raft;
    const travelled = raft?.stage === "travessia" && this.raftGround !== undefined ? Math.min(1, Math.hypot(raft.x - this.raftStart.x, raft.z - this.raftStart.z) / (RAFT.speed * RAFT.travel)) : 0;
    this.view.setPhase(this.clockReading.phase, now / 1000, this.session.state.mandinga, 1 + 8 * travelled);
    this.updateSocial(now);
    this.updateTutorial(now);
    this.measureAuto(dt * 1000);
    this.updateSound(now);
    // F52: a vinheta do pós dá lugar às vinhetas CSS (vida baixa, afogando, o Cerco) enquanto elas estão na tela.
    this.post.vignetteFade = Math.max(Number(this.cercoVignetteEl.style.opacity || 0), this.vitalsHud.vignetteOn ? 1 : 0);
    this.post.desaturate = this.vitalsHud.desaturation;
    this.applyHiddenFx();
    if (this.breakdown) this.renderCounted(this.breakdown, dt);
    else this.view.render(this.post, dt, this.frameStats);
    this.takeGrabs(now);
    // A cruz é a mira da terceira pessoa: some só na câmera de morte e no espectador.
    this.crosshair.style.display = this.wasSpectating ? "none" : "block";
    this.resumeHint.hidden = !showResumeHint({ everLocked: this.everLocked, locked: !!document.pointerLockElement, panelOpen: this.panelOpen });
    if (this.perfOn) {
      this.perfHud.update(now, () => [
        `${this.timer.fps.toFixed(0)} fps · ${this.timer.averageMs.toFixed(1)} ms (pior ${this.timer.worstMs.toFixed(1)}) · JS ${this.cpu.averageMs.toFixed(1)} ms · rtt ${(this.session.room.clock?.smoothedRtt?.() ?? 0).toFixed(0)} ms`,
        `draw calls ${this.frameStats.last.drawCalls} da cena · ${this.frameStats.last.postCalls} do pós (${this.post.plan().tier}) · ${this.frameStats.last.frameCalls} no quadro`,
        this.gpu.available ? `GPU ${this.gpuMs.averageMs.toFixed(1)} ms (pior ${this.gpuMs.worstMs.toFixed(1)})` : "GPU sem medida (extensão ausente)",
      ]);
    }
  }

  /**
   * Recursos removidos e renascidos mudam os colisores da predição: aplicados no instante em que
   * o patch é decodificado (callbacks do schema), antes dos próximos passos preditos.
   */
  private watchResources(): void {
    const $ = getStateCallbacks(this.session.room);
    const resources = $(this.session.state).resources;
    this.offState.push(
      resources.onAdd((entry, key) => {
        const id = Number(key);
        this.offState.push(
          $(entry).listen(
            "removed",
            (removed: boolean, before: boolean | undefined) => {
              // Esgotou agora (não o que já estava esgotado ao entrar): o entulho do modelo (F90). A entrada
              // nasce no primeiro golpe, então a que chega já esgotada depois do estado inicial é um golpe só.
              if (removed && (before === false || (before === undefined && this.resourcesSynced))) this.breakResource(id, false);
              this.setResourceRemoved(id, removed);
            },
            true,
          ),
          // Golpe de coleta de qualquer jogador (F90): lascas do modelo; o primeiro golpe é a entrada nova.
          $(entry).listen("hp", (hp: number, before: number | undefined) => {
            if (!entry.removed && (before === undefined ? this.resourcesSynced : hp < before)) this.breakResource(id, true);
          }),
        );
      }),
      // Entrada apagada = renasceu (cardume): volta instância e colisor.
      resources.onRemove((_entry, key) => this.setResourceRemoved(Number(key), false)),
    );
  }

  /**
   * Entulho de uma estrutura (F90): quebrada, ~24 pedaços dos triângulos do modelo saindo para fora, com o
   * estalo aceso no meio (sem ele com "Reduzir lampejos"); golpeada, umas lascas.
   */
  private breakStructure(id: string, s: StructureType, chip: boolean): void {
    const mesh = this.structures.objectOf(id) as Mesh | undefined;
    if (!mesh?.geometry) return;
    mesh.updateMatrix();
    const pieces = debrisPieces({ geometry: mesh.geometry, matrix: mesh.matrix }, chip ? CHIP_PIECES : BREAK_PIECES);
    this.shards.debris(pieces, chip ? CHIP : this.breakBurst(s.x, s.y + 0.8, s.z));
  }

  /**
   * Entulho de um recurso (F90): esgotado, os pedaços do modelo até 2,5 m do pé (a árvore cai inteira; o
   * entulho é do tronco e da moita); golpeado, lascas. Os da água não soltam nada.
   */
  private breakResource(id: number, chip: boolean): void {
    const k = this.resourceIndex.get(id);
    if (k === undefined) return;
    const set = this.world.resources;
    const type = RESOURCE_TYPES[set.types[k] as number];
    const source = this.view.resources.debrisSource(k);
    if (!type || !source) return;
    // O cardume esgotado não quebra (F95): se desfaz, como o que some sem quebrar.
    if (type.waterDepth) {
      const part = chip ? undefined : this.view.resources.dissolvePart(k);
      if (part) this.dissolves.start([part], performance.now());
      return;
    }
    const want = chip ? CHIP_PIECES : BREAK_PIECES;
    const low = (set.y[k] as number) + 2.5;
    const pieces = debrisPieces(source, want * 4).filter((p) => p.y < low).slice(0, want);
    this.shards.debris(pieces, chip ? CHIP : this.breakBurst(set.x[k] as number, (set.y[k] as number) + 0.8, set.z[k] as number));
  }

  private breakBurst(x: number, y: number, z: number): RubbleBurst {
    return { speed: 2.2, up: 2.4, life: 3.2, ...(QUALITY.post.reduceFlashes ? {} : { pop: { x, y, z, size: 0.55 } }) };
  }

  private setResourceRemoved(id: number, removed: boolean): void {
    if (removed === this.removedResources.has(id)) return;
    this.view.resources.setRemoved(id, removed);
    if (removed) {
      this.removedResources.add(id);
      this.sim.removeCylinder(id);
      return;
    }
    this.removedResources.delete(id);
    const k = this.resourceIndex.get(id);
    const set = this.world.resources;
    if (k === undefined) return;
    const cylinder = resourceCylinder(set.types[k] as number, set.x[k] as number, set.y[k] as number, set.z[k] as number, set.scale[k] as number);
    if (cylinder) this.sim.addCylinder(id, cylinder);
  }

  /**
   * Comando do passo. Com painel aberto a ação não "vaza" para o jogo. Caído, a própria
   * simulação ignora o comando (`MoveState.downed`), igual no servidor.
   */
  private command(): MoveCommand {
    const command = this.input.command();
    if (this.panelOpen) command.use = false;
    // Mira no ponto sob a cruz (F55): a simulação aceita até 25° da câmera, a deste comando.
    this.aim.reaim(command.yaw, command.pitch);
    command.aimYaw = this.aim.aimYaw;
    command.aimPitch = this.aim.aimPitch;
    if (command.leap) this.aimLeap(command);
    return command;
  }

  /**
   * Ponto do Salto do Falcão (F97): o que a mira atinge (chão, tronco, criatura); sem nada na mira, o alcance
   * todo na direção dela.
   * A simulação limita ao alcance da carga, igual no servidor.
   */
  private aimLeap(command: MoveCommand): void {
    const me = (this.reconciler?.state as AvatarType | undefined) ?? this.session.state.avatars.get(this.session.sessionId);
    if (!me) return;
    let x = this.aim.x;
    let z = this.aim.z;
    if (!this.aim.hit) {
      const dir = this.aimRay();
      const flat = Math.hypot(dir.x, dir.z) || 1;
      x = me.x + (dir.x / flat) * LEAP_RANGE;
      z = me.z + (dir.z / flat) * LEAP_RANGE;
    }
    // A mesma faixa que o servidor impõe: perto da borda do mundo, a predição não diverge.
    command.leapX = Math.max(-LEAP_AIM_LIMIT, Math.min(LEAP_AIM_LIMIT, x));
    command.leapZ = Math.max(-LEAP_AIM_LIMIT, Math.min(LEAP_AIM_LIMIT, z));
  }

  /** HUD de atributos (Fôlego do corpo previsto) e aviso quando alguém desmaia. */
  private updateVitals(): void {
    const state = this.session.state;
    const mine = state.vitals.get(this.session.sessionId);
    const body = (this.reconciler?.state as AvatarType | undefined) ?? state.avatars.get(this.session.sessionId);
    const record = state.deaths?.get(this.session.sessionId);
    const watching = this.spectator.following ? state.players.get(this.spectator.following)?.name : undefined;
    if (mine && body) this.vitalsHud.update(vitalsView(mine, body, { wait: record?.wait ?? 0, treta: state.mode === "treta", spectating: watching, solo: record?.solo === true }));
    state.vitals.forEach((v, sessionId) => {
      if (v.dead && !this.fainted.has(sessionId)) {
        this.fainted.add(sessionId);
        // A carga do Rompe-Núcleo sai com a morte de vez, como no servidor, mesmo sem `DamageEvent` (afogado, fome).
        this.skillVfx.cancel(sessionId, "disparo-rompe-nucleo");
        if (sessionId !== this.session.sessionId) toast(`${state.players.get(sessionId)?.name ?? "Alguém"} morreu.`);
      } else if (!v.dead) this.fainted.delete(sessionId);
    });
  }

  /** Cinto e painel refletem o inventário (comparam uma chave e só redesenham se mudou). */
  /** Classe do jogador local cujo acento está na UI (F85; vazio: recalcula). */
  private accentClass = "";

  private syncWorld(now: number): void {
    if (now < this.nextSyncAt) return;
    this.nextSyncAt = now + 100;
    const inventory = this.session.state.inventories.get(this.session.sessionId);
    this.belt.update(inventory);
    this.simpatiasHud.update(inventory?.simpatias.entries());
    if (this.abilities.open) {
      const me = this.session.state.players.get(this.session.sessionId);
      this.abilities.update(abilitiesView(me?.classId ?? "", inventory?.simpatias.entries() ?? [], me?.oaths ?? "", this.oathOffer, { ativa: keyName("skill"), ultimate: keyName("ultimate") }));
    }
    // A ficha a 4 Hz (revisão da F82): os números mudam devagar e o DOM não se refaz a cada quadro.
    if (this.sheet.open && performance.now() >= this.nextSheetAt) {
      this.nextSheetAt = performance.now() + 250;
      this.updateSheet();
    }
    this.updateTrade();
    this.updateCarranca();
    this.updateBosses();
    if (this.inventoryPanel.open) this.inventoryPanel.update(inventory);
    if (this.crafting.open) this.crafting.update(this.craftingTabs());
    // Tema da classe na UI (F85, ADR 0017): o acento e o fantasma da construção na cor da classe do
    // jogador local, a que veio da tela de escolha (o servidor é quem confirma).
    const me = this.session.state.players.get(this.session.sessionId);
    const klass = classOf(me?.classId ?? "");
    const classId = klass.id;
    // O medalhão do cartão do jogador (F109): a forma da classe e o número, como nos quadros do grupo; só
    // com o jogador no estado (antes disso, a classe padrão e o "1" seriam um palpite).
    if (me) this.vitalsHud.setIdentity(SHAPE_GLYPHS[klass.shape], klass.name, me.slot + 1);
    if (classId !== this.accentClass) {
      this.accentClass = classId;
      this.ghost.setColor(applyClassTheme(classId)!);
    }
  }

  /** Abas de fabricação a partir do inventário, do caderno e das estações perto do corpo predito. */
  private craftingTabs() {
    const state = this.session.state;
    const inventory = state.inventories.get(this.session.sessionId);
    const known = knowledgeOf(state, this.session.sessionId);
    const mine = state.avatars.get(this.session.sessionId);
    const p = mine ? this.position(this.session.sessionId, mine) : { x: 0, z: 0 };
    const slots = (inventory?.slots ?? []) as unknown as StackLike[];
    return craftingTabs(slots, (id) => known?.get(id) === true, stationsNear(state.structures.values(), p.x, p.z));
  }

  /** Estruturas novas entram na cena e na predição (mesmo cilindro e id do servidor). */
  private watchStructures(): void {
    const $ = getStateCallbacks(this.session.room);
    this.offState.push(
      $(this.session.state).structures.onAdd((s: StructureType, id: string) => {
        this.structures.add(id, s);
        // Construção de jogador tira os enfeites do chão da célula dela (os do mundo ficam nos POIs, já limpos)
        // e o capim embaixo dela (F53).
        if (!worldStructure(s.itemId)) {
          this.view.details.setStructure(id, s.x, s.z);
          this.view.grassMask.setStructure(id, s);
        }
        if (s.owner === this.session.sessionId) {
          if (s.itemId === "bancada") this.tutorialSignals.bancadaPlaced = true;
          if (s.grid) this.tutorialSignals.piecesBuilt++;
        }
        // Construir e posicionar: baque de madeira (estruturas que já estavam no mundo ao entrar, não).
        if (s.owner === this.session.sessionId && this.structuresSynced) this.sound.play("construir", { x: s.x, y: s.y + 0.5, z: s.z });
        // Golpe numa peça ou estação (F90): lascas do modelo dela, para todos (a vida vem no estado).
        this.offState.push(
          $(s).listen("hp", (hp: number, before: number | undefined) => {
            if (before !== undefined && hp < before) this.breakStructure(id, s, true);
          }),
        );
        if (s.grid) {
          // Peça da grade: as mesmas caixas do servidor entram na predição; a porta as refaz.
          this.setPieceBoxes(id, s);
          this.offState.push(
            $(s).listen("open", (open: boolean, before: boolean | undefined) => {
              this.structures.setOpen(id, open);
              this.setPieceBoxes(id, s);
              if (before !== undefined && open !== before) this.sound.play(open ? "porta" : "porta-fecha", { x: s.x, y: s.y + 1, z: s.z });
            }),
          );
          return;
        }
        this.sim.addCylinder(structureColliderId(id), structureCylinder(s.x, s.y, s.z, s.itemId));
        // Baú de Simpatia comprado: a tampa levanta para todos.
        if (simpatiaChestOf(s.itemId))
          this.offState.push(
            $(s).listen("open", (open: boolean, before: boolean | undefined) => {
              this.structures.setOpen(id, open);
              if (open && before === false) this.sound.play("bau", { x: s.x, y: s.y + 0.5, z: s.z });
            }),
          );
        // Assentada num piso que saiu: desce junto com o colisor.
        this.offState.push(
          $(s).listen("y", (y: number) => {
            this.structures.setY(id, y);
            this.view.grassMask.setStationY(id, y);
            this.sim.removeCylinder(structureColliderId(id));
            this.sim.addCylinder(structureColliderId(id), structureCylinder(s.x, y, s.z, s.itemId));
          }),
        );
        // Fogo aceso (público) vira chama na estação; acender (não o que já estava aceso ao entrar) faz o som.
        this.offState.push(
          $(s).listen(
            "lit",
            (lit: boolean, before: boolean | undefined) => {
              this.structures.setLit(id, lit);
              if (lit && before === false) this.sound.play("acender", { x: s.x, y: s.y + 0.5, z: s.z });
            },
            true,
          ),
        );
      }),
      $(this.session.state).structures.onRemove((s: StructureType, id: string) => {
        // Peça ou estação destruída (não a Cruz que some ao reviver nem a Trouxa recolhida).
        const now = performance.now();
        if (s.itemId !== "cruz" && s.itemId !== "trouxa") {
          this.sound.play("destruir", { x: s.x, y: s.y + 0.5, z: s.z, gain: 0.8 });
          // Blocos que quebram (F90): o entulho sai do modelo antes de ele sair da camada. A peça que este
          // jogador desmontou sai sem entulho para ele (os outros não sabem que foi desmonte: o estado só tira a peça).
          if (!this.dismantling.take(id, now)) this.breakStructure(id, s, false);
          // O próprio desmonte (F95) se desfaz com a borda na cor da classe dele, nas vagas guardadas.
          else this.dissolves.start(this.structures.dissolveParts(id).map((p) => ({ ...p, edge: playerColor(this.session.state.players.get(this.session.sessionId)) })), now, true);
        }
        // O que sai sem quebrar (F95: a Cruz, a Trouxa) se desfaz com a borda neutra. O lote já sai sem ele.
        else this.dissolves.start(this.structures.dissolveParts(id), now);
        this.structures.remove(id);
        this.view.details.removeStructure(id);
        this.view.grassMask.removeStructure(id);
        this.clearPieceBoxes(id);
        this.sim.removeCylinder(structureColliderId(id));
      }),
    );
  }

  /**
   * Drop que sai do estado (F95), apanhado ou expirado (o estado não diz qual; quem apanhou recebe o
   * `collected`, os outros não): se desfaz na pose do último quadro, e a camada já não o desenha.
   */
  private watchDrops(): void {
    const $ = getStateCallbacks(this.session.room);
    this.offState.push(
      $(this.session.state).drops.onRemove((drop: ItemDropType, key: string) => {
        const part = this.drops.dissolvePart(key, drop);
        if (part) this.dissolves.start([part], performance.now());
      }),
    );
  }

  private setPieceBoxes(id: string, s: StructureType): void {
    this.clearPieceBoxes(id);
    const spec = pieceOf(s.itemId);
    if (!spec) return;
    const boxes = pieceBoxes(spec.kind as PieceKind, s, s.open);
    boxes.forEach((b, i) => this.sim.addBox(Number(id) * 4 + i, b));
    this.pieceBoxCount.set(id, boxes.length);
  }

  private clearPieceBoxes(id: string): void {
    const n = this.pieceBoxCount.get(id) ?? 0;
    for (let i = 0; i < n; i++) this.sim.removeBox(Number(id) * 4 + i);
    this.pieceBoxCount.delete(id);
  }

  /**
   * Com uma peça de construção na mão, a mira vira construção (doc 03): prévia na grade com as
   * mesmas regras do servidor (verde ou vermelho com o motivo), R gira 90° e o clique constrói.
   */
  private updateBuild(itemId: string, kind: PieceKind, slot: number, now: number): void {
    if (this.input.consumePressed("rotate")) this.buildRot = (this.buildRot + 1) % 4;
    const queued = this.attackQueued;
    this.attackQueued = false;
    // O raio da cruz a partir do plano do olho (F55): o alcance conta de perto do corpo, não da câmera.
    const dir = this.aimRay();
    const origin = { x: this.aimOrigin.x, y: this.aimOrigin.y, z: this.aimOrigin.z };
    const pieces = piecesFrom(this.session.state.structures);
    const height = (x: number, z: number) => this.sim.height(x, z);
    // Mira: a peça mais perto no raio; senão, o chão.
    const onPiece = aimPiece(origin, dir, pieces, BUILD.reach + 1);
    const ground = aimAtGround(origin, dir, height, BUILD.reach + 1);
    const groundPoint = ground && { x: ground.x, y: height(ground.x, ground.z), z: ground.z };
    const dist = (p: { x: number; y: number; z: number }) => Math.hypot(p.x - origin.x, p.y - origin.y, p.z - origin.z);
    const hit = onPiece && (!groundPoint || dist(onPiece.point) < dist(groundPoint)) ? hitLevel(onPiece.point, onPiece.box) : groundPoint;
    const name = itemDef(itemId)?.name ?? itemId;
    if (!hit) {
      this.ghost.hide();
      this.buildBlocker = "sem chão";
      this.gameHud.setTarget(`${name} · mire no chão perto de você`, true);
      return;
    }
    // Alcance e vista a partir do olho do corpo previsto (como o servidor), não da câmera.
    const mine = this.session.state.avatars.get(this.session.sessionId);
    const body = mine && this.position(this.session.sessionId, mine);
    const eye = body ? { x: body.x, y: body.y + MOVE.eyeHeight, z: body.z } : origin;
    const preview = buildPreview(kind, hit, this.buildRot, eye, pieces, this.sim);
    this.buildBlocker = preview.blocker ?? "";
    this.ghost.show(itemId, preview.center.x, preview.center.y, preview.center.z, preview.center.yaw, !preview.blocker);
    this.gameHud.setTarget(preview.blocker ? `${name} · ${preview.blocker}` : `${name} · clique para construir · ${keyName("rotate")} gira`, Boolean(preview.blocker));
    if (queued && !preview.blocker && now >= this.nextPlaceAt) {
      this.session.room.send(MSG.build, { slot, ...preview.pos });
      this.nextPlaceAt = now + 250;
    }
  }

  /**
   * Com uma estação na mão, a mira vira posicionamento: fantasma verde/vermelho pelas mesmas
   * regras do servidor, R gira e o clique posiciona. Devolve se está nesse modo.
   */
  private updatePlacement(now: number): boolean {
    const state = this.session.state;
    const inventory = state.inventories.get(this.session.sessionId);
    const mine = state.avatars.get(this.session.sessionId);
    const held = inventory && mine ? equippedItem(inventory as unknown as InventoryLike) : "";
    const piece = pieceOf(held);
    this.buildBlocker = "sem peça na mão";
    if (inventory && mine && piece && !this.panelOpen) {
      this.updateBuild(held, piece.kind as PieceKind, inventory.selected, now);
      return true;
    }
    if (!inventory || !mine || !isPlaceableStation(held) || this.panelOpen) {
      this.ghost.hide();
      return false;
    }
    const name = itemDef(held)?.name ?? held;
    if (this.input.consumePressed("rotate")) this.placeTurn = wrapYaw(this.placeTurn + ROTATE_STEP);
    const queued = this.attackQueued;
    this.attackQueued = false;
    const p = this.position(this.session.sessionId, mine);
    const dir = this.aimRay();
    const hit = placementTarget(this.aimOrigin, dir, p, (x, z) => this.sim.height(x, z));
    const yaw = wrapYaw(p.yaw + this.placeTurn);
    const bodies = [...state.avatars.entries()].map(([sid, a]) => this.position(sid, a));
    const blocker = placementBlocker(this.sim, p, hit.x, hit.z, bodies);
    this.ghost.show(held, hit.x, placementBase(this.sim, hit.x, hit.z).y, hit.z, yaw, !blocker);
    this.gameHud.setTarget(blocker ? `${name} · ${blocker}` : `${name} · clique para posicionar · ${keyName("rotate")} gira`, Boolean(blocker));
    if (queued && !blocker && now >= this.nextPlaceAt) {
      this.actions.place(inventory.selected, hit.x, hit.z, yaw);
      this.nextPlaceAt = now + 400;
    }
    return true;
  }

  /**
   * Peça da grade na mira (até 8 m): nome e vida, "E abre" nas portas perto e "X desmonta" nas
   * próprias (ou de quem saiu), com quantas peças cairiam junto, pela mesma regra do servidor.
   */
  private pieceLabel(): { id: string; text: string } | undefined {
    const dir = this.aimRay();
    const origin = { x: this.aimOrigin.x, y: this.aimOrigin.y, z: this.aimOrigin.z };
    const pieces = piecesFrom(this.session.state.structures);
    const aim = aimPiece(origin, dir, pieces, BUILD.reach);
    this.aimedPiece = aim?.piece.id;
    if (!aim) return undefined;
    const s = this.session.state.structures.get(aim.piece.id);
    if (!s) return undefined;
    const def = itemDef(s.itemId);
    const parts = [`${def?.name ?? s.itemId} · ${s.hp}/${def?.placeable?.hp ?? s.hp}`];
    if (s.itemId === "parede-porta") parts.push(!this.doorInReach(s) ? "chegue perto para abrir" : s.open ? `${keyName("interact")} fecha` : `${keyName("interact")} abre`);
    if (s.owner === this.session.sessionId || !this.session.state.players.has(s.owner)) {
      const height = (x: number, z: number) => this.sim.height(x, z);
      const falls = cascade(pieces, new Set([aim.piece.id]), (p) => touchesGround(p.kind, p, height)).length;
      parts.push(falls > 0 ? `${keyName("dismantle")} desmonta (cai junto: ${falls})` : `${keyName("dismantle")} desmonta`);
    }
    return { id: aim.piece.id, text: parts.join(" · ") };
  }

  /** A peça na mira é uma porta ao alcance da mão (o E é dela). */
  private aimedDoorInReach(): boolean {
    const door = this.aimedPiece && this.session.state.structures.get(this.aimedPiece);
    return !!door && door.itemId === "parede-porta" && this.doorInReach(door);
  }

  /** A porta está ao alcance da mão (3 m do olho até ela, como o servidor confere)? */
  private doorInReach(door: StructureType): boolean {
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!mine) return false;
    const p = this.position(this.session.sessionId, mine);
    return pieceDistance("porta", door, { x: p.x, y: p.y + MOVE.eyeHeight, z: p.z }, door.open) <= BUILD.doorReach;
  }

  /** Forno, Panela ou baú mais perto, a até o raio de uso. */
  private nearestProcessStation(): { id: string; name: string } | undefined {
    return this.nearestInteractable("estacao");
  }

  /**
   * O que E alcança mais perto: estação ou baú (abre), Baú de Simpatia (compra, a até 3 m) ou
   * mascate (negocia, a até 4 m), com a dica da mira.
   */
  private nearestInteractable(only?: "estacao"): { kind: Interactable; id: string; name: string; label: string } | undefined {
    const E = keyName("interact");
    const state = this.session.state;
    const mine = state.avatars.get(this.session.sessionId);
    // Morto não interage com nada (só assiste).
    if (!mine || state.vitals.get(this.session.sessionId)?.dead) return undefined;
    const p = this.position(this.session.sessionId, mine);
    let best: { kind: Interactable; id: string; name: string; label: string; d: number } | undefined;
    // A Cruz pronta para reviver passa na frente da Trouxa ao lado dela (reviver é o urgente).
    let ready: { kind: Interactable; id: string; name: string; label: string; d: number } | undefined;
    state.structures.forEach((s, id) => {
      const d = Math.hypot(s.x - p.x, s.z - p.z);
      // A Cruz é sempre conferida (pode passar na frente mesmo mais longe).
      if (best && d >= best.d && s.itemId !== "cruz") return;
      const owner = state.players.get(s.owner)?.name;
      const name = s.itemId === "trouxa" && owner ? `${structureName("trouxa")} de ${owner}` : structureName(s.itemId);
      if (PROCESS_STATION_ITEMS[s.itemId] || storageSlotsOf(s.itemId) > 0) {
        if (d <= STATION_RADIUS + 0.5) best = { kind: "estacao", id, name, label: `${name} · ${E} abre`, d };
        return;
      }
      if (only) return;
      // Cruz de um aliado morto (F21): a espera, a noite, o progresso ou "segure E".
      if (s.itemId === "cruz" && d <= DEATH.reviveReach + 0.5) {
        const record = state.deaths?.get(s.owner);
        const who = owner ? `${structureName("cruz")} de ${owner}` : structureName("cruz");
        if (!record || s.owner === this.session.sessionId) return;
        const why =
          state.mode === "treta" ? "" : record.progress > 0 ? `revivendo ${Math.round(record.progress * 100)} %` : record.wait > 0 ? `revive em ${record.wait} s` : state.clock.night ? "só de dia" : `segure ${E} para reviver`;
        const candidate = { kind: "cruz" as const, id, name: who, label: why ? `${who} · ${why}` : who, d };
        if (!best || d < best.d) best = candidate;
        if ((why === `segure ${E} para reviver` || record.progress > 0) && (!ready || d < ready.d)) ready = candidate;
        return;
      }
      if (s.itemId === "carranca" && d <= DEATH.carrancaReach) {
        const dead = state.mode !== "treta" && [...(state.deaths?.keys() ?? [])].some((sid) => sid !== this.session.sessionId);
        best = { kind: "carranca", id, name, label: dead ? `${name} · ${E} revive um aliado (${carrancaPrice(state.clock.day, state.difficulty as Difficulty)} moedas)` : name, d };
        return;
      }
      // Pedra Fulminada (F24): só a dica (quebra no golpe de picareta de Prata ou melhor).
      if (s.itemId === "pedra-fulminada" && d <= 4) {
        best = { kind: "info", id, name, label: `${name} · ${s.hp} · picareta de Prata ou melhor`, d };
        return;
      }
      // Santuário (F23): desperta o Guardião da cor; o Muiraquitã vai ao Relicário.
      const color = shrineColor(s.itemId);
      if (color && d <= GUARDIAN.reach) {
        const label = state.relic?.get(color) ? `${name} · Gema no Relicário` : s.open ? `${name} · Guardião desperto` : `${name} · ${E} desperta o Guardião`;
        best = { kind: "pedra", id, name, label, d };
        return;
      }
      // Pedra de Encanto (F22): Invocação de chefe ou Desafio, uma vez por Pedra.
      if ((s.itemId === "pedra-chefe" || s.itemId === "pedra-encanto") && d <= ENCANTO.reach) {
        const label = s.open
          ? state.challenges?.has(id)
            ? `${name} · Desafio em curso`
            : `${name} · adormecido`
          : s.itemId === "pedra-chefe"
            ? state.mode === "treta"
              ? `${name} · adormecido na Arena`
              : `${name} · ${E} invoca um chefe`
            : `${name} · ${E} desafia`;
        best = { kind: "pedra", id, name, label, d };
        return;
      }
      const chest = simpatiaChestOf(s.itemId);
      if (chest && d <= SIMPATIA_CHEST_REACH) {
        const price = simpatiaPrice(chest.base, state.clock.day, state.difficulty as Difficulty);
        const label = s.open ? `${name} · aberto` : price > 0 ? `${name} · ${price} moedas · ${E} compra` : `${name} · grátis · ${E} abre`;
        best = { kind: "simpatia", id, name, label, d };
        return;
      }
      if (s.itemId === MASCATE_ITEM && d <= MASCATE_RULES.reach) {
        const m = state.mascates.get(id);
        const title = mascateName(m?.specialty ?? "");
        best = { kind: "mascate", id, name: title, label: m?.closed ? `${title} · fechado até o amanhecer` : `${title} · ${E} negocia`, d };
      }
    });
    // Aliado caído (F74): segurar E a até 2,5 m levanta (a mesma ação do reviver, pelo id dele).
    if (!only && state.mode !== "treta") {
      state.vitals.forEach((v, sid) => {
        if (!v.fallen || sid === this.session.sessionId) return;
        const a = state.avatars.get(sid);
        if (!a) return;
        const d = Math.hypot(a.x - p.x, a.z - p.z);
        if (d > FALL.liftReach + 0.5) return;
        const name = state.players.get(sid)?.name ?? "Aliado";
        const label = v.liftProgress > 0 ? `${name} · levantando ${Math.round(v.liftProgress * 100)} %` : `${name} caiu · segure ${E} para levantar (${v.fallLeft} s)`;
        const candidate = { kind: "cruz" as const, id: sid, name, label, d };
        if (!ready || d < ready.d) ready = candidate;
      });
    }
    const raft = state.raft;
    if (!only && raft?.parts?.size && (raft.stage === "reparo" || raft.stage === "pronta")) {
      const d = Math.hypot(raft.x - p.x, raft.z - p.z);
      if (d <= RAFT.reach + 1 && (!best || d < best.d)) best = { kind: "jangada", id: "jangada", name: "Barco", label: raft.stage === "pronta" ? `Barco · pronto: ${E} para zarpar` : `Barco · ${E} abre o reparo`, d };
    }
    const pick = ready ?? best;
    return pick && { kind: pick.kind, id: pick.id, name: pick.name, label: pick.label };
  }

  /** Barras de chefe, linha e anel do Desafio e o anúncio da noite de chefe (doc 01). */
  private updateBosses(): void {
    const state = this.session.state;
    const bars = bossBars(state.bosses?.entries() ?? [], (id) => state.creatures.get(id));
    // Cambaleou (quebra de postura ou interrompido, F69): a conjuração cai.
    for (const b of bars) {
      const c = state.creatures.get(b.id);
      if (c && creatureState(c.state) === "cambaleando") this.bossBars.stopCast(b.id);
    }
    const challenge = [...(state.challenges?.values() ?? [])][0];
    this.bossBars.update(bars, challenge ? challengeText(challenge) : undefined);
    this.arenas.sync(state.challenges?.entries() ?? []);
    this.relic.update(relicSlots((color) => state.relic?.get(color) === true));
    this.updateRaft();
    // Boitatá trocou de fase: aviso.
    for (const [id, b] of state.bosses ?? []) {
      const before = this.bossPhases.get(id);
      if (b.source === "final" && before !== undefined && b.phase > before) {
        toast(b.phase >= 3 ? "O Dragão ruge: chuva de brasas!" : "O Dragão se enfurece: cuidado com o fogo no convés!");
        const c = state.creatures.get(id);
        if (c) this.sound.play("rugido-fogo", { x: c.x, y: c.y + 3, z: c.z, priority: PRIORITY.critico });
      }
      this.bossPhases.set(id, b.phase);
    }
    if (state.bossNight && !this.bossNightSeen) toast("Hoje à noite: chefe. Junte o grupo antes do pôr do sol.");
    this.bossNightSeen = state.bossNight;
  }

  /** Jangada: painel de reparo, estado (contagem, travessia), anel de fogo e o convés firme na Boca. */
  private updateRaft(): void {
    const state = this.session.state;
    const raft = state.raft;
    if (!raft?.parts?.size) return;
    const inventory = state.inventories.get(this.session.sessionId);
    if (this.raftPanel.open) {
      const mine = state.avatars.get(this.session.sessionId);
      if (!mine || Math.hypot(raft.x - mine.x, raft.z - mine.z) > RAFT.reach + 3 || (raft.stage !== "reparo" && raft.stage !== "pronta")) this.raftPanel.show(false);
      const owned = (item: string) => [...(inventory?.slots ?? [])].reduce((n, s) => n + (s.itemId === item ? s.qty : 0), 0);
      this.raftPanel.update(raftRows(raft.parts, Math.max(1, state.players.size), state.difficulty as Difficulty, owned), raft.stage === "pronta");
    }
    const status = raftStatus(raft.stage, raft.countdown);
    this.raftHud.hidden = status === undefined;
    if (status !== undefined && this.raftHud.textContent !== status) this.raftHud.textContent = status;
    const anchored = raft.stage === "ancorada";
    this.fireRing.sync(anchored ? [["boca", { x: raft.x, z: raft.z, radius: RAFT.ring }]] : []);
    if (anchored && !this.deckAdded) {
      this.deckAdded = true;
      this.sim.addBox(RAFT_DECK_COLLIDER, anchoredDeckBox(raft.x, raft.z, this.sim.seaLevel));
    }
  }

  /** Abre a Carranca (lista de aliados mortos com o preço) ou, sem id, a própria volta (F54). */
  private openCarranca(id: string | undefined): void {
    // A Carranca do solo pode abrir com a pausa aberta (o mundo não para): a pausa sai, o painel é clicável.
    this.closePause(false);
    this.inventoryPanel.toggle(false);
    this.crafting.toggle(false);
    this.trade.show(undefined);
    if (id === undefined) this.carranca.showSolo();
    else this.carranca.show(id);
    this.releasePointer();
  }

  /**
   * Painel da Carranca: aliados mortos, preço e bolsa; fecha sozinho de longe. Sozinho e morto
   * (F54), fica aberto com a própria volta enquanto o servidor oferecer.
   */
  private updateCarranca(): void {
    const state = this.session.state;
    const me = this.session.sessionId;
    const record = state.deaths?.get(me);
    if (record?.solo) {
      if (!this.carranca.solo) this.openCarranca(undefined);
      // Sozinho, `wait` é o prazo para decidir.
      this.carranca.update([{ sessionId: me, name: "Você" }], carrancaPrice(state.clock.day, state.difficulty as Difficulty), state.inventories.get(me)?.coins ?? 0, record.wait);
      return;
    }
    if (this.carranca.solo) this.carranca.show(undefined);
    const id = this.carranca.id;
    if (id === undefined) return;
    const s = state.structures.get(id);
    const mine = state.avatars.get(this.session.sessionId);
    const inventory = state.inventories.get(this.session.sessionId);
    if (!s || !mine || !inventory || Math.hypot(s.x - mine.x, s.z - mine.z) > DEATH.carrancaReach + 1.5) {
      this.carranca.show(undefined);
      return;
    }
    const rows = [...(state.deaths?.keys() ?? [])].filter((sid) => sid !== this.session.sessionId).map((sid) => ({ sessionId: sid, name: state.players.get(sid)?.name ?? "Aliado" }));
    this.carranca.update(rows, carrancaPrice(state.clock.day, state.difficulty as Difficulty), inventory.coins);
  }

  /**
   * Peneira (doc 06): com ela na mão, segurando a ação e um redemoinho a até 3 m (em 3D, a mesma
   * regra do servidor), começa a captura (o redemoinho foge: é preciso correr atrás segurando).
   * Soltar, abrir um painel, trocar de item ou sair do alcance para; recusada ou interrompida
   * pelo servidor, tenta de novo enquanto segura.
   */
  private updatePeneira(): void {
    const state = this.session.state;
    const inventory = state.inventories.get(this.session.sessionId);
    const mine = state.avatars.get(this.session.sessionId);
    const p = mine && this.position(this.session.sessionId, mine);
    const winds = [...(state.whirlwinds?.entries() ?? [])].map(([id, w]) => ({ id, d: p ? Math.hypot(w.x - p.x, p.y - this.sim.height(w.x, w.z), w.z - p.z) : Infinity }));
    const step = peneiraStep({
      on: this.peneiraOn,
      holding: inventory?.slots[inventory.selected]?.itemId === "peneira",
      down: this.input.primaryDown(),
      panel: this.panelOpen,
      dead: state.vitals.get(this.session.sessionId)?.dead === true,
      now: performance.now(),
      retryAt: this.peneiraRetryAt,
      winds,
    });
    this.peneiraOn = step.on;
    this.peneiraRetryAt = step.retryAt;
    if (step.send) this.session.room.send(MSG.peneira, step.send);
    if (step.consumed) this.attackQueued = false;
  }

  /** Travessura do Saci: aviso para quem levou (e para o outro da troca), com o brilho antes da troca. */
  private onTravessura(e: TravessuraEvent): void {
    const me = this.session.sessionId;
    const name = (sid: string) => this.session.state.players.get(sid)?.name ?? "alguém";
    if (e.kind === "troca") {
      for (const sid of [e.sessionId, e.other]) {
        const a = this.session.state.avatars.get(sid);
        if (a) this.smoke.burst(a.x, a.y + 1, a.z, 8, 0.6);
      }
      if (e.sessionId === me || e.other === me) toast(`Travessura do Diabrete: você vai trocar de lugar com ${name(e.sessionId === me ? e.other : e.sessionId)}!`);
      return;
    }
    if (e.sessionId !== me) return;
    const text: Record<string, string> = {
      "item-cai": "Travessura do Diabrete: um item do seu cinto caiu ali perto!",
      "no-na-bota": `Travessura do Diabrete: nó na bota! Lento por ${SACI.slow.seconds} s.`,
      invertidos: `Travessura do Diabrete: controles invertidos por ${SACI.inverted} s!`,
      arremesso: "Travessura do Diabrete: lá vai você para o alto!",
      embaralhado: "Travessura do Diabrete: inventário embaralhado!",
      presente: "O Diabrete deixou um presente!",
    };
    toast(text[e.kind] ?? "Travessura do Diabrete!");
  }

  /** Cerco (Treta): parede de fogo, vinheta pela distância à borda e o aviso no HUD. */
  private drawCerco(time: number): void {
    const c = this.session.state.cerco;
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!c?.active || !mine) {
      this.cercoHud.hidden = true;
      this.cercoVignetteEl.style.opacity = "0";
      return;
    }
    const p = this.position(this.session.sessionId, mine);
    this.cerco.update(c, p.x, p.z, time);
    // Caído (espectador) não queima: sem vinheta nem aviso pela posição do corpo.
    const dead = this.session.state.vitals.get(this.session.sessionId)?.dead === true;
    const d = Math.hypot(p.x - c.x, p.z - c.z);
    this.cercoVignetteEl.style.opacity = dead ? "0" : String(cercoVignette(d, c.radius));
    const text = dead ? undefined : cercoText(c, d > c.radius);
    this.cercoHud.hidden = text === undefined;
    if (text !== undefined && this.cercoHud.textContent !== text) this.cercoHud.textContent = text;
  }

  /** Desenha a Jangada (criada quando o servidor a manda; só no Mutirão). */
  private drawRaft(): void {
    const raft = this.session.state.raft;
    if (!raft?.parts?.size) return;
    if (!this.raftLayer) {
      this.raftLayer = new RaftLayer(this.view.scene, this.view.atmosphere);
      this.raftGround = this.sim.height(raft.x, raft.z);
      this.raftStart = { x: raft.x, z: raft.z };
    }
    const relic = [...(this.session.state.relic?.values() ?? [])].filter(Boolean).length;
    const y = raft.stage === "ancorada" ? this.sim.seaLevel + RAFT.deckHeight - 0.6 : (this.raftGround ?? 0) - 0.2;
    this.raftLayer.update(raft, relic, y);
  }

  /**
   * Diagnóstico de desenho (F36): draw calls do próximo render por camada (filho direto da cena;
   * sem nome, o tipo do objeto e o do material). Usa `onBeforeRender`, que só roda para o que passou
   * pelo corte de visão, instalado só durante um render.
   */
  /** Só nos e2e (F53): pixels de capim vistos de cima em volta de (x, z); `raw`, sem as construções. */
  grassBlades(x: number, z: number, half: number, raw = false): number {
    return this.view.grassBlades(x, z, half, raw);
  }

  drawBreakdown(): Promise<Record<string, number>> {
    return new Promise((resolve) => {
      const counts: Record<string, number> = {};
      this.breakdown = { counts, done: () => resolve(counts) };
    });
  }

  private renderCounted(request: { counts: Record<string, number>; done: () => void }, dt: number): void {
    const restore: [Object3D, Object3D["onBeforeRender"]][] = [];
    for (const top of this.view.scene.children) {
      top.traverse((o) => {
        const drawable = o as Object3D & { isMesh?: boolean; isPoints?: boolean; isLine?: boolean; isSprite?: boolean; material?: { type?: string; name?: string } };
        if (!drawable.isMesh && !drawable.isPoints && !drawable.isLine && !drawable.isSprite) return;
        const label = top.name || `${top.type}/${o.type}:${drawable.material?.name || drawable.material?.type || "?"}`;
        const previous = o.onBeforeRender;
        restore.push([o, previous]);
        o.onBeforeRender = function (this: Object3D, ...args: Parameters<Object3D["onBeforeRender"]>) {
          request.counts[label] = (request.counts[label] ?? 0) + 1;
          previous.apply(this, args);
        };
      });
    }
    try {
      this.view.render(this.post, dt, this.frameStats);
    } finally {
      for (const [o, previous] of restore) o.onBeforeRender = previous;
      this.breakdown = undefined;
      request.done();
    }
  }

  /**
   * Os efeitos de uma habilidade usada (o `skillUsed` do servidor). `color` só no gancho de estresse,
   * que monta as quatro classes numa página: no jogo, a cor é sempre a da classe de quem usou.
   */
  private skillUsed(e: SkillEvent, color?: string): void {
    const by = this.session.state.players.get(e.by);
    const hex = color ?? playerColor(by);
    const now = performance.now();
    this.skillVfx.spawn(e, hex, (x, z) => this.sim.floorHeight(x, z), now, this.fxAlpha(e.by), nightBlend(this.clockReading));
    // Chamas na borda do Círculo da Fênix (F93), na cor da classe, enquanto ele dura.
    if (e.skill === "circulo-da-fenix" && e.radius > 0) this.flameEmitters.add(fenixSource(`fenix-${e.by}`, e, now, hex, this.fxAlpha(e.by)));
    // Onda de choque no pós (F94, só no Alto): o Salto do Falcão, o Bastião e o tiro do Rompe-Núcleo.
    const shock = skillShock(e);
    if (shock) this.post.shockwave(e.x, this.sim.floorHeight(e.x, e.z) + shock.lift, e.z, shock.strength * this.fxAlpha(e.by), now, shock.radius);
    // Flecha Sinalizadora (Gate C): o traço instantâneo de quem atirou até a área (o servidor aplica na hora).
    if (e.skill === "flecha-sinalizadora") {
      const from = this.avatars.get(e.by)?.view.root.position ?? this.session.state.avatars.get(e.by);
      const tracer = from ? flareTracer({ x: from.x, y: from.y + 1.4, z: from.z }, { x: e.x, y: this.sim.floorHeight(e.x, e.z) + 0.6, z: e.z }) : undefined;
      if (tracer) this.skillVfx.spawnPieces([tracer], hex, now, this.fxAlpha(e.by));
    }
    // Rompe-Núcleo (Gate C): o impacto no fim da linha estoura na direção do tiro.
    if (e.skill === "disparo-rompe-nucleo" && e.x2 !== undefined && e.z2 !== undefined) {
      const len = Math.hypot(e.x2 - e.x, e.z2 - e.z) || 1;
      const dir = { x: (e.x2 - e.x) / len, z: (e.z2 - e.z) / len };
      this.shards.burst({ x: e.x2, y: this.sim.floorHeight(e.x2, e.z2) + 1.1, z: e.z2, color: hex, n: 22, speed: 3, size: 0.1, life: 0.5, up: 0.2, dx: dir.x * 6, dz: dir.z * 6, alpha: this.fxAlpha(e.by) });
    }
    // Assinatura da classe de quem usou (F72), mais baixa nos aliados.
    this.sound.play(`classe-${classOf(by?.classId ?? "").id}`, { x: e.x, y: this.sim.floorHeight(e.x, e.z) + 1, z: e.z, gain: e.by === this.session.sessionId ? 0.8 : 0.5 * this.fxAlpha(e.by) });
  }

  /**
   * Só em desenvolvimento (o e2e `stress`, Fase 12 Gate A): os efeitos de habilidades em volta deste
   * jogador, pelo mesmo caminho do evento do servidor, cada uma na cor pedida. Só visual: nada vai ao
   * servidor, nenhuma regra corre. `dx`/`dz` são relativos ao jogador. Cada entrada vem de um ator
   * sintético próprio (`teste-<i>`): duas Fênix são duas fontes de chamas, como de duas pessoas; `own`
   * usa este jogador (o traço da Sinalizadora sai dele).
   */
  testSkills(list: readonly { skill: string; color: string; dx: number; dz: number; radius: number; ms: number; dx2?: number; dz2?: number; own?: boolean }[]): void {
    const me = this.session.state.avatars.get(this.session.sessionId);
    if (!me) return;
    for (const [i, t] of list.entries()) {
      const e: SkillEvent = { by: t.own ? this.session.sessionId : `teste-${i}`, skill: t.skill, x: me.x + t.dx, z: me.z + t.dz, radius: t.radius, ms: t.ms, ...(t.dx2 !== undefined && t.dz2 !== undefined && { x2: me.x + t.dx2, z2: me.z + t.dz2 }) };
      this.skillUsed(e, t.color);
    }
  }

  /**
   * Só em desenvolvimento (Fase 12, Gate C): danos fictícios em volta deste jogador, pelo mesmo caminho do
   * `DamageEvent` do servidor (números, impacto e as passivas), sem regra nenhuma. `fromMe` falso: o dano
   * é em mim (o Escudo Erguido segura o que chega); verdadeiro, é meu num alvo à frente.
   */
  testHits(list: readonly { dx: number; dz: number; fromMe: boolean; amount?: number; kind?: DamageKind; procs?: DamageProcs }[]): void {
    const me = this.session.state.avatars.get(this.session.sessionId);
    if (!me) return;
    for (const [i, t] of list.entries()) {
      const x = me.x + t.dx;
      const z = me.z + t.dz;
      const y = this.sim.floorHeight(x, z) + 1.9;
      this.onDamage({ target: t.fromMe ? `teste-alvo-${i}` : this.session.sessionId, attacker: t.fromMe ? this.session.sessionId : `teste-${i}`, x: t.fromMe ? x : me.x, y: t.fromMe ? y : me.y + 1.9, z: t.fromMe ? z : me.z, amount: t.amount ?? 12, absorbed: 0, crit: false, weak: false, saved: false, died: false, ...(t.kind && { kind: t.kind }), ...(t.procs && { procs: t.procs }) });
    }
  }

  /**
   * Só em desenvolvimento (Fase 14, G0): um golpe deste jogador num alvo fictício `ahead` m à frente da
   * câmera (e `side` m à direita), como o servidor confirmaria — o golpe começa agora (a fita, o braço) e o dano chega no instante
   * do impacto do ciclo (`MELEE.impactFraction`), pelo mesmo `onDamage`. Só visual: nada vai ao servidor.
   */
  testStrike(options: { ahead?: number; side?: number; crit?: boolean; died?: boolean; amount?: number; cycle?: number } = {}): void {
    const me = this.session.state.avatars.get(this.session.sessionId);
    if (!me) return;
    const now = performance.now();
    const cycle = options.cycle ?? swingCycle(meleeCycle(meleeWeapon(this.lastEquipped)), simpatiaEffects(() => 0));
    this.swings.start(this.session.sessionId, now, cycle);
    const ahead = options.ahead ?? 1.6;
    const side = options.side ?? 0;
    const x = me.x - Math.sin(this.input.yaw) * ahead + Math.cos(this.input.yaw) * side;
    const z = me.z - Math.cos(this.input.yaw) * ahead - Math.sin(this.input.yaw) * side;
    window.setTimeout(() => {
      this.onDamage({ target: "teste-alvo", attacker: this.session.sessionId, x, y: this.sim.floorHeight(x, z) + 1.9, z, amount: options.amount ?? 18, absorbed: 0, crit: options.crit ?? false, weak: false, saved: false, died: options.died ?? false, kind: "golpe" });
    }, cycle * 1000 * MELEE.impactFraction);
  }

  /**
   * Só em desenvolvimento (Fase 14, G0): as imagens dos primeiros quadros desenhados depois de cada atraso
   * (ms, a partir de agora), lidas do canvas logo depois do render (antes de o navegador o apresentar).
   */
  testGrab(delays: readonly number[]): Promise<string[]> {
    const now = performance.now();
    return Promise.all(delays.map((ms) => new Promise<string>((done) => this.grabs.push({ at: now + ms, done }))));
  }

  private takeGrabs(now: number): void {
    if (this.grabs.length === 0) return;
    const due = this.grabs.filter((g) => g.at <= now);
    if (due.length === 0) return;
    this.grabs = this.grabs.filter((g) => g.at > now);
    const url = this.renderer.domElement.toDataURL("image/jpeg", 0.92);
    for (const g of due) g.done(url);
  }

  /**
   * Só em desenvolvimento (Fase 12, Gate D2): esconde grupos de efeito para medir o tempo de GPU de cada
   * um no estresse — os materiais por nome (`peca-de-habilidade`, `estilhacos`, `fumaca`) e a energia no
   * chão (`energia`). Lista vazia mostra tudo de novo. Nada muda no jogo, só o desenho.
   */
  testHideFx(groups: readonly string[]): void {
    this.hiddenFx = new Set(groups);
    this.fxHiding = true;
  }

  /** Aplica `testHideFx` no quadro: materiais escondidos pelo nome; a última passada, sem nada, devolve todos. */
  private applyHiddenFx(): void {
    if (!this.fxHiding) return;
    const hidden = this.hiddenFx;
    this.view.scene.traverse((o) => {
      const material = (o as Mesh).material;
      if (!material || Array.isArray(material)) return;
      if (material.name === "peca-de-habilidade" || material.name === "estilhacos" || material.name === "fumaca") material.visible = !hidden.has(material.name);
    });
    if (hidden.has("energia")) this.view.atmosphere.uEnergyCount.value = 0;
    if (hidden.size === 0) this.fxHiding = false;
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): `n` assombrações sem dano em volta (F36). */
  testCreatures(n: number): void {
    this.session.room.send(MSG.testCreatures, { n });
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): o Cerco fechado neste raio em volta deste jogador. */
  testCerco(radius: number): void {
    this.session.room.send(MSG.testCerco, { radius });
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): Jangada pronta, convés, vida do Boitatá ou derrubá-lo. */
  testRaft(options: { complete?: boolean; boitataHp?: number; killBoitata?: boolean }): void {
    this.session.room.send(MSG.testRaft, options);
  }

  /**
   * Só nos e2e, no cliente (F46): força a entrada dos leitos de ambiente (distância da costa, terra
   * ou mar, bioma pelo nome interno, noite de 0 a 1, estágio da Jangada); `null` volta ao jogo.
   */
  testAmbience(options: { coast?: number; land?: boolean; biome?: string; night?: number; raftStage?: string; underwater?: boolean } | null): void {
    if (!options) {
      this.ambienceOverride = undefined;
      return;
    }
    const biome = options.biome === undefined ? -1 : (BIOME_NAMES as readonly string[]).indexOf(options.biome);
    this.ambienceOverride = {
      ...(options.coast !== undefined && { coast: options.coast }),
      ...(options.land !== undefined && { land: options.land }),
      ...(biome >= 0 && { biome }),
      ...(options.night !== undefined && { nightBlend: options.night }),
      ...(options.raftStage !== undefined && { raftStage: options.raftStage }),
      ...(options.underwater !== undefined && { underwater: options.underwater }),
    };
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): Mandinga, redemoinho perto ou travessura. */
  testSaci(options: { mandinga?: string; whirlwind?: number; prank?: string }): void {
    this.session.room.send(MSG.testSaci, options);
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): leva este jogador ao chão livre em (x, z) ou mais perto. */
  testGoto(x: number, z: number): void {
    this.session.room.send(MSG.testGoto, { x, z });
  }

  /** Só nos e2e (F55): o ponto de interesse do tipo mais perto do corpo, com o giro (a Gruta das capturas). */
  testPoi(type: string): { x: number; z: number; rot: number } | undefined {
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!mine) return undefined;
    const p = this.position(this.session.sessionId, mine);
    let best: { x: number; z: number; rot: number } | undefined;
    let bestD = Infinity;
    for (const poi of this.world.plan.pois) {
      const d = Math.hypot(poi.x - p.x, poi.z - p.z);
      if (poi.type === type && d < bestD) {
        bestD = d;
        best = { x: poi.x, z: poi.z, rot: poi.rot };
      }
    }
    return best;
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): um chefe a 12 m, na frente, com a vida dada. */
  testBoss(type: string, hp: number): void {
    this.session.room.send(MSG.testBoss, { type, hp });
  }

  /** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): dano no próprio corpo. */
  selfDamage(amount: number): void {
    this.session.room.send(MSG.testDamage, { amount });
  }

  /** Abre o comércio com o mascate (fecha inventário e fabricação). */
  private openTrade(id: string): void {
    this.inventoryPanel.toggle(false);
    this.crafting.toggle(false);
    this.trade.show(id);
    this.releasePointer();
  }

  /** Painel do mascate: ofertas, saldo e o que o jogador tem; fecha sozinho de longe. */
  private updateTrade(): void {
    const id = this.trade.id;
    if (id === undefined) return;
    const state = this.session.state;
    const m = state.mascates.get(id);
    const s = state.structures.get(id);
    const mine = state.avatars.get(this.session.sessionId);
    const inventory = state.inventories.get(this.session.sessionId);
    if (!m || !s || !mine || !inventory || Math.hypot(s.x - mine.x, s.z - mine.z) > MASCATE_RULES.reach + 1.5) {
      this.trade.show(undefined);
      return;
    }
    const owned = (itemId: string) => [...inventory.slots, inventory.quiver].reduce((n, x) => n + (x.itemId === itemId ? x.qty : 0), 0);
    this.trade.update(mascateName(m.specialty), m.closed, inventory.coins, tradeRows(m, inventory.coins, owned, this.session.state.mandinga));
  }

  /** Ficha da classe (F82): o perfil, os atributos e as estatísticas ao vivo do jogador local. */
  private updateSheet(): void {
    const state = this.session.state;
    const sid = this.session.sessionId;
    const me = state.players.get(sid);
    const vitals = state.vitals.get(sid);
    const inventory = state.inventories.get(sid);
    const body = (this.reconciler?.state as AvatarType | undefined) ?? state.avatars.get(sid);
    this.sheet.update(
      sheetView({
        name: me?.name ?? "",
        classId: me?.classId ?? "",
        slot: me?.slot ?? 0,
        color: playerColor(me),
        mode: state.mode,
        vitals,
        stamina: body && { value: body.stamina, max: body.staminaMax },
        simpatias: inventory?.simpatias.entries() ?? [],
        armor: inventory?.armor.map((a) => a.itemId) ?? [],
        oaths: me?.oaths ?? "",
        ultCharge: vitals?.ultCharge ?? 0,
        activeLeft: Math.max(0, this.skillReadyAt - performance.now()) / 1000,
        keys: { ativa: keyName("skill"), ultimate: keyName("ultimate") },
        stats: state.stats.get(sid),
      }),
    );
  }

  /** Inventário, fabricação, mascate, pausa… abertos: a mira e a ação principal ficam de fora. */
  private get panelOpen(): boolean {
    return this.otherPanelOpen || this.pause.open;
  }

  /** Algum painel aberto além da pausa (F80). */
  private get otherPanelOpen(): boolean {
    return this.selfClosingOpen || this.plainPanelOpen || this.carranca.open;
  }

  /** Painéis que fecham no próprio Esc (mapa, chat, Opções, ficha): o Esc é deles. */
  private get selfClosingOpen(): boolean {
    return this.islandMap.open || this.chat.open || this.settingsPanel.open || this.sheet.open;
  }

  /** Painéis sem Esc próprio (revisão da F80): a cena os fecha no Esc. A Carranca do solo fica (é a decisão da volta). */
  private get plainPanelOpen(): boolean {
    return this.inventoryPanel.open || this.crafting.open || this.trade.open || this.raftPanel.open || this.abilities.open || (this.carranca.open && !this.carranca.solo);
  }

  /** Os painéis com Esc próprio abertos com o foco fora deles, como o fechar de cada um (Opções, mapa, ficha). */
  private unfocusedSelfPanels(target: Node): (() => void)[] {
    const out: (() => void)[] = [];
    if (this.settingsPanel.open && !this.settingsPanel.element.contains(target)) out.push(() => this.settingsPanel.hide());
    if (this.islandMap.open && !this.islandMap.element.contains(target)) out.push(() => this.islandMap.toggle(false));
    if (this.sheet.open && !this.sheet.element.contains(target)) out.push(() => this.sheet.toggle(false));
    return out;
  }

  private closePlainPanels(): void {
    this.inventoryPanel.toggle(false);
    this.crafting.toggle(false);
    this.trade.show(undefined);
    this.raftPanel.show(false);
    this.abilities.toggle(false);
    if (!this.carranca.solo) this.carranca.show(undefined);
    this.sound.play("ui-fechar", { bus: "ui" });
  }

  /**
   * Botão do canto do HUD (F81): o mesmo que tocar a tecla da ação (a primeira configurada), pelo mesmo
   * caminho do teclado no próximo quadro (sons, painéis que fecham, o ponteiro que solta).
   */
  private pressAction(action: "settings" | "inventory" | "map"): void {
    // Com o teclado de um painel (as Opções, a pausa), só as Opções valem, como pela tecla (revisão da F81).
    if (this.input.paused && action !== "settings") return;
    const code = this.input.bindings[action][0];
    if (!code) return;
    // O clique que fecha o último painel trava o ponteiro, como a tecla (F99).
    if (!document.pointerLockElement && relockOnKey(action, this.openPanels(), this.relockContext())) this.lockPointer();
    this.input.key(code, true);
    this.input.key(code, false);
  }

  /** Botão Pausa do HUD (F81): fecha a pausa; com painéis abertos, fecha-os antes (a ordem do Esc), e abre. */
  private togglePauseButton(): void {
    if (this.pause.open) return this.closePause(false);
    if (this.otherPanelOpen) {
      for (const close of this.unfocusedSelfPanels(document.body)) close();
      this.closePlainPanels();
      if (this.chat.open) return;
    }
    this.openPause();
  }

  /** Abre a pausa (F80): o teclado é dela, o mundo segue (o subtítulo avisa). */
  private openPause(): void {
    this.input.clear();
    this.releasePointer();
    const others = [...this.session.state.players.values()].filter((p) => p.sessionId !== this.session.sessionId).length;
    this.pause.show(pauseNote(others, this.session.state.mode === "treta"));
    this.sound.play("ui-abrir", { bus: "ui" });
  }

  /** Fecha a pausa (e as Opções abertas por ela); por clique, trava o ponteiro de novo (o Esc não pode). */
  private closePause(lock: boolean): void {
    if (!this.pause.open) return;
    this.pause.hide();
    this.settingsPanel.hide();
    this.sound.play("ui-fechar", { bus: "ui" });
    this.renderer.domElement.focus?.();
    // Com um painel aberto por baixo (a Carranca do solo), o mouse é dele.
    if (lock && !this.otherPanelOpen) this.lockPointer();
  }

  /** Solta o ponteiro a pedido da cena: a soltura não abre a pausa (F99). */
  private releasePointer(): void {
    if (!document.pointerLockElement) return;
    this.plannedUnlock = true;
    document.exitPointerLock();
  }

  /**
   * Trava o ponteiro; só vale dentro de um gesto (clique, keydown). No headless, no cooldown do Chrome depois
   * do Esc e onde o navegador recusa, falha: o aviso "Clique para voltar ao jogo" fica, e o clique trava.
   */
  private lockPointer(): void {
    this.lockRequestedAt = performance.now();
    Promise.resolve(this.renderer.domElement.requestPointerLock?.()).catch(() => {});
  }

  /** A ação de painel da tecla (F99): o Enter dentro do chat envia e fecha; digitando, nenhuma outra. */
  private relockKey(e: KeyboardEvent): RelockKey | undefined {
    if (this.chat.open && (e.code === "Enter" || e.code === "NumpadEnter") && this.chat.element.contains(e.target as Node)) return "chat-send";
    if (typingIn(e.target)) return undefined;
    const actions = ["inventory", "crafting", "map", "abilities", "sheet", "settings", "interact"] as const;
    return actions.find((a) => this.input.bindings[a].includes(e.code));
  }

  /** O que mais decide a tecla, para `relockOnKey` (revisão da F99). */
  private relockContext(): RelockContext {
    return { doorInReach: this.aimedDoorInReach(), capturingKey: this.settingsPanel.capturingKey };
  }

  /** Os painéis abertos agora, para `relockOnKey`. */
  private openPanels(): OpenPanels {
    return {
      inventory: this.inventoryPanel.open,
      crafting: this.crafting.open,
      map: this.islandMap.open,
      abilities: this.abilities.open,
      sheet: this.sheet.open,
      settings: this.settingsPanel.open,
      chat: this.chat.open,
      trade: this.trade.open,
      raft: this.raftPanel.open,
      carranca: this.carranca.open && !this.carranca.solo,
      carrancaSolo: this.carranca.open && this.carranca.solo,
      pause: this.pause.open,
    };
  }

  /** Opções pela pausa: abrem ao lado, com o botão aceso; fechar volta para a pausa. */
  private openSettingsFromPause(): void {
    if (this.settingsPanel.open) return;
    this.pause.setSettingsOpen(true);
    this.settingsPanel.show();
  }

  /** Enter abre o chat, M o mapa (centrado em você), G ou o botão do meio marcam um ping. */
  private handleSocialInput(): void {
    if (this.input.consumePressed("chat") && !this.chat.open) {
      // As teclas seguradas soltam e o ponteiro destrava: digitar não anda, não gira a câmera nem golpeia.
      this.input.clear();
      this.releasePointer();
      this.chat.show();
    }
    if (this.input.consumePressed("map")) {
      const mine = this.session.state.avatars.get(this.session.sessionId);
      this.islandMap.toggle(undefined, mine && this.position(this.session.sessionId, mine));
      // Abriu: desenha já, sem esperar a próxima rodada do minimapa (revisão da F81).
      if (this.islandMap.open) this.nextMapAt = 0;
      if (this.islandMap.open) this.releasePointer();
    }
    if (this.input.consumePressed("abilities")) {
      this.abilities.toggle();
      this.sound.play(this.abilities.open ? "ui-abrir" : "ui-fechar", { bus: "ui" });
      if (this.abilities.open) this.releasePointer();
    }
    if (this.input.consumePressed("sheet")) {
      this.sheet.toggle();
      this.sound.play(this.sheet.open ? "ui-abrir" : "ui-fechar", { bus: "ui" });
      if (this.sheet.open) {
        this.releasePointer();
        this.updateSheet();
      }
    }
    if (this.input.consumePressed("ping")) this.sendPing();
    if (this.input.consumePressed("skill")) this.useSkill("ativa");
    if (this.input.consumePressed("ultimate")) this.useSkill("ultimate");
    // Opções em jogo (P): o jogo não pausa; o ponteiro destrava enquanto a tela está aberta.
    if (this.input.consumePressed("settings")) {
      if (this.pause.open && !this.settingsPanel.open) this.openSettingsFromPause();
      else this.settingsPanel.toggle();
      if (this.settingsPanel.open) {
        this.input.clear();
        this.releasePointer();
      }
    }
    // Aberta (por P, pela pausa ou pelo menu), a tela de Opções fica com o teclado; a pausa também (F80).
    this.input.paused = this.settingsPanel.open || this.pause.open;
  }

  /** Aplica as Opções (ao nascer a cena e a cada mudança). */
  private applySettings(s: Settings): void {
    // Entrou no Automático agora: mede de novo, do zero, na base Média.
    if (s.graphics.preset === "automatico" && this.prefs.graphics.preset !== "automatico") {
      this.autoMeasured = false;
      this.autoMeter.reset();
      settings.setAuto(undefined);
    }
    this.prefs = s;
    this.input.bindings = s.bindings;
    this.input.sensitivity = DEFAULT_SENSITIVITY * s.sensitivity;
    this.input.invertX = s.invertX;
    this.input.invertY = s.invertY;
    // "Reduzir lampejos" também corta o tremor à metade (F55).
    this.shake.scale = (s.shake / 100) * (s.reduceFlashes ? SHAKE.reduced : 1);
    this.captionsHud.enabled = s.captions;
    this.telegraphs.reduceFlash = s.reduceFlashes;
    // Chamas e magia (F93): sem tremeluzir nem cintilar, e sem passar de 1.
    this.smoke.reduceFlashes = s.reduceFlashes;
    // Peças das habilidades e das passivas (Fase 12): o brilho das arestas não passa de 1 (sem o pico do bloom).
    this.skillVfx.reduceFlashes = s.reduceFlashes;
    // Dissolve das famílias (F95): o pontilhado sem a borda acesa.
    this.dissolves.reduceFlashes = s.reduceFlashes;
    this.telegraphs.colorblind = s.colorblind;
    this.pings.colorblind = s.colorblind;
    this.perfOn = s.showPerf ?? import.meta.env.DEV;
    this.perfHud.visible = this.perfOn;
    // O acento da classe (UI e fantasma) passa pelo modo daltônico: recalcula no próximo quadro.
    this.accentClass = "";
    refreshKeyHints();
  }

  /** Automático (doc 05): mede 10 s de quadros ativos e fixa o preset desta partida. */
  private measureAuto(dtMs: number): void {
    if (this.autoMeasured || this.prefs.graphics.preset !== "automatico") return;
    const chosen = this.autoMeter.push(dtMs, !document.hidden && !this.settingsPanel.open);
    if (!chosen) return;
    this.autoMeasured = true;
    settings.setAuto(chosen);
  }

  /** Ping pelo que a mira atinge até 300 m (o servidor confere distância, vista e cadência). */
  /**
   * Habilidade (F65): o ponto sob a cruz (ou 12 m à frente, sem nada na mira). O servidor confere tudo
   * (recarga, alcance, vida) e responde; a barra só adianta o "ainda recarregando".
   */
  private useSkill(slot: "ativa" | "ultimate"): void {
    // A Investida (F66) anda no comando de movimento (predita); não há pedido à parte.
    if (slot === "ativa" && this.myActive() === "investida") return;
    // A ultimate pede a carga cheia (F67); o Salto do Falcão anda no comando de movimento.
    if (slot === "ultimate") {
      if ((this.session.state.vitals.get(this.session.sessionId)?.ultCharge ?? 0) < ULT_CHARGE.max) {
        this.skillBar.refuse("ultimate");
        return;
      }
      if (this.myUltimate() === "salto-do-falcao") return;
    }
    if (slot === "ativa" && performance.now() < this.skillReadyAt) {
      this.skillBar.refuse("ativa");
      return;
    }
    const me = this.session.state.avatars.get(this.session.sessionId);
    if (!me) return;
    const dir = this.aimRay();
    const x = this.aim.hit ? this.aim.x : me.x + dir.x * 12;
    const z = this.aim.hit ? this.aim.z : me.z + dir.z * 12;
    this.session.room.send(MSG.skill, { slot, x, z });
  }

  /** A ultimate da minha classe (F67). */
  private myUltimate(): string {
    const me = this.session.state.players.get(this.session.sessionId);
    return classSkills(classOf(me?.classId ?? "")).ultimate.id;
  }

  /** A ativa da minha classe (F66). */
  private myActive(): string {
    const me = this.session.state.players.get(this.session.sessionId);
    return classSkills(classOf(me?.classId ?? "")).ativa.id;
  }

  private sendPing(): void {
    const dir = this.aimRay();
    const state = this.session.state;
    const target = inferPing(
      { origin: this.aimOrigin, dir },
      (x, z) => this.sim.height(x, z),
      this.creatureFrame,
      (x, z, r) => [...state.drops.values()].some((d) => Math.hypot(d.x - x, d.z - z) <= r),
      (x, z, r) => this.targeting.near(x, z, r, (id) => this.view.resources.isRemoved(id)),
    );
    if (!target) {
      toast("Nada à vista para marcar.");
      return;
    }
    this.session.room.send(MSG.mark, { x: target.x, y: target.y, z: target.z, kind: target.kind, ...(target.target ? { target: target.target } : {}) });
  }

  /** Fala ou comando do chat; os comandos de consulta respondem aqui mesmo (doc 06). */
  private onChatSubmit(text: string): void {
    const state = this.session.state;
    const local = (line: string) => this.chat.push({ kind: "local", text: line, at: performance.now() });
    const input = parseChatInput(text);
    if (input.type === "vazio") return;
    if (input.type === "fala") {
      this.session.room.send(MSG.chat, { text: input.text });
      return;
    }
    if (input.type === "desconhecido") {
      local(`Comando desconhecido: ${input.word}. Veja /ajuda.`);
      return;
    }
    switch (input.name) {
      case "semente": {
        local(`Seed da ilha: ${state.seedCode}`);
        void navigator.clipboard?.writeText(state.seedCode).then(
          () => local("Seed copiada."),
          () => undefined,
        );
        return;
      }
      case "latencia":
        local(`Latência: ${Math.round(this.session.serverRtt)} ms (medida pelo servidor).`);
        return;
      case "ajuda":
        for (const line of helpLines()) local(line);
        return;
      case "mandinga":
        local(mandingaText(state.mandinga));
        return;
      case "silenciar": {
        const want = input.arg.replace(/^@/, "").trim().toLowerCase();
        const hit = [...state.players.values()].find((p) => p.sessionId !== this.session.sessionId && p.name.toLowerCase() === want);
        if (!hit) local(want ? `Ninguém chamado "${input.arg}" na sala.` : "Use /silenciar <apelido>.");
        else local(this.chat.log.toggleMute(hit.sessionId) ? `Você silenciou ${hit.name} (só para você).` : `Você voltou a ouvir ${hit.name}.`);
        return;
      }
      default:
        this.session.room.send(MSG.command, { name: input.name, arg: input.arg.slice(0, 64) });
    }
  }

  /** Chat, pings, linha da votação, névoa (2 vezes por segundo) e o mapa aberto (10 vezes por segundo). */
  private updateSocial(now: number): void {
    const state = this.session.state;
    this.chat.update(now);
    this.pings.update(this.view.camera, window.innerWidth, window.innerHeight, now);
    const vote = voteText(state.vote, (sid) => state.players.get(sid)?.name ?? "alguém", this.session.sessionId);
    this.voteHud.hidden = vote === undefined;
    if (vote !== undefined && this.voteHud.textContent !== vote) this.voteHud.textContent = vote;
    const treta = state.mode === "treta";
    if (now >= this.nextFogAt) {
      this.nextFogAt = now + 500;
      // Mutirão: a névoa se abre em volta de todos os aliados; Treta: só em volta de você.
      state.avatars.forEach((a, sid) => {
        if (!treta || sid === this.session.sessionId) this.fog.reveal(a.x, a.z);
      });
    }
    // O mapa aberto e o minimapa (F81, sempre à vista) dividem os marcadores, a 10 Hz.
    if (now < this.nextMapAt) return;
    this.nextMapAt = now + MINIMAP_REDRAW_MS;
    const mine = state.avatars.get(this.session.sessionId);
    const self = mine && { ...this.position(this.session.sessionId, mine), yaw: this.input.yaw };
    const allies = treta
      ? []
      : [...state.avatars.entries()]
          .filter(([sid]) => sid !== this.session.sessionId)
          .map(([sid, a]) => {
            const p = this.position(sid, a);
            const info = state.players.get(sid);
            return { name: info?.name ?? "?", color: playerColor(info), n: (info?.slot ?? 0) + 1, x: p.x, z: p.z, dead: state.vitals.get(sid)?.dead === true };
          });
    const raft = state.raft;
    const { markers, circles } = mapMarkers({
      self,
      allies,
      pings: this.pings.list(now).map((p) => ({ x: p.event.x, z: p.event.z, kind: p.event.kind, name: p.name, color: gameColor(p.hex), id: p.event.id })),
      pois: this.world.plan.pois,
      revealed: (x, z) => this.fog.revealed(x, z),
      raftKnown: state.objectives?.raftKnown === true || raft?.found === true,
      guardiansKnown: state.objectives?.guardiansKnown === true,
      raft: raft?.parts?.size ? { x: raft.x, z: raft.z } : undefined,
      crosses: [...state.structures.values()].filter((st) => st.itemId === "cruz").map((st) => ({ x: st.x, z: st.z, name: state.players.get(st.owner)?.name ?? "alguém" })),
      whirlwinds: [...(state.whirlwinds?.entries() ?? [])].map(([id, w]) => ({ x: w.x, z: w.z, id })),
      bosses: [...(state.bosses?.keys() ?? [])].flatMap((id) => {
        const c = state.creatures.get(id);
        return c && c.hp > 0 ? [{ x: c.x, z: c.z, name: CREATURE_BY_ID.get(c.type)?.name ?? "Chefe", id }] : [];
      }),
      cerco: state.cerco?.active ? state.cerco : undefined,
      paint: gameColor,
    });
    if (this.islandMap.open) this.islandMap.draw(this.fog, markers, circles);
    // O minimapa centra em você; morto (assistindo), na câmera, como a bússola (revisão da F81).
    const dead = state.vitals.get(this.session.sessionId)?.dead === true;
    const center = (!dead && self) || this.view.camera.position;
    this.navHud.setMarkers(center.x, center.z, this.fog, markers, circles);
  }

  /**
   * Áudio do quadro (F29): ouvinte na câmera, passa-baixa por Vida e água, passos, corpo, ambiente,
   * Cerco, redemoinhos, fogos, chefes, assombrações e a trilha pelo estado da partida.
   */
  private updateSound(now: number): void {
    const state = this.session.state;
    const me = this.session.sessionId;
    const camera = this.view.camera;
    const dir = camera.getWorldDirection(this.aimDir);
    const mine = state.avatars.get(me);
    const v = state.vitals.get(me);
    const body = (this.reconciler?.state as AvatarType | undefined) ?? mine;
    // Patuá quebrou e comida mastigada: bordas do estado.
    if (v) {
      if (v.patuaBroken && !this.patuaWasBroken) this.sound.play("patua-quebra", { priority: PRIORITY.importante });
      this.patuaWasBroken = v.patuaBroken;
      // Dano levado: baque se a Vida caiu 3 % ou mais (a queima fraca do Cerco não martela); Patuá
      // absorvendo: brilho vítreo; caiu: tom grave longo.
      const seen = this.hpSeen;
      if (seen && now - this.hurtAt > 250) {
        if (seen.hp - v.hp >= v.hpMax * 0.03) {
          this.hurtAt = now;
          this.sound.play("dano", { priority: PRIORITY.importante });
        } else if (v.hp >= seen.hp && v.patua < seen.patua - 0.5) {
          this.hurtAt = now;
          this.sound.play("patua", { priority: PRIORITY.importante });
        }
      }
      if (seen && v.dead && !seen.dead) this.sound.play("morte", { bus: "ui", priority: PRIORITY.critico });
      this.hpSeen = { hp: v.hp, patua: v.patua, dead: v.dead };
    }
    if (mine) {
      if (this.eatSeen !== undefined && mine.eatCount !== this.eatSeen) this.sound.play("comer", { gain: 0.8 });
      this.eatSeen = mine.eatCount;
    }
    for (const [id, f] of this.hazardFires) if (now >= f.until) this.hazardFires.delete(id);
    const fires: { id: string; x: number; y: number; z: number }[] = [...this.hazardFires.entries()].map(([id, f]) => ({ id: `perigo-${id}`, x: f.x, y: f.y, z: f.z }));
    state.structures.forEach((s, id) => {
      if (s.lit) fires.push({ id, x: s.x, y: s.y + 0.5, z: s.z });
    });
    const p = mine && this.position(me, mine);
    const ground = p ? groundAt(this.sim, p.x, p.z, p.y) : undefined;
    const plan = this.world.plan;
    const cell = (v: number) => Math.min(PLAN_SIDE - 1, Math.max(0, Math.round((v + WORLD_HALF) / PLAN_SPACING)));
    const self =
      p && body
        ? {
            id: me,
            x: p.x,
            y: p.y,
            z: p.z,
            speed: horizontalSpeed(body),
            grounded: body.grounded,
            swimming: body.swimming,
            // Sobre construção, madeira; na laje da Caverna (teto suspenso), pedra.
            onBox: ground?.onBox === true,
            onRock: ground?.onCeiling === true,
            vy: body.vy,
          }
        : undefined;
    const others: { id: string; x: number; y: number; z: number; speed: number; grounded: boolean; swimming: boolean }[] = [];
    state.avatars.forEach((a, sid) => {
      if (sid === me || state.vitals.get(sid)?.dead) return;
      const q = this.position(sid, a);
      if (Math.hypot(q.x - camera.position.x, q.z - camera.position.z) > 40) return;
      others.push({ id: sid, x: q.x, y: q.y, z: q.z, speed: horizontalSpeed(a), grounded: a.grounded, swimming: a.swimming });
    });
    const bosses: { id: string; x: number; y: number; z: number; final: boolean }[] = [];
    state.bosses?.forEach((b, id) => {
      const c = state.creatures.get(id);
      if (c && c.hp > 0) bosses.push({ id, x: c.x, y: c.y, z: c.z, final: b.source === "final" });
    });
    const clock = this.clockReading;
    this.sound.update({
      now,
      listener: { x: camera.position.x, y: camera.position.y, z: camera.position.z, fx: dir.x, fy: dir.y, fz: dir.z, yaw: this.input.yaw },
      self,
      others,
      // Filtro de embaixo d'água pela cabeça (F55), não pela câmera atrás do ombro.
      underwater: body ? body.submerged === true : camera.position.y < this.sim.seaLevel,
      hp: v && v.hpMax > 0 ? v.hp / v.hpMax : 1,
      spectating: v?.dead === true,
      lowHunger: !!v && v.hunger > 0 && v.hunger < VITALS.lowHunger,
      exhausted: body?.exhausted === true && body.submerged !== true,
      drowning: v?.drowning === true,
      criticalHp: !!v && !v.dead && v.hp < v.hpMax * 0.2,
      night: clock?.night === true,
      dusk: clock?.dusk === true,
      land: this.sim.height(camera.position.x, camera.position.z) >= this.sim.seaLevel,
      nightBlend: nightBlend(clock),
      ambience: this.ambienceOverride,
      bosses,
      challenge: (state.challenges?.size ?? 0) > 0,
      raftStage: state.raft?.parts?.size ? state.raft.stage : undefined,
      treta: state.mode === "treta",
      cerco: state.cerco?.active ? state.cerco : undefined,
      whirlwinds: [...(state.whirlwinds?.entries() ?? [])].map(([id, w]) => ({ id, x: w.x, z: w.z })),
      creatures: this.creatureFrame,
      fires,
      biome: (x, z) => plan.biome[cell(z) * PLAN_SIDE + cell(x)] ?? BIOME.mar,
      coast: (x, z) => samplePlanGrid(this.sim.height(x, z) >= this.sim.seaLevel ? plan.coastDistance : plan.landDistance, x, z),
      seed: state.seed,
    });
    this.captionsHud.update(this.sound.captions);
  }

  /** Grava juntando com o que outra aba possa ter gravado (sem apagar progresso nem religar). */
  private saveTutorial(): void {
    const merged = mergeTutorial(this.tutorial.toJSON(), loadTutorial());
    this.tutorial.adopt(merged);
    saveTutorial(merged);
  }

  /**
   * Tutorial (doc 05 e 06): confere o passo atual 4 vezes por segundo pelos sinais da partida, guarda
   * o que foi feito e aponta a seta para o alvo mais perto (presa à borda fora da vista).
   */
  private updateTutorial(now: number): void {
    if (now >= this.nextTutorialAt) {
      this.nextTutorialAt = now + 250;
      const state = this.session.state;
      const s = this.tutorialSignals;
      if (this.inventoryPanel.open) s.inventoryOpened = true;
      const inventory = state.inventories.get(this.session.sessionId);
      s.hasAxe ||= !!inventory && [...inventory.slots].some((slot) => slot.qty > 0 && toolOf(slot.itemId).kind === "machado");
      const mine = state.avatars.get(this.session.sessionId);
      if (mine) {
        this.eatBase ??= mine.eatCount;
        if (mine.eatCount !== this.eatBase) s.ate = true;
      }
      if (this.tutorial.update(s, now)) {
        this.saveTutorial();
        this.sound.play("tarefa", { bus: "ui" });
      }
      const view = this.tutorial.view(now, tutorialKeys(this.input.bindings));
      this.tutorialCard.update(view);
      this.tutorialTarget = view?.target && mine ? this.findTutorialTarget(view.target, mine.x, mine.z) : undefined;
    }
    const t = this.tutorialTarget;
    this.tutorialCard.point(t && screenPoint({ x: t.x, y: t.y + 2.2, z: t.z }, this.view.camera, window.innerWidth, window.innerHeight));
  }

  /** Alvo da seta: a galhada ou moita mais perto, uma árvore leve, ou a própria Bancada (sempre até 120 m). */
  private findTutorialTarget(target: TutorialTarget, x: number, z: number): { x: number; y: number; z: number } | undefined {
    const removed = (id: number) => this.view.resources.isRemoved(id);
    if (target === "galhada") return this.targeting.nearest(x, z, 120, (t) => RESOURCE_TYPES[t]?.id === "galhada" || RESOURCE_TYPES[t]?.id === "moita", removed);
    if (target === "arvore") return this.targeting.nearest(x, z, 120, (t) => RESOURCE_TYPES[t]?.tool === "machado" && (RESOURCE_TYPES[t]?.hardness ?? 9) <= 1, removed);
    let best: { x: number; y: number; z: number } | undefined;
    let bestD = 120;
    this.session.state.structures.forEach((st) => {
      if (st.itemId !== "bancada" || st.owner !== this.session.sessionId) return;
      const d = Math.hypot(st.x - x, st.z - z);
      if (d < bestD) {
        bestD = d;
        best = { x: st.x, y: st.y, z: st.z };
      }
    });
    return best;
  }

  /** Mapa num inventário revelou um objetivo ao grupo (doc 03): avisa uma vez. */
  private announceObjectives(): void {
    const o = this.session.state.objectives;
    if (!o) return;
    if (o.raftKnown && !this.objectivesSeen.raft) {
      this.objectivesSeen.raft = true;
      toast("O Mapa do Naufrágio revelou o Naufrágio para o grupo.");
    }
    if (o.guardiansKnown && !this.objectivesSeen.guardians) {
      this.objectivesSeen.guardians = true;
      toast("O Mapa das Gemas revelou os santuários dos Guardiões para o grupo.");
    }
  }

  /** E abre (ou fecha) o Forno/Panela mais perto junto com o inventário; fechar o painel fecha a estação. */
  private toggleStation(): void {
    if (this.openStation !== undefined) {
      this.closeStation();
      this.inventoryPanel.toggle(false);
      return;
    }
    const near = this.nearestProcessStation();
    if (!near) return;
    this.openStation = near.id;
    this.session.room.send(MSG.stationOpen, { id: near.id });
    this.chestLid(near.id, true);
    this.crafting.toggle(false);
    this.inventoryPanel.toggle(true);
    this.releasePointer();
    this.inventoryPanel.update(this.session.state.inventories.get(this.session.sessionId));
  }

  private closeStation(): void {
    if (this.openStation === undefined) return;
    this.chestLid(this.openStation, false);
    this.openStation = undefined;
    this.session.room.send(MSG.stationClose, {});
  }

  /**
   * Tampa do baú de guardar abrindo ou fechando, só para quem abre (o baú aberto não é público): o som
   * e, com o modelo (F48), a tampa, levantada enquanto o painel fica aberto.
   */
  private chestLid(id: string, open: boolean): void {
    const s = this.session.state.structures.get(id);
    if (!s || storageSlotsOf(s.itemId) === 0) return;
    this.sound.play(open ? "bau" : "bau-fecha", { x: s.x, y: s.y + 0.5, z: s.z });
    this.structures.setLocalOpen(id, open);
  }

  /** Atalhos: 1–8 e roda escolhem o slot do cinto; Tab/I abre o inventário; Q larga o item na mão; E abre o Forno/Panela. */
  private handleInventoryInput(): void {
    const inventory = this.session.state.inventories.get(this.session.sessionId);
    if (this.input.consumePressed("interact")) {
      // Porta na mira a até 3 m abre ou fecha; senão, E é do Forno/Panela.
      const near = this.nearestInteractable();
      if (this.aimedPiece && this.aimedDoorInReach()) this.session.room.send(MSG.door, { id: this.aimedPiece });
      else if (this.trade.open) this.trade.show(undefined);
      else if (this.carranca.open && !this.carranca.solo) this.carranca.show(undefined);
      else if (this.raftPanel.open) this.raftPanel.show(false);
      else if (this.openStation === undefined && near?.kind === "jangada") {
        this.raftPanel.show(true);
        this.releasePointer();
      }
      else if (this.openStation === undefined && near?.kind === "cruz") {
        this.revivingCross = near.id;
        this.reviveLocked = !!document.pointerLockElement;
        this.session.room.send(MSG.revive, { id: near.id, holding: true });
      } else if (this.openStation === undefined && near?.kind === "carranca") this.openCarranca(near.id);
      else if (this.openStation === undefined && near?.kind === "simpatia") this.session.room.send(MSG.simpatiaOpen, { id: near.id });
      else if (this.openStation === undefined && near?.kind === "pedra") this.session.room.send(MSG.encanto, { id: near.id });
      else if (this.openStation === undefined && near?.kind === "mascate") this.openTrade(near.id);
      else this.toggleStation();
    }
    if (this.input.consumePressed("dismantle") && this.aimedPiece) {
      this.dismantling.add(this.aimedPiece, performance.now());
      this.session.room.send(MSG.dismantle, { id: this.aimedPiece });
    }
    if (this.input.consumePressed("inventory")) {
      this.trade.show(undefined);
      this.inventoryPanel.toggle();
      this.sound.play(this.inventoryPanel.open ? "ui-abrir" : "ui-fechar", { bus: "ui" });
      if (!this.inventoryPanel.open) this.closeStation();
      if (this.inventoryPanel.open) {
        this.crafting.toggle(false);
        this.releasePointer();
        this.inventoryPanel.update(inventory);
      }
    }
    if (this.input.consumePressed("crafting")) {
      this.trade.show(undefined);
      this.crafting.toggle();
      this.sound.play(this.crafting.open ? "ui-abrir" : "ui-fechar", { bus: "ui" });
      if (this.crafting.open) {
        this.inventoryPanel.toggle(false);
        this.closeStation();
        this.releasePointer();
        this.crafting.update(this.craftingTabs());
      }
    }
    if (!inventory) return;
    const key = this.input.consumeBeltKey();
    if (key >= 0) {
      this.actions.select(key);
      this.tutorialSignals.beltPicks++;
    }
    const wheel = this.input.consumeWheel();
    if (wheel !== 0) {
      this.actions.select((((inventory.selected + wheel) % BELT_SLOTS) + BELT_SLOTS) % BELT_SLOTS);
      this.tutorialSignals.beltPicks++;
    }
    if (this.input.consumePressed("discard")) {
      const slot = { area: "inv" as const, index: inventory.selected };
      this.actions.discard(slot, this.input.shiftHeld() ? undefined : 1);
    }
  }

  /**
   * Ação principal (clique; segurar repete): com comida na mão, come (na simulação); com arma,
   * golpeia; com ferramenta ou mão nua mirando um recurso que dá para colher, coleta; senão, golpeia
   * com arma, ferramenta ou mão nua (material e peça feita não atacam). Golpe e coleta dividem a mesma cadência, como no servidor.
   */
  private updateAction(now: number): void {
    const mine = this.session.state.avatars.get(this.session.sessionId);
    const queued = this.attackQueued;
    this.attackQueued = false;
    if (!mine) return;
    const panels = this.panelOpen;
    const p = this.position(this.session.sessionId, mine);
    // Criatura e recurso sob a cruz (F55): o primeiro que o raio da câmera pega ao alcance do corpo;
    // sem nenhum, o cone do corpo pela mira fica de reserva (vale o mais perto).
    const isRemoved = (id: number) => this.view.resources.isRemoved(id);
    let near = panels ? -1 : this.targeting.rayPick(this.aim, p, isRemoved);
    // Com um recurso sob a cruz, só a criatura na frente dele (o raio a pega antes) vale.
    let creature = panels ? undefined : rayPickCreature(this.aim, p, this.creatureFrame, near >= 0 ? this.targeting.rayT : undefined);
    if (!panels && near < 0 && !creature) {
      const looker = { x: p.x, y: p.y, z: p.z, yaw: this.aim.aimYaw };
      near = this.targeting.pick(looker, isRemoved);
      creature = creatureFirst(looker, pickCreature(looker, this.creatureFrame), near >= 0 ? this.targeting.edgeDistance(looker, near) : undefined);
    }
    const k = creature ? -1 : near;
    const set = this.world.resources;
    const inventory = this.session.state.inventories.get(this.session.sessionId);
    const equipped = inventory ? equippedItem(inventory as unknown as InventoryLike) : "";
    const tool = toolOf(equipped);
    let harvestable: number | undefined;
    if (creature) {
      const name = CREATURE_BY_ID.get(creature.type)?.name ?? creature.type;
      this.gameHud.setTarget(`${name}${creature.elite ? " reforçada" : ""} · ${creature.hp}/${creature.hpMax}`, false);
    } else if (k < 0 && !panels && this.pieceLabel()) {
      const label = this.pieceLabel()!;
      this.gameHud.setTarget(label.text, false);
    } else if (k < 0 && !panels && this.nearestInteractable()) {
      this.gameHud.setTarget(this.nearestInteractable()!.label, false);
    } else if (k >= 0) {
      const id = set.ids[k] as number;
      const type = RESOURCE_TYPES[set.types[k] as number]!;
      const hp = this.session.state.resources.get(String(id))?.hp ?? type.hp;
      const blocker = harvestBlocker(type, tool);
      this.gameHud.setTarget(blocker ? `${type.name} · ${blocker}` : `${type.name} · ${hp}/${type.hp} · clique para coletar`, Boolean(blocker));
      if (!blocker) harvestable = id;
    } else this.gameHud.setTarget(undefined);
    const dead = this.session.state.vitals.get(this.session.sessionId)?.dead === true;
    const bow = bowOf(equipped);
    if (equipped !== this.lastEquipped) {
      this.lastEquipped = equipped;
      this.bowString.cancel();
    }
    if (bow && inventory) {
      this.updateBow(bow, inventory, now, queued, panels || dead);
      return;
    }
    this.bowHud.update(0, false, undefined);
    if (panels || dead || edibleFood(equipped)) {
      this.actionGate.cancel();
      return;
    }
    if (!this.actionGate.poll(now, queued, this.input.primaryDown())) return;
    const weapon = itemDef(equipped)?.kind === "arma";
    if (!weapon && harvestable !== undefined) {
      this.session.harvest(harvestable);
      this.actionGate.start(now, tool.cooldown);
      this.swings.start(this.session.sessionId, now, tool.cooldown, "coleta");
      return;
    }
    // Só o que tem golpe (arma, ferramenta) ou a mão vazia, como no servidor: o aviso só no clique, não a
    // cada ciclo segurando.
    if (!meleeAllowed(equipped)) {
      if (queued) toast(`${itemDef(equipped)?.name ?? "Isso"} não serve para atacar: use uma arma, uma ferramenta ou as mãos.`);
      return;
    }
    // Café Coado (Simpatia do próprio jogador): mesma cadência que o servidor aplica.
    const counts = inventory?.simpatias;
    const cycle = swingCycle(meleeCycle(meleeWeapon(equipped)), simpatiaEffects((id) => counts?.get(id) ?? 0));
    this.session.room.send(MSG.attack, {});
    this.sound.play("golpe-ar", { gain: 0.6 });
    this.actionGate.start(now, cycle);
    this.swings.start(this.session.sessionId, now, cycle);
  }

  /**
   * Arco na mão (doc 02 §12): segurar puxa a corda (anel na mira), soltar atira. A flecha prevista
   * sai na hora com a mesma curva do servidor; o evento dele a confirma ou corrige.
   */
  private updateBow(bow: WeaponStats, inventory: InventoryType, now: number, clicked: boolean, blocked: boolean): void {
    const arrows = ammoOf(inventory.quiver.itemId) ? inventory.quiver.qty : 0;
    const f = this.bowString.frame(bow, this.input.primaryDown(), clicked, !blocked && arrows > 0, now);
    this.bowHud.update(f.charge, f.charging, arrows);
    // O servidor mede a corda do `puxar` ao `atirar` no relógio dele.
    if (f.started) {
      this.session.room.send(MSG.draw, {});
      this.drawSent = true;
      this.sound.play("flecha-carga", { gain: 0.5, rate: 1 });
    }
    if (f.release === undefined) return;
    // Soltar encerra a puxada que os outros veem (o servidor a encerra no disparo).
    this.drawSent = false;
    this.sound.play("flecha-solta", { gain: 0.8 });
    const shot = ++this.shots;
    this.session.room.send(MSG.shoot, { shot });
    // Flechas previstas: do olho do corpo previsto, pela mira que a predição aceitou (a do servidor, F55).
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!mine) return;
    const p = this.position(this.session.sessionId, mine);
    const predicted = this.reconciler?.state as AvatarType | undefined;
    const crouching = predicted?.crouching ?? mine.crouching;
    const aimYaw = predicted?.aimYaw ?? this.aim.aimYaw;
    const aimPitch = predicted?.aimPitch ?? this.aim.aimPitch;
    const speed = arrowSpeed(bow, bowCharge(bow, f.release / 1000));
    const count = Math.min(bow.arrows ?? 1, arrows);
    for (let i = 0; i < count; i++) {
      const fan = (i - (count - 1) / 2) * BOW.fanDegrees * (Math.PI / 180);
      const dir = lookDirection(aimYaw + fan, aimPitch);
      const eye = { x: p.x, y: p.y + eyeFor(crouching), z: p.z };
      this.arrows.launch(
        arrowKey(this.session.sessionId, shot, i),
        this.session.sessionId,
        { x: eye.x + dir.x * BOW.muzzle, y: eye.y + dir.y * BOW.muzzle, z: eye.z + dir.z * BOW.muzzle, vx: dir.x * speed, vy: dir.y * speed, vz: dir.z * speed, gravity: BOW.gravity },
        now,
      );
    }
    this.swings.start(this.session.sessionId, now, 0.35, "arco");
  }

  /**
   * Ataque de criatura: aviso no chão sincronizado com o impacto e, no caldeirão da Cuca, o pote
   * voando da mão dela (fim do preparo) até o centro do aviso (impacto).
   */
  private onCreatureAttack(e: CreatureAttackEvent): void {
    this.combatStats.telegraphs++;
    const now = performance.now();
    const rtt = this.session.room.clock?.smoothedRtt?.() ?? 0;
    // No piso: no convés da Jangada, acima do fundo do mar (a mesma regra do servidor); mirado em quem
    // está em cima da laje da Caverna, no topo dela (`e.y`).
    const height = (x: number, z: number) => e.y ?? this.sim.floorHeight(x, z);
    const creature = this.session.state.creatures.get(e.id);
    const def = creature && CREATURE_BY_ID.get(creature.type)?.attacks.find((a) => a.id === e.attack);
    // O caldeirão lançado cai mesmo que a Cuca morra: o aviso fica preso a partir do lançamento.
    // As marcas no chão (caldeirões da Cuca Rainha, raízes) já estão garantidas ao aparecer.
    // Malha neon (F60): o aviso no tom de quem ataca (vermelho de borda dupla no chefe).
    const tone = creatureTone(CREATURE_BY_ID.get(creature?.type ?? "")?.archetype ?? "assombracao", true);
    // Barra de conjuração do chefe (F63): o nome do golpe até o impacto.
    if (this.session.state.bosses?.has(e.id)) {
      // Mecânicas de grupo (F69): a dica de o que fazer vai junto do nome do golpe.
      const hint = def?.share ? MECHANIC_HINTS.share : def?.buster ? MECHANIC_HINTS.buster : def?.interruptible ? MECHANIC_HINTS.interruptible : undefined;
      this.bossBars.cast(e.id, hint ? `${attackName(e.attack)} · ${hint}` : attackName(e.attack), e.inMs);
    }
    // Som por forma (F69): arco, círculo e linha têm tons próprios; o chefe, mais grave.
    this.sound.play(`aviso-${e.shape}`, { x: e.x, y: height(e.x, e.z), z: e.z, gain: creature && BOSS_IDS.includes(creature.type) ? 0.7 : 0.4, ...(creature && BOSS_IDS.includes(creature.type) ? { rate: 0.75 } : {}) });
    this.telegraphs.spawn(e, now, rtt, height, def?.kind === "arremesso" ? def.windup * 1000 : def?.kind === "marcas" ? 0 : undefined, tone);
    // O golpe do modelo animado (F50): o quadro do impacto no mesmo instante do aviso.
    this.creatures.strike(e, now, rtt);
    // Golpe de chefe perto (F55): a câmera treme quando ele cai, pela distância até o impacto.
    if (creature && BOSS_IDS.includes(creature.type)) this.shakeAt(now + Math.max(0, e.inMs - rtt / 2), e.x, e.z, 0.55, 30);
    // Assinatura dos inimigos maiores (F91): estilhaços, onda, cristais e tremor no tempo do golpe, a até 60 m.
    // Os golpes pesados de chefe (F94) entram só com a onda de choque do pós.
    const kind = creature ? CREATURE_BY_ID.get(creature.type) : undefined;
    const type = kind && (hasCreatureFx(kind.id, e.attack) || bossSlam(kind.archetype, def) > 0) ? kind : undefined;
    const camera = this.view.camera.position;
    const distance = Math.hypot(e.x - camera.x, e.z - camera.z);
    if (type && distance < 60) this.creatureFx.play(planCreatureFx(e, type, now, rtt, { color: gameColor(TONE_COLORS[tone]), reduced: this.prefs.reduceFlashes, density: this.creatureFx.density(), distance }));
    // Voz da criatura no preparo do ataque (doc 07, A8: o aviso tem pelo menos 0,5 s).
    const voice = creature ? creatureVoice(creature.type) : undefined;
    if (voice && creature) this.sound.play(voice, { x: creature.x, y: creature.y + 1, z: creature.z, priority: CREATURE_BY_ID.get(creature.type)?.archetype === "assombracao" ? PRIORITY.comum : PRIORITY.importante });
    const from = this.creatureFrame.find((c) => c.id === e.id);
    if (def?.kind === "arremesso" && from) {
      const impactAt = now + Math.max(0, e.inMs - rtt / 2);
      this.lobs.spawn({ x: from.x, y: from.y + LOB_RELEASE_HEIGHT, z: from.z }, { x: e.x, y: height(e.x, e.z), z: e.z }, impactAt - (e.inMs - def.windup * 1000), impactAt, e.id);
    }
  }

  /** Dano confirmado pelo servidor: números, marcador de acerto, tremor e lampejo em quem apanhou. */
  private onDamage(e: DamageEvent): void {
    const me = this.session.sessionId;
    const hurt = this.avatars.get(e.target);
    if (hurt) hurt.hurtAt = performance.now();
    // Cor de quem bateu (F63; F85 a da classe do atacante); o próprio dano maior, o dos aliados menor.
    const attacker = this.session.state.players.get(e.attacker);
    this.numbers.spawn(e, this.view.camera, attacker ? { color: playerColor(attacker), ally: e.attacker !== me } : undefined);
    if (e.attacker === me) {
      this.combatStats.dealt += e.amount;
      this.hitMarkerUntil = performance.now() + 160;
      // O próprio golpe acertou: soco curto na câmera (F55).
      this.shake.hit();
      // Marcador de acerto com som por tipo: crítico com sino por cima.
      this.sound.play(e.crit ? "critico" : "acerto", { bus: "ui", gain: 0.6 });
    } else if (e.target !== me) this.sound.play("acerto", { x: e.x, y: e.y, z: e.z, gain: 0.6 });
    if (this.session.state.creatures.has(e.target)) {
      this.creatureBars.markHit(e.target, performance.now());
      if (e.attacker === me) this.combatStats.creatureHits++;
    }
    this.impact(e, attacker);
    // Quem atira o Rompe-Núcleo caiu antes do tiro: a carga sai (o servidor não manda cancelamento).
    if (e.died && this.session.state.players.has(e.target)) this.skillVfx.cancel(e.target, "disparo-rompe-nucleo");
    // Égide absorvendo (F76, doc 09 §2.3): estilhaços de cristal prateados no corpo de quem segurou.
    if (e.absorbed > 0 && this.session.state.players.has(e.target)) {
      // O número sai acima da cabeça (o `y` do evento); o corpo, como no impacto.
      this.shards.burst({ x: e.x, y: e.y - 1.1, z: e.z, color: PALETTE.neutro.hex, n: Math.round(Math.min(18, 6 + e.absorbed / 2) * this.fxAlpha(e.target)), speed: 2.6, size: 0.09, life: 0.5, up: 0.5 });
    }
    if (e.target === me) {
      this.combatStats.taken += e.amount;
      // De onde veio (F76): o chevron em volta da mira, na cor da fonte.
      const mine = this.session.state.avatars.get(me);
      const fromMob = this.session.state.creatures.get(e.attacker);
      const fromPlayer = this.session.state.avatars.get(e.attacker);
      const from = fromMob ?? (e.attacker !== me ? fromPlayer : undefined);
      if (mine && from) {
        const color = fromMob ? gameColor(TONE_COLORS[creatureTone(CREATURE_BY_ID.get(fromMob.type)?.archetype ?? "assombracao", true)]) : playerColor(attacker);
        this.chevrons.show(chevronAngle(this.input.yaw, mine, from), color);
      }
      const v = this.session.state.vitals.get(me);
      this.shake.add(CameraShake.intensity(e.amount, (v?.hpMax ?? 100) + (v?.patuaMax ?? 25)));
      // Lampejo do golpe: no máximo 3 por segundo (WCAG 2.3.1); nenhum com a redução de lampejos.
      if (this.flashes.allow(performance.now(), this.prefs.reduceFlashes)) this.vitalsHud.flash();
    }
  }

  /** Força dos efeitos de quem causou (F68): os próprios inteiros; os dos aliados, pela opção. */
  private fxAlpha(by: string): number {
    return by === this.session.sessionId || !this.session.state.players.has(by) ? 1 : this.prefs.allyFx / 100;
  }

  /**
   * Impacto (F68, guia §21): estilhaços na cor de quem bateu (10 a 30 pelo dano), um lampejo curto, anel no
   * chão no crítico e no abate, a criatura que morre em estilhaços no tom dela e a pausa no próprio golpe. No
   * acerto do cajado, faíscas de magia (F93).
   */
  private impact(e: DamageEvent, attacker: { readonly classId: string } | undefined): void {
    if (e.amount <= 0) return;
    const now = performance.now();
    const creature = this.session.state.creatures.get(e.target);
    const def = creature ? CREATURE_BY_ID.get(creature.type) : undefined;
    const source = this.session.state.creatures.get(e.attacker);
    const tone = (c: typeof creature) => gameColor(TONE_COLORS[creatureTone(CREATURE_BY_ID.get(c?.type ?? "")?.archetype ?? "assombracao", true)]);
    const color = attacker ? playerColor(attacker) : source ? tone(source) : gameColor(PALETTE.neutro.hex);
    const alpha = this.fxAlpha(e.attacker);
    const scale = alpha < 1 ? alpha : 1;
    // O número sai acima da cabeça; o golpe, no meio do corpo.
    const y = e.y - Math.min(1.2, (def?.height ?? 1.6) * 0.5) - 0.3;
    const n = Math.round(Math.min(30, 10 + e.amount / 3) * scale);
    this.shards.burst({ x: e.x, y, z: e.z, color, n, speed: 3.5 + Math.min(3, e.amount / 20), size: 0.07, life: 0.45, up: 0.4, alpha });
    // Fase 14 (a referência do golpe): para onde o golpe empurra, de quem bateu para o alvo.
    const from = this.session.state.avatars.get(e.attacker) ?? source;
    const pushLen = from ? Math.hypot(e.x - from.x, e.z - from.z) : 0;
    const push = pushLen > 1e-3 && from ? { x: (e.x - from.x) / pushLen, z: (e.z - from.z) / pushLen } : { x: 0, z: 0 };
    const tier = QUALITY.post.tier;
    const heavy = e.crit || e.died;
    // Golpe direto (arma, flecha, orbe, criatura): os ticks de perigo e de efeito contínuo ficam sem estrela nem anel.
    const direct = heavy || e.kind === "golpe" || e.kind === "flecha" || e.kind === "orbe" || e.kind === "criatura";
    // Faíscas: fios rápidos que voam para o lado do golpe (não no Baixo).
    if (direct && tier !== "baixo") this.shards.burst({ x: e.x, y, z: e.z, color, n: Math.round((heavy ? 16 : 9) * scale), speed: 7, size: 0.06, life: 0.24, up: 0.45, streak: 0.55, alpha, dx: push.x * 3, dz: push.z * 3 });
    // A estrela do impacto, um pouco para o lado da câmera (fora do corpo); "Reduzir lampejos" fica no estalo parado de antes, sem a estrela.
    if (this.prefs.reduceFlashes || !direct) this.shards.burst({ x: e.x, y, z: e.z, color, n: 1, speed: 0, size: e.crit ? 0.45 : 0.3, life: 0.09, fall: false, alpha });
    else {
      const cam = this.view.camera.position;
      const toCam = Math.hypot(cam.x - e.x, cam.y - y, cam.z - e.z) || 1;
      const k = 0.35 / toCam;
      this.skillVfx.flash(e.x + (cam.x - e.x) * k, y + (cam.y - y) * k, e.z + (cam.z - e.z) * k, e.died ? 1.7 : e.crit ? 1.35 : 0.9, color, now, heavy ? 190 : 130, alpha);
    }
    const shape = attacker ? classSkills(classOf(attacker.classId)).ativa.shape : "linhas";
    // O anel no chão em todo golpe (Fase 14; antes só no crítico e no abate): a luz dele acende as arestas do corpo atingido.
    if (direct) this.skillVfx.pulse(e.x, e.z, this.sim.floorHeight(e.x, e.z), e.died ? 1.8 : e.crit ? 1.2 : 0.75, color, shape, now, heavy ? alpha : alpha * 0.6);
    // Cristais grandes no crítico e no abate (Fase 14), pelo teto do tier; a fita do golpe fica mais clara.
    if (heavy) {
      // O teto é do tier, para todos; os aliados fracos (a opção) soltam menos, sem cortar os cristais dos outros.
      this.shards.crystals({ x: e.x, y, z: e.z, color, n: Math.round((e.died ? 6 : 4) * scale), size: 0.42, speed: 2.4, life: 1.5, dx: push.x * 2.2, dz: push.z * 2.2, alpha, cap: CRYSTALS[tier] });
      if (!this.prefs.reduceFlashes) this.swingRibbons.boost(e.attacker, 1.45);
      // O próprio golpe forte: o zoom do desfoque de movimento a partir do impacto (só com o efeito, Fase 14).
      if (e.attacker === this.session.sessionId) this.post.motionImpact(e.x, y, e.z, e.died ? 1 : 0.7);
    }
    if (e.died && creature) {
      // Morte em estilhaços: o corpo se desfaz no tom da criatura (laranja, vermelho ou neutro).
      this.shards.burst({ x: e.x, y, z: e.z, color: tone(creature), n: def?.boss ? 60 : 36, speed: 4.5, size: 0.11, life: 0.9, up: 0.55 });
    }
    if (e.attacker === this.session.sessionId && creature) this.hitStop.trigger(now, e.crit || e.died ? HIT_STOP.heavyMs : HIT_STOP.normalMs);
    // Passivas de classe que agiram (Fase 12, Gate C): só pelo que o servidor disse (`kind`, `procs`).
    const target = this.session.state.players.get(e.target);
    for (const fx of passiveFx(e, e.x, y, e.z, this.sim.floorHeight(e.x, e.z), this.session.state.avatars.get(e.target)?.yaw ?? 0)) {
      const owner = fx.color === "alvo" ? e.target : e.attacker;
      const tint = fx.color === "alvo" ? (target ? playerColor(target) : color) : color;
      this.skillVfx.passive(fx, tint, e.x, e.z, this.sim.floorHeight(e.x, e.z), now, this.fxAlpha(owner));
    }
  }

  /**
   * Rastros (F68): a fita do golpe (F92), entre a base e a ponta do item na mão, e o corpo na Investida e no
   * Salto, em estilhaços parados. A fita segue enquanto o golpe dura (`SwingRibbons`): acabou, começou outro
   * ou o item trocou, ela some pela cauda; fora da tela ou longe (`animated` falso), na hora.
   */
  private trail(sessionId: string, entry: AvatarEntry, body: AvatarType, swing: number, dead: boolean, p: { x: number; y: number; z: number }, now: number, animated: boolean): void {
    const held = entry.heldMesh;
    const progress = dead || this.swings.kind(sessionId) === "arco" ? 0 : swing;
    const ribbon = this.swingRibbons.frame(sessionId, now, progress, entry.held, animated && held?.visible === true);
    if (ribbon && held) {
      // Do terço do item até a ponta, pelo comprimento dele no +y (o cabo); a matriz é a desta pose.
      held.updateWorldMatrix(true, false);
      swingEnds(held, this.trailBase, this.trailTip);
      const [base, tip] = [this.trailBase, this.trailTip];
      ribbon.sample(now, base.x, base.y, base.z, tip.x, tip.y, tip.z);
      // Grãos que a energia solta pela ponta (Fase 14; não no Baixo).
      if (QUALITY.post.tier !== "baixo" && now - (this.grainAt.get(sessionId) ?? -Infinity) >= GRAIN_MS) {
        this.grainAt.set(sessionId, now);
        this.shards.burst({ x: tip.x, y: tip.y, z: tip.z, color: playerColor(this.session.state.players.get(sessionId)), n: 1, speed: 0.7, size: 0.035, life: 0.4, up: 0.2, alpha: this.fxAlpha(sessionId) });
      }
    }
    // Investida (Gate C): uma faixa em pé, do joelho ao peito, pelo caminho que o corpo fez de verdade
    // (o evento do servidor só traz o fim planejado). Acabou o avanço, ela some pela cauda.
    let dash = this.dashing.get(sessionId);
    if (!dead && body.dashTime > 0) {
      if (!dash) {
        const alpha = this.fxAlpha(sessionId);
        dash = alpha > 0 ? this.dashRibbons.acquire(ribbonLook("investida", playerColor(this.session.state.players.get(sessionId)), alpha, this.prefs.reduceFlashes)) : undefined;
        if (dash) this.dashing.set(sessionId, dash);
      }
      dash?.sample(now, p.x, p.y + 0.35, p.z, p.x, p.y + 1.4, p.z);
    } else if (dash) {
      dash.release(now);
      this.dashing.delete(sessionId);
    }
    // Desfoque de movimento (Fase 14): o próprio corpo na Investida e no Salto (o fundo corre, o corpo fica nítido).
    if (!dead && sessionId === this.session.sessionId && (body.dashTime > 0 || body.leaping)) this.post.motionStreak(p.x, p.y + 1, p.z, body.vx, body.vy, body.vz, 1);
    // Poeira nos pés (Fase 14): a arrancada, o pouso e o rastro da corrida no chão; só de perto e na tela.
    // Caído, o estado esquece (quem volta na Lápide não "pousa" do nada).
    if (dead) this.footDust.remove(sessionId);
    else for (const dust of this.footDust.frame(sessionId, body, now)) if (animated) this.kickDust(dust, p, body.yaw, this.fxAlpha(sessionId));
    // Passos no chão (respingo no raso e nadando, lama, capim, poeira), com a passada dos sons.
    if (dead) this.footSteps.remove(sessionId);
    else {
      const step = this.footSteps.frame(sessionId, { x: p.x, y: p.y, z: p.z, speed: horizontalSpeed(body), grounded: body.grounded, swimming: body.swimming }, this.sim.seaLevel, this.stepSurfaceAt);
      if (step && animated) this.stepFx(step, body.yaw, this.fxAlpha(sessionId));
    }
    // A Investida corre no chão e levanta poeira; no ar (o Salto), o corpo deixa estilhaços parados.
    if (!dead && body.leaping) this.shards.burst({ x: p.x, y: p.y + 0.9, z: p.z, color: playerColor(this.session.state.players.get(sessionId)), n: 2, speed: 0.4, size: 0.09, life: 0.3, fall: false, alpha: this.fxAlpha(sessionId) });
  }

  /** Um sopro de poeira (Fase 14) nos pés de quem está em `p`, olhando para `yaw`: o rastro sai para trás. No Baixo, metade e sem rastro. */
  private kickDust(e: DustEvent, p: { x: number; y: number; z: number }, yaw: number, alpha: number): void {
    const low = QUALITY.post.tier === "baixo";
    if (low && e.kind === "rastro") return;
    const back = { x: Math.sin(yaw), z: Math.cos(yaw) };
    const s = e.strength;
    const n = (k: number) => Math.max(1, Math.round(k * (low ? 0.5 : 1)));
    if (e.kind === "pouso") this.smoke.dust(p.x, p.y, p.z, n(12 * s), 0.7 * s, 2.6 * s, 0.45 * alpha);
    else if (e.kind === "arranque") this.smoke.dust(p.x, p.y, p.z, n(9 * s), 0.55, 2.2, 0.4 * alpha, back.x * 1.5, back.z * 1.5);
    else this.smoke.dust(p.x, p.y, p.z, n(2), 0.35 * s + 0.15, 0.9, 0.3 * alpha, back.x * 1.4, back.z * 1.4);
  }

  /** O chão de um passo: construção por baixo, o bioma do plano e se o capim está à vista ali. */
  private readonly stepSurfaceAt = (b: { x: number; y: number; z: number; swimming: boolean }): StepSurface => {
    const cell = (v: number) => Math.min(PLAN_SIDE - 1, Math.max(0, Math.round((v + WORLD_HALF) / PLAN_SPACING)));
    const biome = this.world.plan.biome[cell(b.z) * PLAN_SIDE + cell(b.x)] ?? BIOME.mar;
    const onBox = groundAt(this.sim, b.x, b.z, b.y).onBox;
    const grassy = QUALITY.grass > 0 && !this.view.grassMask.hiddenAt(b.x, b.z);
    return stepSurface(biome, b.y, this.sim.seaLevel, onBox, b.swimming, grassy);
  };

  /**
   * O efeito de um passo (`STEP_FX`) nos pés, chutado um pouco para trás de quem anda (`yaw`); na água, na
   * superfície, com uma onda; na lama e na areia molhada, com a marca do pé. Mais forte correndo e ao entrar
   * na água; no Baixo, metade (e a pedra sem poeira).
   */
  private stepFx(e: StepEvent, yaw: number, alpha: number): void {
    const fx = STEP_FX[e.surface];
    if (alpha <= 0 || fx.n <= 0) return;
    const low = QUALITY.post.tier === "baixo";
    if (low && e.surface === "pedra") return;
    this.stepCounts[e.surface] = (this.stepCounts[e.surface] ?? 0) + 1;
    const n = Math.max(1, Math.round(fx.n * e.strength * (low ? 0.5 : 1)));
    // Para trás de quem anda: com a câmera de ombro, os pés ficam atrás do corpo, e o chute sai dele.
    const back = { x: Math.sin(yaw) * 0.9, z: Math.cos(yaw) * 0.9 };
    if (fx.dust !== undefined) {
      this.smoke.dust(e.x, e.y, e.z, n, fx.dust * e.strength, fx.speed, 0.4 * alpha, back.x, back.z);
      return;
    }
    const water = e.surface === "agua" || e.surface === "raso";
    const y = water ? this.sim.seaLevel + 0.03 : e.y + 0.05;
    this.shards.burst({ x: e.x, y, z: e.z, color: "", gray: fx.gray, n, speed: fx.speed * Math.sqrt(e.strength), size: fx.size, life: fx.life, up: fx.up, alpha, dx: back.x, dz: back.z });
    // Na água, uma onda que abre em volta (maior nadando e ao entrar); na lama e na areia molhada, a marca
    // do pé, que fica alguns segundos (no Baixo, metade).
    if (water) this.smoke.ripple(e.x, this.sim.seaLevel + 0.04, e.z, (e.surface === "agua" ? 1.6 : 1.1) * Math.sqrt(e.strength), 0.55 * alpha);
    else if (e.surface === "lama" || e.surface === "molhada") this.smoke.mudMark(e.x, e.y + 0.1, e.z, e.surface === "lama" ? 0.5 : 0.38, (e.surface === "lama" ? 0.6 : 0.35) * alpha, MARK_LIFE * (low ? 0.5 : 1));
  }

  /** Fios (F68): do monstro Provocado a quem provocou, e de quem segura o reviver à Lápide. */
  private drawTethers(): void {
    const state = this.session.state;
    const taunt = 1 << STATUS_IDS.indexOf("provocado");
    const bySlot = new Map<number, string>();
    for (const [id, p] of state.players) bySlot.set(p.slot + 1, id);
    for (const [, c] of state.creatures) {
      if ((c.status & taunt) === 0 || c.statusBy === 0) continue;
      const id = bySlot.get(c.statusBy);
      const a = id ? state.avatars.get(id) : undefined;
      if (!a || !id) continue;
      this.shards.tether({ x: c.x, y: c.y + 1, z: c.z }, { x: a.x, y: a.y + 1, z: a.z }, playerColor(state.players.get(id)), this.fxAlpha(id) * 0.8);
    }
    for (const [, d] of state.deaths) {
      if (!d.reviver) continue;
      const a = state.avatars.get(d.reviver);
      const cross = state.structures.get(d.cross);
      if (a && cross) this.shards.tether({ x: a.x, y: a.y + 1, z: a.z }, { x: cross.x, y: cross.y + 0.8, z: cross.z }, playerColor(state.players.get(d.reviver)), this.fxAlpha(d.reviver));
    }
  }

  private position(sessionId: string, avatar: AvatarType): { x: number; y: number; z: number; yaw: number; pitch: number } {
    if (sessionId === this.session.sessionId && this.reconciler) {
      return {
        x: this.reconciler.value("x"),
        y: this.reconciler.value("y"),
        z: this.reconciler.value("z"),
        yaw: this.input.yaw,
        pitch: this.input.pitch,
      };
    }
    return {
      x: this.predict.value(avatar, "x"),
      y: this.predict.value(avatar, "y"),
      z: this.predict.value(avatar, "z"),
      yaw: this.predict.value(avatar, "yaw"),
      pitch: this.predict.value(avatar, "pitch"),
    };
  }

  /** Avatar novo: o animado do personagem da classe (F49) ou, sem o pacote, o boneco procedural. */
  private createAvatar(sessionId: string): AvatarEntry {
    const info = this.session.state.players.get(sessionId);
    const slot = info?.slot ?? 0;
    // O personagem e a cor vêm da classe (F64, F85); o slot é só o número.
    const c = classOf(info?.classId ?? "");
    const view = createAvatarView(avatars.library, this.view.atmosphere, classColor(c.id), c.character, this.avatars.size);
    // O mundo na cor da classe de quem joga (Fase 13, ADR 0026): arestas e nós do ambiente.
    if (sessionId === this.session.sessionId) setWorldAccent(classColor(c.id));
    // Anel sob os pés na cor da classe, com o número do jogador em marcas (F63; só na malha neon).
    view.root.add(createSlotRing(this.view.atmosphere, gameColor(classColor(c.id)), slot + 1));
    this.view.scene.add(view.root);
    const entry: AvatarEntry = { view, held: "", heldMesh: undefined, diedAt: -1, revivedAt: -1, deadAt: -1, rose: false, hurtAt: -Infinity };
    this.avatars.set(sessionId, entry);
    return entry;
  }

  /** Puxando o arco: o próprio pela corda local; os outros pelo aviso do servidor, enquanto o arco estiver na mão. */
  private isDrawing(sessionId: string, local: boolean, held: string, dead: boolean, now: number): boolean {
    // A corda local só conta com o arco na mão: no modo de posicionar, a ação principal não roda e a
    // corda não é cancelada ao trocar de item.
    if (local) return this.bowString.charging && bowOf(held) !== undefined;
    const since = this.drawing.get(sessionId);
    if (since === undefined) return false;
    if (dead || !bowOf(held) || now - since > STALE_DRAW_MS) {
      this.drawing.delete(sessionId);
      return false;
    }
    return true;
  }

  private syncAvatars(dt: number, now: number): void {
    const started = performance.now();
    const present = new Set<string>();
    const camera = this.view.camera;
    this.frameIndex++;
    // Na tela pela câmera do quadro anterior (ela ainda não andou neste): um quadro de atraso no LOD.
    this.frustum.setFromProjectionMatrix(this.projection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    // A luz do anel no chão (Fase 13): só a do jogador local, vivo e à vista; apaga até achá-lo.
    setRingLight(this.view.atmosphere, 0, 0, 0);
    this.session.state.avatars.forEach((avatar, sessionId) => {
      present.add(sessionId);
      const entry = this.avatars.get(sessionId) ?? this.createAvatar(sessionId);
      const view = entry.view;
      const local = sessionId === this.session.sessionId;
      const p = this.position(sessionId, avatar);
      const body = local && this.reconciler ? (this.reconciler.state as AvatarType) : avatar;
      view.root.position.set(p.x, p.y, p.z);
      // Morto (F21): cai (o clipe `morte` ou o tombo do boneco) e, depois de 1 s, some em dissolve; ao voltar, reaparece.
      // Caído (F74): tomba como na morte, sem sumir; levantado, levanta (o mesmo clipe da volta).
      const vit = this.session.state.vitals.get(sessionId);
      const dead = vit?.fallen === true || vit?.dead === true;
      if (dead && entry.diedAt < 0) entry.diedAt = now;
      if (vit?.dead === true && entry.deadAt < 0) entry.deadAt = now;
      if (!dead && entry.diedAt >= 0) {
        entry.diedAt = -1;
        entry.revivedAt = now;
        entry.rose = entry.deadAt >= 0;
        entry.deadAt = -1;
        // Voltou da Lápide (F78, doc 09 §3.4): uma coluna de luz na cor da classe dele, onde o servidor o pôs
        // (a Estátua e o Barco mudam o corpo de lugar no mesmo tick; a posição predita ainda está no corpo antigo).
        if (entry.rose) this.skillVfx.column(avatar.x, avatar.y, avatar.z, playerColor(this.session.state.players.get(sessionId)), performance.now());
      }
      const dissolve = entry.deadAt >= 0 ? Math.min(1, Math.max(0, (now - entry.deadAt - 1000) / 1500)) : entry.rose && entry.revivedAt >= 0 ? Math.max(0, 1 - (now - entry.revivedAt) / 600) : 0;
      // Com a câmera colada nele (F55), o próprio boneco some em pontilhado, sem a borda acesa da morte.
      const fade = local && !dead ? this.rig.fade : 0;
      if (fade > dissolve) view.setDissolve(fade, false);
      else view.setDissolve(dissolve);
      view.root.visible = Math.max(dissolve, fade) < 1;
      if (local && !dead && dissolve <= 0) setRingLight(this.view.atmosphere, p.x, p.y, p.z, playerColor(this.session.state.players.get(sessionId)));
      // Sombra-blob (F51): quem está vivo e inteiro (o próprio corpo primeiro: no Baixo, com 8 vagas, as
      // criaturas mais perto da câmera não o tiram).
      if (!dead && dissolve <= 0) this.blobs.add(sessionId, p.x, p.y, p.z, PLAYER_BLOB_RADIUS, local ? 1 : 0);
      const held = this.session.state.players.get(sessionId)?.held ?? "";
      if (held !== entry.held) {
        entry.heldMesh = heldMesh(held, models.library, this.view.heldFor(classColor(this.session.state.players.get(sessionId)?.classId ?? "")), view.heldView);
        view.setHeld(entry.heldMesh, held);
        entry.held = held;
      }
      // Depois da troca: o item novo também some com o boneco morto ou com a câmera colada.
      if (entry.heldMesh) entry.heldMesh.visible = !dead && fade < 0.5;
      const swing = this.swings.progress(sessionId, now);
      const signals: AvatarSignals = {
        speed: horizontalSpeed(body),
        vy: body.vy,
        grounded: body.grounded,
        crouching: body.crouching,
        swimming: body.swimming,
        sliding: body.slideTime > 0,
        dead,
        // O disparo do arco mexe o braço do boneco procedural; no animado, a corda puxada é o gesto, e o
        // disparo a segura um instante (num toque rápido, puxar e soltar chegam juntos e não se veriam).
        swing: this.swings.kind(sessionId) === "arco" ? 0 : swing,
        drawing: this.isDrawing(sessionId, local, held, dead, now) || (this.swings.kind(sessionId) === "arco" && swing > 0),
        eating: body.useTime > 0,
        bites: body.eatCount,
        hurtAgo: Math.max(0, (now - entry.hurtAt) / 1000),
        revivedAgo: entry.revivedAt >= 0 ? Math.max(0, (now - entry.revivedAt) / 1000) : Infinity,
      };
      this.sphere.center.set(p.x, p.y + 0.9, p.z);
      this.sphere.radius = AVATAR_BOUNDS.radius;
      const distance = camera.position.distanceTo(this.sphere.center);
      const onScreen = view.root.visible && this.frustum.intersectsSphere(this.sphere);
      view.update({
        dt,
        now,
        yaw: p.yaw,
        pitch: p.pitch,
        signals,
        armSwing: swing,
        diedAt: entry.diedAt,
        distance,
        visible: onScreen,
        frame: this.frameIndex,
        range: QUALITY.distance,
      });
      // Depois da pose do quadro: a fita segue o item onde ele está agora, não no quadro anterior. Só com a
      // pose a cada quadro (na tela e perto, F49): parada ou aos saltos, a fita sairia quebrada.
      this.trail(sessionId, entry, body, swing, dead, p, now, animationEvery(distance, onScreen, QUALITY.distance) === 1);
    });
    for (const [sessionId, entry] of this.avatars) {
      if (present.has(sessionId)) continue;
      this.swingRibbons.remove(sessionId, now);
      this.footDust.remove(sessionId);
      this.footSteps.remove(sessionId);
      this.grainAt.delete(sessionId);
      this.dashing.get(sessionId)?.release(now);
      this.dashing.delete(sessionId);
      // Sem avatar, o servidor abandona o tiro: a carga sai junto.
      this.skillVfx.cancel(sessionId, "disparo-rompe-nucleo");
      entry.view.dispose();
      this.avatars.delete(sessionId);
    this.swings.forget(sessionId);
      this.drawing.delete(sessionId);
    }
    this.swingRibbons.update(now);
    this.dashRibbons.update(now);
    this.avatarMs += (performance.now() - started - this.avatarMs) * 0.05;
  }

  /**
   * Criaturas (interpoladas 100 ms no passado), barras de vida pela mira da câmera já posicionada
   * neste quadro e avisos de ataque no chão.
   */
  private syncCreatures(dt: number, now: number): void {
    const camera = this.view.camera;
    this.creatureFrame = this.creatures.update(
      this.session.state.creatures,
      (c: CreatureEntity) => ({ x: this.predict.value(c, "x"), y: this.predict.value(c, "y"), z: this.predict.value(c, "z"), yaw: this.predict.value(c, "yaw") }),
      dt,
      now / 1000,
      camera.position.x,
      camera.position.z,
    );
    this.creatureBars.update(this.creatureFrame, camera.position, camera.getWorldDirection(this.aimDir), now, dt);
    this.hazards.update(now);
    this.whirlwinds.update(this.session.state.whirlwinds?.entries() ?? [], (x, z) => this.sim.height(x, z), camera.position.x, camera.position.z, dt);
    this.drawRaft();
    this.drawCerco(now / 1000);
    this.lobs.update(now);
    this.orbs.update(now);
    this.arrows.update(now, camera.position);
    this.flameEmitters.update(now, dt, camera.position.x, camera.position.z, this.floorAt);
    this.drawTethers();
    // Camada ambiente (F90): vagalumes à noite, poeira de dia, na densidade de "Capim e detalhes".
    this.shards.ambient({ x: camera.position.x, z: camera.position.z, time: now / 1000, night: this.view.atmosphere.uNight.value, density: QUALITY.details, steady: QUALITY.post.reduceFlashes });
    this.creatureFx.update(now);
    // Depois da assinatura e das fontes de chama: o que saiu neste quadro já entra no desenho dele.
    this.smoke.update(dt);
    this.shards.update(dt);
    this.dissolves.update(now);
    this.telegraphs.update(now, (id) => this.attacking(id));
  }

  /** A criatura segue no ataque (os avisos e a assinatura da F91): existe, não cambaleia, não morreu. */
  private attacking(id: string): boolean {
    const c = this.session.state.creatures.get(id);
    const state = c && creatureState(c.state);
    return state !== undefined && state !== "cambaleando" && state !== "morta";
  }

  /** Raio da cruz deste quadro (F55): a origem no plano do olho em `aimOrigin`, a direção em `aimDir`. */
  private aimRay(): Vector3 {
    const a = this.aim;
    this.aimOrigin.set(a.ox + a.dx * a.t0, a.oy + a.dy * a.t0, a.oz + a.dz * a.t0);
    return this.aimDir.set(a.dx, a.dy, a.dz);
  }

  /** Trauma marcado para o instante `at` (ms) de um impacto em (x, z), caindo até `radius` m. */
  private shakeAt(at: number, x: number, z: number, amount: number, radius: number): void {
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!mine) return;
    const p = this.position(this.session.sessionId, mine);
    this.shake.schedule(at, CameraShake.falloff(amount, Math.hypot(x - p.x, z - p.z), radius));
  }

  /**
   * Câmera do quadro (F55, `docs/game/camera.md`): a de ombro segue o corpo previsto; morto, a de morte e
   * o espectador (F21), com uma mistura de 0,6 s em cada troca. Depois, o ponto sob a cruz e a mira, e
   * por cima o tremor (que não mexe na mira).
   */
  private updateCamera(dt: number, now: number): void {
    const mine = this.session.state.avatars.get(this.session.sessionId);
    if (!mine) return;
    const p = this.position(this.session.sessionId, mine);
    const body = (this.reconciler?.state as AvatarType | undefined) ?? mine;
    // Morto: câmera de morte e espectador (só aliados vivos; câmera livre só no Mutirão).
    const state = this.session.state;
    const allies: { sessionId: string; x: number; y: number; z: number }[] = [];
    state.avatars.forEach((a, sid) => {
      if (sid !== this.session.sessionId && !state.vitals.get(sid)?.dead && state.players.get(sid)?.connected) allies.push({ sessionId: sid, ...this.position(sid, a) });
    });
    const command = this.input.command();
    const spectating = this.spectator.frame(
      performance.now(),
      dt,
      state.vitals.get(this.session.sessionId)?.dead === true,
      p,
      allies,
      { yaw: this.input.yaw, pitch: this.input.pitch, moveX: command.moveX, moveZ: command.moveZ, cycle: this.spectatorCycle, free: state.mode !== "treta" },
      (x, z) => this.sim.height(x, z),
    );
    this.spectatorCycle = 0;
    const camera = this.view.camera;
    // Troca de câmera (morreu, voltou): a nova parte de onde a outra estava.
    if (Boolean(spectating) !== this.wasSpectating) {
      this.blendPos.copy(camera.position);
      this.blendQuat.copy(camera.quaternion);
      this.blendStart = now;
      this.wasSpectating = Boolean(spectating);
      if (!spectating) {
        this.rig.reset();
        this.fallSpeed = 0;
        this.wasGrounded = true;
      }
    }
    // Pouso: a maior queda vista no ar vira o mergulho da câmera ao tocar o chão (não na água, nem
    // saindo dela, nem ao voltar da morte).
    if (body.swimming || spectating) this.fallSpeed = 0;
    else if (!body.grounded) this.fallSpeed = Math.max(this.fallSpeed, -body.vy);
    else {
      if (!this.wasGrounded && !body.swimming && !spectating) this.shake.land(this.fallSpeed);
      this.fallSpeed = 0;
    }
    this.wasGrounded = body.grounded;
    const held = state.players.get(this.session.sessionId)?.held ?? "";
    const input = this.cameraInput;
    input.dt = dt;
    input.x = p.x;
    input.y = p.y;
    input.z = p.z;
    input.yaw = this.input.yaw;
    input.pitch = this.input.pitch;
    input.speed = horizontalSpeed(body);
    input.grounded = body.grounded;
    input.crouching = body.crouching;
    input.swimming = body.swimming;
    input.submerged = body.submerged;
    input.carried = body.carried;
    input.aiming = this.bowString.charging && bowOf(held) !== undefined;
    input.seaLevel = this.sim.seaLevel;
    const pose = this.rig.update(input, this.sim);
    // A cruz (sem tremor): o ponto sob ela e a mira do olho até ele, com criaturas e outros jogadores.
    const aim = this.aim;
    if (spectating) {
      aim.begin(spectating.x, spectating.y, spectating.z, spectating.yaw, spectating.pitch, spectating.x, spectating.y, spectating.z, this.sim);
      aim.aimYaw = this.input.yaw;
      aim.aimPitch = this.input.pitch;
    } else {
      aim.begin(pose.x, pose.y, pose.z, pose.yaw, pose.pitch, p.x, p.y + eyeFor(body.crouching), p.z, this.sim);
      for (const c of this.creatureFrame) {
        const def = CREATURE_BY_ID.get(c.type);
        if (def) aim.cylinder(c.id, c.x, c.z, def.radius, c.y, c.y + def.height);
      }
      state.avatars.forEach((a, sid) => {
        if (sid === this.session.sessionId || state.vitals.get(sid)?.dead) return;
        const q = this.position(sid, a);
        aim.cylinder(sid, q.x, q.z, MOVE.radius, q.y, q.y + MOVE.height);
      });
      aim.finish(pose.yaw, pose.pitch);
    }
    const x = spectating ? spectating.x : pose.x;
    const y = spectating ? spectating.y : pose.y;
    const z = spectating ? spectating.z : pose.z;
    const shake = this.shake.step(dt, now);
    camera.position.set(x, y, z);
    camera.rotation.set((spectating ? spectating.pitch : pose.pitch) + shake.pitch, (spectating ? spectating.yaw : pose.yaw) + shake.yaw, shake.roll, "YXZ");
    // O tremor em translação, no espaço da câmera, menor com o braço encolhido (encostado na parede).
    const room = spectating ? 1 : Math.min(1, this.rig.arm / 1.5);
    camera.translateX(shake.x * room);
    camera.translateY(shake.y * room);
    camera.translateZ(shake.z * room);
    const blend = (now - this.blendStart) / 600;
    if (blend < 1) {
      const k = blend * blend * (3 - 2 * blend);
      this.targetQuat.copy(camera.quaternion);
      camera.position.lerpVectors(this.blendPos, camera.position, k);
      camera.quaternion.slerpQuaternions(this.blendQuat, this.targetQuat, k);
    }
    // FOV das Opções direto na terceira pessoa, mais o do enquadramento (correr abre, mirar fecha).
    const fov = this.prefs.fov + (spectating ? 0 : pose.fov);
    if (camera.fov !== fov) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  }
}
